import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import { queueOp, getPendingOps, removeOp, popLastOp, countPendingOps, cacheData, getCached, clearAllCache, hashPassword, cacheAuth, getAuth, clearAuth } from './offlineDb';

// ─── API Layer ───────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_URL || "http://localhost:8090";
let _token = localStorage.getItem("cd_token") || "";

function setToken(t) {
  _token = t;
  if (t) localStorage.setItem("cd_token", t);
  else localStorage.removeItem("cd_token");
}

async function apiFetch(path, opts = {}) {
  let res;
  try {
    res = await fetch(API + path, {
      ...opts,
      headers: {
        "Content-Type": "application/json",
        ...(_token ? { Authorization: "Bearer " + _token } : {}),
        ...(opts.headers || {}),
      },
    });
  } catch (err) {
    throw new Error(navigator.onLine ? (err.message || "Network error") : "offline");
  }
  if (res.status === 401) {
    // Server restarted or token expired — clear stored token so user gets login page
    setToken("");
    window.location.reload();
    return;
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// ─── Read return-view once at module load (before React StrictMode double-runs effects) ──
const _pendingReturnView = (() => {
  const rv = localStorage.getItem("cd_return_view");
  if (rv) localStorage.removeItem("cd_return_view");
  return rv || null;
})();

// ─── Clipboard helper (works on HTTP/mobile) ──────────────────────────────────

function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.cssText = "position:fixed;opacity:0;pointer-events:none;";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand("copy"); } catch (_) {}
  document.body.removeChild(ta);
  return Promise.resolve();
}

// ─── Mobile hook ──────────────────────────────────────────────────────────────

function useIsMobile() {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 640
  );
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return mobile;
}

// ─── Online status hook ───────────────────────────────────────────────────────

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const dn = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', dn);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', dn); };
  }, []);
  return online;
}

// ─── Scoring helpers ─────────────────────────────────────────────────────────

function printMatchScorecard(match, players, playerMap, resultText) {
  const fmtOvers = (lb) => `${Math.floor(lb / 6)}.${lb % 6}`;
  const innings = match.innings || [];

  let body = "";

  // Header
  body += `<div class="match-header">
    <h1>${match.teamA?.name || "Team A"} vs ${match.teamB?.name || "Team B"}</h1>
    <div class="toss">${(match.tossWonBy === "A" ? match.teamA?.name : match.teamB?.name) || ""} won toss & chose to ${(match.tossChoice || "").toLowerCase()}</div>
    ${resultText ? `<div class="result">${resultText}</div>` : ""}
  </div>`;

  // Score summary
  body += `<div class="scores">`;
  const _pe = match.penaltyExtras || false;
  const _wr = match.wideRuns || 1;
  for (let i = 0; i < innings.length; i++) {
    const inn = innings[i];
    const st = calcInning(inn.balls || [], _pe, _wr);
    const teamName = inn.batting === "A" ? (match.teamA?.name || "Team A") : (match.teamB?.name || "Team B");
    body += `<span class="score-chip">${teamName}: <strong>${st.total}/${st.wickets}</strong> (${st.overs} ov)</span> `;
  }
  body += `</div>`;

  // Each innings
  for (let i = 0; i < innings.length; i++) {
    const inn = innings[i];
    const balls = inn.balls || [];
    const st = calcInning(balls, _pe, _wr);
    const batTeam = inn.batting || "A";
    const bowlTeam = batTeam === "A" ? "B" : "A";
    const batIds = batTeam === "A" ? (match.teamA?.playerIds || []) : (match.teamB?.playerIds || []);
    const bowlIds = bowlTeam === "A" ? (match.teamA?.playerIds || []) : (match.teamB?.playerIds || []);
    const teamName = batTeam === "A" ? (match.teamA?.name || "Team A") : (match.teamB?.name || "Team B");

    // Batting table
    const batters = {};
    let runningTotal = 0, legalSoFar = 0;
    const fow = [];
    for (const b of balls) {
      runningTotal += (b.runs || 0) + (b.extras || 0);
      if (!b.wide && !b.noBall) legalSoFar++;
      if (b.batter) {
        if (!batters[b.batter]) batters[b.batter] = { runs: 0, balls: 0, fours: 0, sixes: 0, dismissed: false, how: "" };
        if (!b.wide) { batters[b.batter].runs += b.runs || 0; batters[b.batter].balls++; }
        if (b.runs === 4) batters[b.batter].fours++;
        if (b.runs === 6) batters[b.batter].sixes++;
      }
      if (b.wicket) {
        fow.push({ score: runningTotal, overs: fmtOvers(legalSoFar), batter: b.wicket.batter });
        if (b.wicket.batter && batters[b.wicket.batter]) {
          batters[b.wicket.batter].dismissed = true;
          batters[b.wicket.batter].how = b.wicket.type || "out";
        }
      }
    }

    body += `<h2>Innings ${i+1}: ${teamName} — ${st.total}/${st.wickets} (${st.overs})</h2>`;
    body += `<table><thead><tr><th>Batter</th><th>How Out</th><th>R</th><th>B</th><th>4s</th><th>6s</th><th>SR</th></tr></thead><tbody>`;
    for (const pid of batIds) {
      if (!batters[pid] && !batIds.includes(pid)) continue;
      const b = batters[pid];
      if (!b && !batIds.includes(pid)) continue;
      if (!b) continue;
      const sr = b.balls > 0 ? (b.runs / b.balls * 100).toFixed(1) : "-";
      const name = playerMap[pid] || pid;
      body += `<tr><td><strong>${name}</strong></td><td>${b.dismissed ? b.how : "<em>not out</em>"}</td><td>${b.runs}</td><td>${b.balls}</td><td>${b.fours}</td><td>${b.sixes}</td><td>${sr}</td></tr>`;
    }
    body += `<tr class="extras"><td colspan="2">Extras</td><td>${st.extras || 0}</td><td colspan="4"></td></tr>`;
    body += `<tr class="total"><td colspan="2"><strong>Total</strong></td><td><strong>${st.total}/${st.wickets}</strong></td><td colspan="4">(${st.overs} overs)</td></tr>`;
    body += `</tbody></table>`;

    if (fow.length > 0) {
      body += `<div class="fow"><strong>Fall of Wickets:</strong> `;
      body += fow.map((f, idx) => `${idx+1}-${f.score} (${playerMap[f.batter] || f.batter}, ${f.overs})`).join(" &nbsp;·&nbsp; ");
      body += `</div>`;
    }

    // Bowling table
    const bowlers = {};
    for (const b of balls) {
      if (!b.bowler) continue;
      if (!bowlers[b.bowler]) bowlers[b.bowler] = { runs: 0, balls: 0, wides: 0, noBalls: 0, wickets: 0 };
      bowlers[b.bowler].runs += (b.runs || 0) + (b.extras || 0);
      if (!b.wide && !b.noBall) bowlers[b.bowler].balls++;
      if (b.wide) bowlers[b.bowler].wides++;
      if (b.noBall) bowlers[b.bowler].noBalls++;
      if (b.wicket) bowlers[b.bowler].wickets++;
    }
    body += `<table><thead><tr><th>Bowler</th><th>O</th><th>R</th><th>W</th><th>Wd</th><th>Nb</th><th>Econ</th></tr></thead><tbody>`;
    for (const pid of bowlIds) {
      const bw = bowlers[pid];
      if (!bw) continue;
      const ov = `${Math.floor(bw.balls/6)}.${bw.balls%6}`;
      const econ = bw.balls > 0 ? (bw.runs / (bw.balls/6)).toFixed(1) : "-";
      body += `<tr><td>${playerMap[pid] || pid}</td><td>${ov}</td><td>${bw.runs}</td><td>${bw.wickets}</td><td>${bw.wides}</td><td>${bw.noBalls}</td><td>${econ}</td></tr>`;
    }
    body += `</tbody></table>`;
  }

  // MOTM
  if (match.motm) {
    body += `<div class="motm">🏆 Man of the Match: <strong>${playerMap[match.motm] || match.motm}</strong></div>`;
  }

  // Summary
  if (match.aiSummary) {
    body += `<h2>Match Summary</h2><div class="summary-text">${match.aiSummary.replace(/\n/g, "<br>")}</div>`;
  }

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${match.teamA?.name || "Team A"} vs ${match.teamB?.name || "Team B"} — Scorecard</title>
<style>
  body { font-family: Arial, sans-serif; max-width: 900px; margin: 0 auto; padding: 24px; color: #222; font-size: 13px; }
  h1 { color: #1a5c38; font-size: 22px; margin-bottom: 4px; }
  h2 { color: #1a5c38; font-size: 16px; border-bottom: 2px solid #1a5c38; padding-bottom: 4px; margin-top: 24px; }
  .toss { color: #666; font-size: 12px; margin-bottom: 4px; }
  .result { font-size: 18px; font-weight: 700; color: #1a5c38; margin: 8px 0 16px; }
  .scores { margin-bottom: 16px; }
  .score-chip { background: #f0faf4; border: 1px solid #c8e6c9; padding: 4px 12px; border-radius: 20px; margin-right: 8px; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; }
  th { background: #1a5c38; color: white; padding: 6px 8px; text-align: left; }
  td { padding: 5px 8px; border-bottom: 1px solid #eee; }
  tr:nth-child(even) td { background: #f9f9f9; }
  tr.extras td, tr.total td { background: #f0faf4; font-weight: 600; }
  .fow { font-size: 11px; color: #555; margin-bottom: 16px; padding: 6px 8px; background: #f5f5f5; border-radius: 4px; }
  .motm { font-size: 15px; margin: 16px 0; padding: 10px 14px; background: #fff9e6; border: 1px solid #f5c842; border-radius: 6px; }
  .summary-text { line-height: 1.7; white-space: pre-wrap; color: #333; }
  @media print { body { padding: 8px; } .no-print { display: none; } }
</style>
</head><body>
<div class="no-print" style="margin-bottom:20px;display:flex;gap:10px">
  <button onclick="window.close()" style="padding:8px 18px;border:none;background:#1a5c38;color:white;border-radius:6px;cursor:pointer;font-size:14px;font-family:Arial,sans-serif">← Back / Close</button>
  <button onclick="window.print()" style="padding:8px 18px;border:1px solid #1a5c38;background:white;color:#1a5c38;border-radius:6px;cursor:pointer;font-size:14px;font-family:Arial,sans-serif">Print</button>
</div>
${body}
<div style="text-align:center;margin-top:30px;color:#aaa;font-size:11px">Generated by CricDada</div>
</body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

// ─── Auction localStorage helpers ────────────────────────────────────────────
const AUCTIONS_KEY = "cd_auctions";
function getAuctions() {
  try { return JSON.parse(localStorage.getItem(AUCTIONS_KEY) || "[]"); } catch { return []; }
}
function saveAuctionToStorage(auction) {
  const list = getAuctions();
  const idx = list.findIndex(a => a.id === auction.id);
  if (idx >= 0) list[idx] = auction; else list.unshift(auction);
  localStorage.setItem(AUCTIONS_KEY, JSON.stringify(list));
}
function deleteAuctionFromStorage(id) {
  localStorage.setItem(AUCTIONS_KEY, JSON.stringify(getAuctions().filter(a => a.id !== id)));
}

function calcInning(balls, penaltyExtras = false, wideRunsSetting = 1) {
  const batters = {};
  const bowlers = {};
  let total = 0;
  let wickets = 0;
  let legalBalls = 0;

  for (const b of balls) {
    const isExtra = b.wide || b.noBall;

    if (penaltyExtras && isExtra) {
      // Penalty mode: 3-run base penalty for every extra delivery
      // Wide: 3 + any overthrow extras, all go to team total (no batter credit)
      // No-ball: 3 to extras; batter's own runs credited to batter
      legalBalls++;
      const bowl = b.bowler;
      if (b.wide) {
        const additionalExtras = Math.max(0, (b.extras || 0) - wideRunsSetting);
        const penaltyRuns = 3 + additionalExtras;
        total += penaltyRuns;
        if (bowl) {
          if (!bowlers[bowl]) bowlers[bowl] = { runs: 0, balls: 0, wickets: 0, wides: 0, noBalls: 0 };
          bowlers[bowl].runs += penaltyRuns;
          bowlers[bowl].balls++;
        }
      } else {
        // No-ball: 3 penalty to extras, batter's runs credited to batter
        const batterRuns = b.runs || 0;
        total += 3 + batterRuns;
        const bat = b.batter;
        if (bat) {
          if (!batters[bat]) batters[bat] = { runs: 0, balls: 0, fours: 0, sixes: 0, dots: 0, ones: 0, twos: 0, threes: 0, dismissed: false, dismissalType: null };
          batters[bat].runs += batterRuns;
          // balls faced does NOT increment on no-ball (it's a free hit)
          if (batterRuns === 4) batters[bat].fours++;
          if (batterRuns === 6) batters[bat].sixes++;
        }
        if (bowl) {
          if (!bowlers[bowl]) bowlers[bowl] = { runs: 0, balls: 0, wickets: 0, wides: 0, noBalls: 0 };
          bowlers[bowl].runs += 3 + batterRuns;
          bowlers[bowl].balls++;
        }
      }
    } else {
      total += (b.runs || 0) + (b.extras || 0);
      if (!b.wide && !b.noBall) legalBalls++;

      const bat = b.batter;
      if (bat) {
        if (!batters[bat])
          batters[bat] = { runs: 0, balls: 0, fours: 0, sixes: 0, dots: 0, ones: 0, twos: 0, threes: 0, dismissed: false, dismissalType: null };
        if (!b.wide) {
          batters[bat].balls++;
          batters[bat].runs += b.runs || 0;
          const r = b.runs || 0;
          if (r === 0) batters[bat].dots++;
          else if (r === 1) batters[bat].ones++;
          else if (r === 2) batters[bat].twos++;
          else if (r === 3) batters[bat].threes++;
        }
        if (b.runs === 4) batters[bat].fours++;
        if (b.runs === 6) batters[bat].sixes++;
      }

      const bowl = b.bowler;
      if (bowl) {
        if (!bowlers[bowl]) bowlers[bowl] = { runs: 0, balls: 0, wickets: 0, wides: 0, noBalls: 0 };
        bowlers[bowl].runs += (b.runs || 0) + (b.extras || 0);
        if (!b.wide && !b.noBall) bowlers[bowl].balls++;
        if (b.wide) bowlers[bowl].wides++;
        if (b.noBall) bowlers[bowl].noBalls++;
      }
    }

    if (b.wicket) {
      wickets++;
      const wb = b.wicket.batter;
      if (wb && batters[wb]) {
        batters[wb].dismissed = true;
        batters[wb].dismissalType = b.wicket.type;
      }
      const bowl = b.bowler;
      if (bowl && bowlers[bowl] && b.wicket.type !== "Run out") {
        bowlers[bowl].wickets++;
      }
    }
  }

  const overs = Math.floor(legalBalls / 6) + "." + (legalBalls % 6);
  return { total, wickets, legalBalls, overs, batters, bowlers };
}

function suggestMOTM(match, playerMap) {
  const scores = {};
  const pe = match.penaltyExtras || false;
  const wr = match.wideRuns || 1;
  for (const inn of match.innings || []) {
    const stats = calcInning(inn.balls || [], pe, wr);
    for (const [id, b] of Object.entries(stats.batters))
      scores[id] = (scores[id] || 0) + b.runs * 0.5;
    for (const [id, bw] of Object.entries(stats.bowlers))
      scores[id] = (scores[id] || 0) + bw.wickets * 15 - bw.runs * 0.1;
  }
  return Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
}

async function getMatchSummary(match, playerMap) {
  const lines = [];
  const pe = match.penaltyExtras || false;
  const wr = match.wideRuns || 1;
  for (const inn of match.innings || []) {
    const stats = calcInning(inn.balls || [], pe, wr);
    const team = inn.batting === "A" ? match.teamA?.name : match.teamB?.name;
    lines.push(`${team} scored ${stats.total}/${stats.wickets} in ${stats.overs} overs`);
  }
  const prompt = `You are a cricket commentator. Write a match summary for:\n${lines.join("\n")}\nTeams: ${match.teamA?.name} vs ${match.teamB?.name}\nWrite 5 lines per team and 10 lines overall match summary.`;
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": process.env.REACT_APP_ANTHROPIC_KEY || "",
      "anthropic-version": "2023-06-01",
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-5",
      max_tokens: 1024,
      messages: [{ role: "user", content: prompt }],
    }),
  });
  const data = await res.json();
  return data.content?.[0]?.text || "Summary not available";
}

// ─── Themes ───────────────────────────────────────────────────────────────────

const THEMES = {
  brown:  { primary: "#7A2700", primaryDark: "#5c1d00", light: "#fdf0e8",  label: "Premium Brown"  },
  orange: { primary: "#FF7E00", primaryDark: "#cc6400", light: "#fff8f0",  label: "Neon Orange"    },
  green:  { primary: "#1A5C38", primaryDark: "#145230", light: "#f0f4f0",  label: "Forest Green"   },
  blue:   { primary: "#1a4a8a", primaryDark: "#133870", light: "#f0f4fb",  label: "Royal Blue"     },
};

// logo-{theme}-light.png = colored icon on white bg  (login page)
// logo-{theme}-dark.png  = white icon on theme-colored bg (navbar, blends seamlessly)

const _themeName = localStorage.getItem("cd_theme") || "orange";
const T = THEMES[_themeName] || THEMES.orange;

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  navbar: {
    backgroundColor: T.primary,
    color: "white",
    padding: "6px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 6,
  },
  navBrand: { fontSize: 23, fontWeight: 700, letterSpacing: 1 },
  navLinks: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  navBtn: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.5)",
    color: "white",
    padding: "4px 10px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
  },
  navBtnActive: {
    background: "rgba(255,255,255,0.2)",
    border: "1px solid white",
    color: "white",
    padding: "4px 10px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  page: { maxWidth: 960, margin: "0 auto", padding: "20px 16px" },
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    padding: "20px 16px",
    marginBottom: 16,
  },
  btn: {
    backgroundColor: T.primary,
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  btnSm: {
    backgroundColor: T.primary,
    color: "white",
    border: "none",
    borderRadius: 4,
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  btnDanger: {
    backgroundColor: "#c0392b",
    color: "white",
    border: "none",
    borderRadius: 4,
    padding: "8px 14px",
    cursor: "pointer",
    fontSize: 13,
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  btnSecondary: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: 6,
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: 14,
  },
  btnOutline: {
    backgroundColor: "white",
    color: T.primary,
    border: "2px solid " + T.primary,
    borderRadius: 6,
    padding: "9px 19px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  input: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "9px 12px",
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: "9px 12px",
    fontSize: 14,
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "white",
  },
  label: { fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 4, display: "block" },
  error: { color: "#c0392b", fontSize: 13, marginTop: 8 },
  success: { color: T.primary, fontSize: 13, marginTop: 8 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { backgroundColor: "#f5f5f5", padding: "8px 10px", textAlign: "left", borderBottom: "2px solid #ddd", fontWeight: 600 },
  td: { padding: "7px 10px", borderBottom: "1px solid #eee" },
  h2: { margin: "0 0 16px", color: T.primary },
  h3: { margin: "0 0 12px", color: "#333" },
  badge: (color) => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: color === "live" ? "#e74c3c" : color === "completed" ? "#27ae60" : "#f39c12",
    color: "white",
  }),
  modal: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  modalBox: {
    backgroundColor: "white",
    borderRadius: 10,
    padding: 32,
    minWidth: 320,
    maxWidth: 480,
    width: "90%",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  },
};

// ─── ScorecardTable ───────────────────────────────────────────────────────────

function ScorecardTable({ balls, playerIds, allPlayerIds, playerMap, striker, nonStriker, fow, roleMap, penaltyExtras, wideRuns }) {
  const stats = calcInning(balls || [], penaltyExtras || false, wideRuns || 1);

  const batters = playerIds
    ? playerIds.map((id) => ({ id, ...(stats.batters[id] || { runs: 0, balls: 0, fours: 0, sixes: 0, dots: 0, ones: 0, twos: 0, threes: 0, dismissed: false, dismissalType: null }) }))
    : Object.entries(stats.batters).map(([id, s]) => ({ id, ...s }));

  const bowlers = allPlayerIds
    ? allPlayerIds.filter((id) => stats.bowlers[id]).map((id) => ({ id, ...stats.bowlers[id] }))
    : Object.entries(stats.bowlers).map(([id, s]) => ({ id, ...s }));

  const fmtOvers = (b) => `${Math.floor(b / 6)}.${b % 6}`;

  const visibleBatters = batters.filter((b) => b.balls > 0 || b.runs > 0 || b.dismissed || b.id === striker || b.id === nonStriker);

  return (
    <div>
      <h4 style={{ marginBottom: 8, color: T.primary }}>Batting</h4>
      <div className="table-scroll"><table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Batter</th>
            <th style={S.th}>Status</th>
            <th style={{ ...S.th, textAlign: "right" }}>R</th>
            <th style={{ ...S.th, textAlign: "right" }}>B</th>
            <th style={{ ...S.th, textAlign: "right" }}>0s</th>
            <th style={{ ...S.th, textAlign: "right" }}>1s</th>
            <th style={{ ...S.th, textAlign: "right" }}>2s</th>
            <th style={{ ...S.th, textAlign: "right" }}>3s</th>
            <th style={{ ...S.th, textAlign: "right" }}>4s</th>
            <th style={{ ...S.th, textAlign: "right" }}>6s</th>
            <th style={{ ...S.th, textAlign: "right" }}>SR</th>
          </tr>
        </thead>
        <tbody>
          {visibleBatters.map((b) => {
            const isStriker = b.id === striker;
            const isNonStriker = b.id === nonStriker;
            const notOut = !b.dismissed && (isStriker || isNonStriker);
            return (
              <tr key={b.id} style={notOut ? { background: "#f0faf4" } : {}}>
                <td style={S.td}>
                  <strong>{playerMap[b.id] || b.id}</strong>
                  {isStriker && <span style={{ color: "#e65100", marginLeft: 4 }} title="On strike">★</span>}
                  {isNonStriker && <span style={{ color: "#1565c0", marginLeft: 4 }} title="Non-striker">†</span>}
                  {roleMap?.[b.id]?.map(r => <span key={r} style={{ fontSize: 10, fontWeight: 700, backgroundColor: r === "C" ? T.primary : r === "VC" ? "#2980b9" : "#7d3c98", color: "white", padding: "1px 5px", borderRadius: 8, marginLeft: 4 }}>{r}</span>)}
                </td>
                <td style={S.td}>
                  {b.dismissed ? (b.dismissalType || "out") : (notOut ? <span style={{ color: T.primary, fontStyle: "italic" }}>not out</span> : "-")}
                </td>
                <td style={{ ...S.td, textAlign: "right" }}><strong>{b.runs}</strong></td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.balls}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.dots}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.ones}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.twos}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.threes}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.fours}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.sixes}</td>
                <td style={{ ...S.td, textAlign: "right" }}>{b.balls > 0 ? ((b.runs / b.balls) * 100).toFixed(0) : "-"}</td>
              </tr>
            );
          })}
          {(() => {
            // Extras row — shown in both normal and penalty mode
            // Normal mode: Wd = b.extras (wideRuns + overthrows), NB = b.extras (penalty only; batter runs credited in batting rows)
            // Penalty mode: Wd = 3 + overthrows, NB = 3 (batter runs credited in batting rows)
            let wdCount = 0, wdTotal = 0, nbCount = 0, nbTotal = 0;
            for (const b of (balls || [])) {
              if (b.wide) {
                wdCount++;
                wdTotal += penaltyExtras
                  ? 3 + Math.max(0, (b.extras || 0) - (wideRuns || 1))
                  : (b.extras || 0);
              } else if (b.noBall) {
                nbCount++;
                nbTotal += penaltyExtras ? 3 : (b.extras || 0);
              }
            }
            const extrasTotal = wdTotal + nbTotal;
            if (extrasTotal === 0) return null;
            const parts = [];
            if (wdCount > 0) parts.push(`Wd ×${wdCount} = ${wdTotal}`);
            if (nbCount > 0) parts.push(`NB ×${nbCount} = ${nbTotal}`);
            return (
              <tr style={{ backgroundColor: "#fff8e1" }}>
                <td style={S.td} colSpan={2}>
                  <span style={{ color: "#795548", fontWeight: 600 }}>Extras</span>
                  <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>({parts.join(", ")})</span>
                </td>
                <td style={{ ...S.td, textAlign: "right" }}><strong>{extrasTotal}</strong></td>
                <td style={S.td} colSpan={8} />
              </tr>
            );
          })()}
        </tbody>
      </table></div>

      <h4 style={{ marginTop: 20, marginBottom: 8, color: T.primary }}>Bowling</h4>
      <div className="table-scroll"><table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Bowler</th>
            <th style={{ ...S.th, textAlign: "right" }}>O</th>
            <th style={{ ...S.th, textAlign: "right" }}>R</th>
            <th style={{ ...S.th, textAlign: "right" }}>W</th>
            <th style={{ ...S.th, textAlign: "right" }}>Wd</th>
            <th style={{ ...S.th, textAlign: "right" }}>NB</th>
            <th style={{ ...S.th, textAlign: "right" }}>Econ</th>
          </tr>
        </thead>
        <tbody>
          {bowlers.map((b) => (
            <tr key={b.id}>
              <td style={S.td}>{playerMap[b.id] || b.id}</td>
              <td style={{ ...S.td, textAlign: "right" }}>{fmtOvers(b.balls)}</td>
              <td style={{ ...S.td, textAlign: "right" }}>{b.runs}</td>
              <td style={{ ...S.td, textAlign: "right" }}><strong>{b.wickets}</strong></td>
              <td style={{ ...S.td, textAlign: "right" }}>{b.wides}</td>
              <td style={{ ...S.td, textAlign: "right" }}>{b.noBalls}</td>
              <td style={{ ...S.td, textAlign: "right" }}>{b.balls > 0 ? (b.runs / (b.balls / 6)).toFixed(1) : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {fow && fow.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ marginBottom: 8, color: T.primary }}>Fall of Wickets</h4>
          <div style={{ fontSize: 13, color: "#444", display: "flex", flexWrap: "wrap", gap: 8 }}>
            {fow.map((f, i) => (
              <span key={i} style={{ background: "#f5f5f5", border: "1px solid #ddd", borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap" }}>
                <strong>{i + 1}-{f.score}</strong> <span style={{ color: "#666" }}>({playerMap[f.batter] || f.batter}, {f.overs} ov)</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Over-by-Over breakdown */}
      {balls && balls.length > 0 && (() => {
        // Group balls into overs
        // In penalty mode: ALL balls (including extras) count toward over completion
        // In normal mode: only legal balls count
        const overs = [];
        let curOver = [];
        let ballsInOver = 0;
        let overNum = 0;
        for (const b of balls) {
          curOver.push(b);
          const countsInOver = penaltyExtras ? true : (!b.wide && !b.noBall);
          if (countsInOver) {
            ballsInOver++;
            if (ballsInOver === 6) {
              overs.push({ num: overNum, balls: curOver });
              overNum++;
              curOver = [];
              ballsInOver = 0;
            }
          }
        }
        if (curOver.length > 0) overs.push({ num: overNum, balls: curOver, incomplete: true });

        // Determine bowler for each over from the first ball that has a bowler set
        overs.forEach(ov => {
          ov.bowlerId = ov.balls.find(b => b.bowler)?.bowler || null;
        });

        const getChip = (b) => {
          const w = b.wicket;
          if (b.wide) {
            const extra = (b.extras || 0) - 1;
            const label = extra > 0 ? `Wd+${extra}` : "Wd";
            return { text: w ? label + "+W" : label, bg: "#e67e22" };
          }
          if (b.noBall) {
            const label = (b.runs || 0) > 0 ? `Nb+${b.runs}` : "Nb";
            return { text: w ? label + "+W" : label, bg: "#d35400" };
          }
          if (w) return { text: (b.runs || 0) > 0 ? `${b.runs}W` : "W", bg: "#c0392b" };
          if ((b.runs || 0) === 4) return { text: "4", bg: "#2980b9" };
          if ((b.runs || 0) === 6) return { text: "6", bg: "#8e44ad" };
          return { text: (b.runs || 0) === 0 ? "·" : String(b.runs), bg: "#555" };
        };

        return (
          <div style={{ marginTop: 20 }}>
            <h4 style={{ marginBottom: 10, color: T.primary }}>Over-by-Over</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {overs.map((ov) => {
                const ovRuns = penaltyExtras
                  ? ov.balls.reduce((s, b) => {
                      if (b.wide) return s + 3 + Math.max(0, (b.extras || 0) - (wideRuns || 1));
                      if (b.noBall) return s + 3 + (b.runs || 0);
                      return s + (b.runs || 0) + (b.extras || 0);
                    }, 0)
                  : ov.balls.reduce((s, b) => s + (b.runs || 0) + (b.extras || 0), 0);
                const ovWkts = ov.balls.filter(b => b.wicket).length;
                return (
                  <div key={ov.num} style={{ backgroundColor: "#f9f9f9", border: "1px solid #e8e8e8", borderRadius: 8, padding: "8px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.primary, minWidth: 52 }}>
                        Over {ov.num + 1}{ov.incomplete ? "*" : ""}
                      </span>
                      {ov.bowlerId && playerMap[ov.bowlerId] && (
                        <span style={{ fontSize: 11, color: "#7d3c98", fontWeight: 600 }}>
                          ⚾ {playerMap[ov.bowlerId]}
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: "#555" }}>
                        {ovRuns} run{ovRuns !== 1 ? "s" : ""}
                        {ovWkts > 0 && <span style={{ color: "#c0392b", fontWeight: 700 }}>, {ovWkts}W</span>}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {ov.balls.map((b, bi) => {
                        const chip = getChip(b);
                        return (
                          <span
                            key={bi}
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              minWidth: 32, height: 28, padding: "0 6px",
                              borderRadius: 6, backgroundColor: chip.bg,
                              color: "white", fontSize: 11, fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {chip.text}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Icon helpers ─────────────────────────────────────────────────────────────

const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
  </svg>
);

const IconDelete = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const IconShare = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ display: "block" }}>
    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
  </svg>
);

// ─── Themed Logo ──────────────────────────────────────────────────────────────
// Loads the orange base PNG and recolours it to match the active theme via
// canvas pixel manipulation.  Works for every theme including blue (whose
// pre-built PNG file has the wrong colour).
//
// variant="dark"  → white icon on theme-coloured bg  (navbar)
// variant="light" → theme-coloured icon on white bg   (login page)

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
}

function ThemedLogo({ size = 96, variant = "dark", style = {} }) {
  const canvasRef = useRef(null);
  // Always use the orange base — we recolour in JS so every theme looks right.
  const src = variant === "dark" ? "/logo-orange-dark.png" : "/logo-orange-light.png";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
      try {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d = imgData.data;
        const [tr, tg, tb] = hexToRgb(T.primary);
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i+1], b = d[i+2], a = d[i+3];
          if (a < 10) continue; // fully transparent – skip
          const lum = 0.299*r + 0.587*g + 0.114*b;
          if (variant === "dark") {
            // dark bg: pixels that are NOT white (lum < 230) get the theme colour
            if (lum < 230) { d[i]=tr; d[i+1]=tg; d[i+2]=tb; }
          } else {
            // light bg: pixels that are NOT near-white (lum < 240) get theme colour
            if (lum < 240) { d[i]=tr; d[i+1]=tg; d[i+2]=tb; }
          }
        }
        ctx.putImageData(imgData, 0, 0);
      } catch (_) { /* CORS taint – shows original orange, acceptable fallback */ }
    };
  }, [src]); // eslint-disable-line

  return (
    <canvas
      ref={canvasRef}
      style={{ width: size, height: size, display: "block", objectFit: "contain", ...style }}
    />
  );
}

// ─── Password helpers ─────────────────────────────────────────────────────────

function validatePassword(pass) {
  if (!pass || pass.length < 8) return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(pass)) return "Password must contain at least 1 uppercase letter";
  if (!/[a-z]/.test(pass)) return "Password must contain at least 1 lowercase letter";
  if (!/[0-9]/.test(pass)) return "Password must contain at least 1 number";
  return null;
}

// ─── ForgotPasswordView ───────────────────────────────────────────────────────

function ForgotPasswordView({ onBack }) {
  const [step, setStep] = useState("email"); // email | token
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [generatedToken, setGeneratedToken] = useState("");
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const d = await apiFetch("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
      setGeneratedToken(d.token);
      setToken(d.token); // auto-fill so user doesn't have to type it
      setStep("token");
    } catch (ex) {
      let m = ex.message; try { m = JSON.parse(m).error || m; } catch {}
      setErr(m);
    } finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setErr("");
    const ve = validatePassword(pass);
    if (ve) { setErr(ve); return; }
    if (pass !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await apiFetch("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password: pass }) });
      setMsg("Password reset successfully! You can now sign in.");
    } catch (ex) {
      let m = ex.message; try { m = JSON.parse(m).error || m; } catch {}
      setErr(m);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: T.light, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ ...S.card, width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <ThemedLogo size={100} variant="light" style={{ margin: "0 auto 4px", borderRadius: 12 }} />
        </div>
        <h3 style={{ color: T.primary, marginBottom: 16, textAlign: "center" }}>Reset Password</h3>
        {msg ? (
          <div>
            <div style={{ ...S.success, marginBottom: 16 }}>{msg}</div>
            <button style={{ ...S.btn, width: "100%" }} onClick={onBack}>Back to Sign In</button>
          </div>
        ) : step === "email" ? (
          <form onSubmit={requestReset}>
            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Enter your registered email. A reset token will be displayed on screen.</p>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="you@email.com" />
            </div>
            {err && <div style={S.error}>{err}</div>}
            <button style={{ ...S.btn, width: "100%", marginTop: 8 }} disabled={loading}>{loading ? "Sending..." : "Get Reset Token"}</button>
            <button type="button" style={{ ...S.btnSecondary, width: "100%", marginTop: 10 }} onClick={onBack}>Back to Sign In</button>
          </form>
        ) : (
          <form onSubmit={doReset}>
            <div style={{ backgroundColor: T.light, border: "1px solid " + T.primary, borderRadius: 6, padding: "10px 14px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Reset token (auto-applied, valid 30 min)</div>
              <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 24, color: T.primary, letterSpacing: 6 }}>{generatedToken}</div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>New Password</label>
              <input style={S.input} type="password" value={pass} onChange={e => setPass(e.target.value)} required placeholder="Min 8 chars, A-Z, a-z, 0-9" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Confirm Password</label>
              <input style={S.input} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required placeholder="Repeat new password" />
            </div>
            {err && <div style={S.error}>{err}</div>}
            <button style={{ ...S.btn, width: "100%", marginTop: 8 }} disabled={loading}>{loading ? "Resetting..." : "Reset Password"}</button>
            <button type="button" style={{ ...S.btnSecondary, width: "100%", marginTop: 10 }} onClick={onBack}>Back to Sign In</button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── LoginView ────────────────────────────────────────────────────────────────

function LoginView({ onLogin, onGoRegister, onForgotPassword }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const data = await apiFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password: pass }),
      });
      setToken(data.token);
      const me = await apiFetch("/api/auth/me");
      // Cache credentials for offline login
      cacheAuth(email, pass, me, data.token).catch(() => {});
      onLogin(me);
    } catch (ex) {
      // If offline, try cached credentials
      if (!navigator.onLine || ex.message === "offline") {
        try {
          const cached = await getAuth();
          if (cached && cached.email === email) {
            const h = await hashPassword(pass);
            if (h === cached.passwordHash) {
              setToken(cached.token);
              onLogin(cached.user);
              return;
            }
          }
          setErr("Offline — credentials don't match cached login");
        } catch (_) {
          setErr("Offline — no cached login found");
        }
        return;
      }
      let msg = ex.message;
      try { msg = JSON.parse(msg).error || msg; } catch {}
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: T.light, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ ...S.card, width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <ThemedLogo size={150} variant="light" style={{ margin: "0 auto 4px", borderRadius: 16 }} />
          <p style={{ color: "#666", margin: 0 }}>Sign in to continue</p>
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Email / Username</label>
            <input style={S.input} type="text" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com or username" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={S.label}>Password</label>
            <input style={S.input} type="password" value={pass} onChange={(e) => setPass(e.target.value)} required placeholder="••••••••" />
          </div>
          {err && <div style={S.error}>{err}</div>}
          <button style={{ ...S.btn, width: "100%", marginTop: 12 }} disabled={loading}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <div style={{ textAlign: "center", marginTop: 12, fontSize: 13 }}>
          <button style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 12 }} onClick={onForgotPassword}>
            Forgot Password?
          </button>
        </div>
        <div style={{ textAlign: "center", marginTop: 8, fontSize: 13 }}>
          No account?{" "}
          <button style={{ background: "none", border: "none", color: T.primary, cursor: "pointer", fontWeight: 600 }} onClick={onGoRegister}>
            Register
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RegisterView ─────────────────────────────────────────────────────────────

function RegisterView({ onGoLogin }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [requestedRole, setRequestedRole] = useState("player");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    const ve = validatePassword(pass);
    if (ve) { setErr(ve); return; }
    setLoading(true);
    try {
      const data = await apiFetch("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({ name, email, password: pass, requestedRole }),
      });
      setMsg(data.message || "Registration submitted! Awaiting admin approval.");
    } catch (ex) {
      let m = ex.message;
      try { m = JSON.parse(m).error || m; } catch {}
      setErr(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: T.light, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ ...S.card, width: "100%", maxWidth: 420 }}>
        <h2 style={{ ...S.h2, textAlign: "center" }}>Create Account</h2>
        {msg ? (
          <div>
            <div style={{ ...S.success, fontSize: 15, padding: "12px 16px", backgroundColor: "#eafaf1", borderRadius: 6 }}>{msg}</div>
            <button style={{ ...S.btn, width: "100%", marginTop: 16 }} onClick={onGoLogin}>Back to Login</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Full Name</label>
              <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Email / Username</label>
              <input style={S.input} type="text" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com or username" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Password</label>
              <input style={S.input} type="password" value={pass} onChange={(e) => setPass(e.target.value)} required placeholder="••••••••" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Register as</label>
              <div style={{ display: "flex", gap: 10 }}>
                {[["player", "🏏 Player"], ["captain", "⭐ Captain"]].map(([val, label]) => (
                  <button key={val} type="button"
                    style={{ flex: 1, padding: "10px 0", borderRadius: 6, border: "2px solid",
                      borderColor: requestedRole === val ? T.primary : "#ddd",
                      backgroundColor: requestedRole === val ? T.primary : "white",
                      color: requestedRole === val ? "white" : "#333",
                      fontWeight: 600, fontSize: 14, cursor: "pointer" }}
                    onClick={() => setRequestedRole(val)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {err && <div style={S.error}>{err}</div>}
            <button style={{ ...S.btn, width: "100%", marginTop: 8 }} disabled={loading}>
              {loading ? "Submitting..." : "Register"}
            </button>
            <button type="button" style={{ ...S.btnSecondary, width: "100%", marginTop: 10 }} onClick={onGoLogin}>
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── AdminView ────────────────────────────────────────────────────────────────

function AdminView() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerRole, setNewPlayerRole] = useState("BATSMAN");
  const [newPlayerTeamId, setNewPlayerTeamId] = useState("");
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editingPlayerName, setEditingPlayerName] = useState("");
  const [editingPlayerRole, setEditingPlayerRole] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [statsPlayer, setStatsPlayer] = useState(null); // {id, name}
  const [statsData, setStatsData] = useState(null);
  const [statsTab, setStatsTab] = useState("batting");
  const [requireApproval, setRequireApproval] = useState(true);
  const [unlimitedMatchEdit, setUnlimitedMatchEdit] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState("");

  const loadPending = useCallback(async () => {
    try { setPending(await apiFetch("/api/admin/pending")); } catch {}
  }, []);

  const loadUsers = useCallback(async () => {
    try { setUsers(await apiFetch("/api/admin/users")); } catch {}
  }, []);

  const loadPlayers = useCallback(async () => {
    try { setPlayers(await apiFetch("/api/players")); } catch {}
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await apiFetch("/api/admin/settings");
      setRequireApproval(s.requireApproval !== false);
      setUnlimitedMatchEdit(s.unlimitedMatchEdit === true);
    } catch {}
  }, []);

  const toggleRequireApproval = async (val) => {
    setSettingsLoading(true);
    setSettingsMsg("");
    try {
      await apiFetch("/api/admin/settings", { method: "PUT", body: JSON.stringify({ requireApproval: val }) });
      setRequireApproval(val);
      setSettingsMsg(val ? "Approval required — new users will wait for admin approval." : "Approval disabled — new users log in immediately with their chosen role.");
    } catch (ex) {
      setSettingsMsg("Error: " + ex.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleUnlimitedMatchEdit = async (val) => {
    setSettingsLoading(true);
    setSettingsMsg("");
    try {
      await apiFetch("/api/admin/settings", { method: "PUT", body: JSON.stringify({ unlimitedMatchEdit: val }) });
      setUnlimitedMatchEdit(val);
      setSettingsMsg(val ? "Unlimited editing ON — completed matches can be edited at any time." : "Unlimited editing OFF — completed matches can only be edited within 36 hours.");
    } catch (ex) {
      setSettingsMsg("Error: " + ex.message);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadPending();
    loadUsers();
    loadPlayers();
    loadSettings();
    apiFetch("/api/teams").then(setTeams).catch(() => {});
  }, [loadPending, loadUsers, loadPlayers, loadSettings]);

  const approve = async (email, role) => {
    try {
      await apiFetch("/api/admin/approve", { method: "POST", body: JSON.stringify({ email, role }) });
      setMsg("User approved");
      loadPending(); loadUsers();
    } catch (ex) { setErr(ex.message); }
  };

  const deny = async (email) => {
    try {
      await apiFetch("/api/admin/pending/" + encodeURIComponent(email), { method: "DELETE" });
      loadPending();
    } catch (ex) { setErr(ex.message); }
  };

  const changeRole = async (email, role) => {
    try {
      await apiFetch("/api/admin/users/" + encodeURIComponent(email) + "/role", { method: "PUT", body: JSON.stringify({ role }) });
      loadUsers();
    } catch (ex) { setErr(ex.message); }
  };

  const addPlayer = async () => {
    if (!newPlayerName.trim()) return;
    try {
      await apiFetch("/api/players", {
        method: "POST",
        body: JSON.stringify({ name: newPlayerName, role: newPlayerRole, teamId: newPlayerTeamId || undefined }),
      });
      setNewPlayerName("");
      setNewPlayerRole("BATSMAN");
      setNewPlayerTeamId("");
      loadPlayers();
      apiFetch("/api/teams").then(setTeams).catch(() => {});
    } catch (ex) { setErr(ex.message); }
  };

  const deletePlayer = async (id) => {
    if (!window.confirm("Delete this player?")) return;
    try {
      await apiFetch("/api/players/" + id, { method: "DELETE" });
      loadPlayers();
    } catch (ex) { setErr(ex.message); }
  };

  const editPlayer = (p) => {
    setEditingPlayerId(p.id);
    setEditingPlayerName(p.name);
    setEditingPlayerRole(p.role || "BATSMAN");
  };

  const savePlayer = async (id) => {
    if (!editingPlayerName.trim()) return;
    try {
      await apiFetch("/api/players/" + id, { method: "PUT", body: JSON.stringify({ name: editingPlayerName, role: editingPlayerRole }) });
      setEditingPlayerId(null);
      setEditingPlayerName("");
      setEditingPlayerRole("");
      loadPlayers();
    } catch (ex) { setErr(ex.message); }
  };

  const tabStyle = (t) => ({
    padding: "8px 20px",
    cursor: "pointer",
    border: "none",
    borderBottom: tab === t ? "3px solid " + T.primary : "3px solid transparent",
    backgroundColor: "transparent",
    fontWeight: tab === t ? 700 : 400,
    color: tab === t ? T.primary : "#555",
    fontSize: 14,
  });

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Admin Panel</h2>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}
      <div style={{ display: "flex", borderBottom: "1px solid #ddd", marginBottom: 20 }}>
        <button style={tabStyle("pending")} onClick={() => setTab("pending")}>Pending ({pending.length})</button>
        <button style={tabStyle("users")} onClick={() => setTab("users")}>All Users</button>
        <button style={tabStyle("players")} onClick={() => setTab("players")}>Players ({players.length})</button>
      </div>

      {tab === "pending" && (
        <div>
          {/* Approval toggle */}
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 8,
            backgroundColor: requireApproval ? "#fff8e1" : "#e8f5e9",
            border: "1px solid " + (requireApproval ? "#ffe082" : "#a5d6a7") }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>Admin Approval for New Registrations</div>
                <div style={{ fontSize: 12, color: requireApproval ? "#f57c00" : "#388e3c", marginTop: 2 }}>
                  {requireApproval
                    ? "ON — new users are held in Pending until an admin approves them"
                    : "OFF — new users can log in immediately with their chosen role (Player/Captain)"}
                </div>
              </div>
              {/* Toggle switch */}
              <div
                onClick={() => !settingsLoading && toggleRequireApproval(!requireApproval)}
                title={requireApproval ? "Click to disable approval" : "Click to enable approval"}
                style={{ width: 52, height: 28, borderRadius: 14, flexShrink: 0,
                  backgroundColor: requireApproval ? T.primary : "#ccc",
                  position: "relative", cursor: settingsLoading ? "not-allowed" : "pointer",
                  transition: "background-color 0.25s", opacity: settingsLoading ? 0.6 : 1 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "white",
                  position: "absolute", top: 3,
                  left: requireApproval ? 27 : 3,
                  transition: "left 0.25s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, minWidth: 30,
                color: requireApproval ? T.primary : "#999" }}>
                {settingsLoading ? "..." : requireApproval ? "ON" : "OFF"}
              </span>
            </div>
            {settingsMsg && (
              <div style={{ marginTop: 8, fontSize: 12, padding: "6px 10px", borderRadius: 6,
                backgroundColor: settingsMsg.startsWith("Error") ? "#fdecea" : "#f0fff4",
                color: settingsMsg.startsWith("Error") ? "#c62828" : "#2e7d32",
                border: "1px solid " + (settingsMsg.startsWith("Error") ? "#ffcdd2" : "#c8e6c9") }}>
                {settingsMsg}
              </div>
            )}
          </div>

          {/* Unlimited match editing toggle */}
          <div style={{ marginBottom: 16, padding: "12px 16px", borderRadius: 8,
            backgroundColor: unlimitedMatchEdit ? "#e3f2fd" : "#fafafa",
            border: "1px solid " + (unlimitedMatchEdit ? "#90caf9" : "#e0e0e0") }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>Unlimited Match Editing</div>
                <div style={{ fontSize: 12, color: unlimitedMatchEdit ? "#1565c0" : "#888", marginTop: 2 }}>
                  {unlimitedMatchEdit
                    ? "ON — admins can edit any completed match scorecard at any time"
                    : "OFF — completed matches can only be edited within 36 hours of creation"}
                </div>
              </div>
              {/* Toggle switch */}
              <div
                onClick={() => !settingsLoading && toggleUnlimitedMatchEdit(!unlimitedMatchEdit)}
                title={unlimitedMatchEdit ? "Click to restrict to 36h window" : "Click to allow unlimited editing"}
                style={{ width: 52, height: 28, borderRadius: 14, flexShrink: 0,
                  backgroundColor: unlimitedMatchEdit ? "#1976d2" : "#ccc",
                  position: "relative", cursor: settingsLoading ? "not-allowed" : "pointer",
                  transition: "background-color 0.25s", opacity: settingsLoading ? 0.6 : 1 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "white",
                  position: "absolute", top: 3,
                  left: unlimitedMatchEdit ? 27 : 3,
                  transition: "left 0.25s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, minWidth: 30,
                color: unlimitedMatchEdit ? "#1976d2" : "#999" }}>
                {settingsLoading ? "..." : unlimitedMatchEdit ? "ON" : "OFF"}
              </span>
            </div>
          </div>

          {pending.length === 0 ? (
            <p style={{ color: "#888" }}>No pending registrations.</p>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Requested</th>
                  <th style={S.th}>Approve As</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((u) => (
                  <PendingRow key={u.email} u={u} onApprove={approve} onDeny={deny} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "users" && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Change Role</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.email}>
                <td style={S.td}>{u.name}</td>
                <td style={S.td}>{u.email}</td>
                <td style={S.td}><span style={{ fontWeight: 600 }}>{u.role}</span></td>
                <td style={S.td}>
                  <select
                    style={{ ...S.select, width: "auto" }}
                    value={u.role}
                    onChange={(e) => changeRole(u.email, e.target.value)}
                  >
                    <option value="player">player</option>
                    <option value="captain">captain</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "players" && (
        <div>
          {/* Add player form */}
          <div style={{ ...S.card, marginBottom: 20 }}>
            <h3 style={{ ...S.h3, marginBottom: 14 }}>Add New Player</h3>
            <div className="resp-grid-3" style={{ marginBottom: 12 }}>
              <div>
                <label style={S.label}>Full Name</label>
                <input
                  style={S.input}
                  placeholder="e.g. Virat Kohli"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addPlayer()}
                />
              </div>
              <div>
                <label style={S.label}>Role</label>
                <select style={S.select} value={newPlayerRole} onChange={(e) => setNewPlayerRole(e.target.value)}>
                  <option value="BATSMAN">Batsman</option>
                  <option value="BOWLER">Bowler</option>
                  <option value="ALL_ROUNDER">All-Rounder</option>
                </select>
              </div>
              <div>
                <label style={S.label}>Club / Team</label>
                <select style={S.select} value={newPlayerTeamId} onChange={(e) => setNewPlayerTeamId(e.target.value)}>
                  <option value="">-- No team --</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </div>
            <button style={S.btn} onClick={addPlayer}>+ Add Player</button>
          </div>

          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Name</th>
                <th style={S.th}>Role</th>
                <th style={S.th}>ID</th>
                <th style={S.th}>Teams</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => {
                const roleColors = { BATSMAN: T.primary, BOWLER: "#c0392b", ALL_ROUNDER: "#7d3c98" };
                const roleLabels = { BATSMAN: "Bat", BOWLER: "Bowl", ALL_ROUNDER: "All-R" };
                return (
                  <tr key={p.id}>
                    <td style={S.td}>
                      {editingPlayerId === p.id ? (
                        <input
                          style={{ ...S.input, maxWidth: 160, width: "100%" }}
                          value={editingPlayerName}
                          onChange={(e) => setEditingPlayerName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") savePlayer(p.id); if (e.key === "Escape") setEditingPlayerId(null); }}
                          autoFocus
                        />
                      ) : (
                        <strong
                          style={{ cursor: "pointer", color: T.primary, textDecoration: "underline" }}
                          onClick={async () => {
                            setStatsPlayer({ id: p.id, name: p.name });
                            setStatsData(null);
                            setStatsTab("batting");
                            try { const d = await apiFetch("/api/players/" + p.id + "/career"); setStatsData(d.career || d); }
                            catch (ex) { setErr(ex.message); }
                          }}
                        >{p.name}</strong>
                      )}
                    </td>
                    <td style={S.td}>
                      {editingPlayerId === p.id ? (
                        <select style={{ ...S.select, width: "auto", fontSize: 12, padding: "4px 8px" }} value={editingPlayerRole} onChange={(e) => setEditingPlayerRole(e.target.value)}>
                          <option value="BATSMAN">Batsman</option>
                          <option value="BOWLER">Bowler</option>
                          <option value="ALL_ROUNDER">All-Rounder</option>
                        </select>
                      ) : p.role ? (
                        <span style={{ backgroundColor: roleColors[p.role] || "#888", color: "white", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10 }}>
                          {roleLabels[p.role] || p.role}
                        </span>
                      ) : <span style={{ color: "#aaa" }}>—</span>}
                    </td>
                    <td style={{ ...S.td, color: "#888", fontFamily: "monospace", fontSize: 12 }}>{p.id}</td>
                    <td style={S.td}>{(p.teams || []).join(", ") || <span style={{ color: "#aaa" }}>—</span>}</td>
                    <td style={S.td}>
                      {editingPlayerId === p.id ? (
                        <>
                          <button style={{ ...S.btn, marginRight: 6 }} onClick={() => savePlayer(p.id)}>Save</button>
                          <button style={S.btnSecondary} onClick={() => setEditingPlayerId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button title="Edit" style={{ background: "none", border: "1px solid #ccc", borderRadius: 5, padding: "3px 6px", cursor: "pointer", color: "#555", marginRight: 3, display: "inline-flex", alignItems: "center" }} onClick={() => editPlayer(p)}><IconEdit /></button>
                          <button title="Delete" style={{ background: "none", border: "1px solid #f99", borderRadius: 5, padding: "3px 6px", cursor: "pointer", color: "#c0392b", display: "inline-flex", alignItems: "center" }} onClick={() => deletePlayer(p.id)}><IconDelete /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* Player Stats Modal */}
      {statsPlayer && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", borderRadius: 10, padding: 24, width: "94vw", maxWidth: 520, maxHeight: "85vh", overflowY: "auto", boxShadow: "0 4px 32px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: T.primary }}>{statsPlayer.name} — Career Stats</h3>
              <button style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888" }} onClick={() => { setStatsPlayer(null); setStatsData(null); }}>✕</button>
            </div>
            {!statsData ? (
              <p style={{ color: "#888" }}>Loading...</p>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px", marginBottom: 16, padding: "10px 12px", backgroundColor: "#f0faf4", borderRadius: 6 }}>
                  <div><span style={{ color: "#888", fontSize: 12 }}>Matches</span><br /><strong style={{ fontSize: 18 }}>{statsData.matchesPlayed ?? 0}</strong></div>
                  <div><span style={{ color: "#888", fontSize: 12 }}>Innings</span><br /><strong style={{ fontSize: 18 }}>{statsData.innings ?? 0}</strong></div>
                </div>
                <div style={{ display: "flex", borderBottom: "2px solid #eee", marginBottom: 16 }}>
                  {["batting", "bowling"].map(t => (
                    <button key={t} style={{ padding: "8px 20px", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: 14, color: statsTab === t ? T.primary : "#888", borderBottom: statsTab === t ? "2px solid " + T.primary : "2px solid transparent", marginBottom: -2, textTransform: "capitalize" }} onClick={() => setStatsTab(t)}>{t}</button>
                  ))}
                </div>
                {statsTab === "batting" ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Runs</span><br /><strong style={{ fontSize: 22 }}>{statsData.totRuns ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Highest Score</span><br /><strong style={{ fontSize: 22 }}>{statsData.highestScore ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Average</span><br /><strong>{statsData.avg ?? "-"}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Strike Rate</span><br /><strong>{statsData.sr ?? "-"}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Balls Faced</span><br /><strong>{statsData.totBalls ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>4s</span><br /><strong>{statsData.totFours ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>6s</span><br /><strong>{statsData.totSixes ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>50s</span><br /><strong>{statsData.fifties ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>100s</span><br /><strong>{statsData.hundreds ?? 0}</strong></div>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Wickets</span><br /><strong style={{ fontSize: 22 }}>{statsData.totWickets ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Overs Bowled</span><br /><strong style={{ fontSize: 22 }}>{Math.floor((statsData.totBowlBalls || 0) / 6)}.{(statsData.totBowlBalls || 0) % 6}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Runs Conceded</span><br /><strong>{statsData.totBowlRuns ?? 0}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Best Figures</span><br /><strong>{statsData.bestBowl && statsData.bestBowl.w > 0 ? `${statsData.bestBowl.w}/${statsData.bestBowl.r}` : "-"}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Bowling Avg</span><br /><strong>{statsData.bowlAvg ?? "-"}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>Economy</span><br /><strong>{statsData.econ ?? "-"}</strong></div>
                    <div><span style={{ color: "#888", fontSize: 12 }}>3W Hauls</span><br /><strong>{statsData.threeWicketHauls ?? 0}</strong></div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PendingRow({ u, onApprove, onDeny }) {
  const [role, setRole] = useState(u.requestedRole || "player");
  return (
    <tr>
      <td style={S.td}>{u.name}</td>
      <td style={S.td}>{u.email}</td>
      <td style={S.td}>{u.requestedAt ? new Date(u.requestedAt).toLocaleDateString() : "-"}</td>
      <td style={S.td}>
        <select style={{ ...S.select, width: "auto" }} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="player">player</option>
          <option value="captain">captain</option>
          <option value="admin">admin</option>
        </select>
        {u.requestedRole && <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>Requested: {u.requestedRole}</div>}
      </td>
      <td style={{ ...S.td, display: "flex", gap: 8 }}>
        <button style={S.btnSm} onClick={() => onApprove(u.email, role)}>Approve</button>
        <button style={S.btnDanger} onClick={() => onDeny(u.email)}>Deny</button>
      </td>
    </tr>
  );
}

// ─── HomeView ─────────────────────────────────────────────────────────────────

function HomeView({ user, onGoMatch }) {
  const [matches, setMatches] = useState([]);
  const [myTeam, setMyTeam] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [searchClub, setSearchClub] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [sortDir, setSortDir] = useState("desc"); // desc = newest first

  const isRestricted = user.role === "captain" || user.role === "player";

  const loadData = useCallback(async () => {
    try {
      const reqs = [apiFetch("/api/matches")];
      if (isRestricted) reqs.push(apiFetch("/api/teams/mine").catch(() => null));
      const [data, mine] = await Promise.all(reqs);
      setMatches(data || []);
      if (mine) setMyTeam(mine);
    } catch (ex) { setErr(ex.message); } finally { setLoading(false); }
  }, [isRestricted]);

  useEffect(() => { loadData(); }, [loadData]);

  const myTeamName = myTeam?.name || null;

  const visibleMatches = useMemo(() => {
    let list = matches;
    // Team filter for restricted roles
    if (isRestricted && !showAll && myTeamName) {
      list = list.filter(m =>
        (m.teamA?.name || "") === myTeamName ||
        (m.teamB?.name || "") === myTeamName ||
        (m.teamAName || "") === myTeamName ||
        (m.teamBName || "") === myTeamName
      );
    }
    // Search by club/team name
    if (searchClub.trim()) {
      const q = searchClub.trim().toLowerCase();
      list = list.filter(m =>
        (m.teamA?.name || "").toLowerCase().includes(q) ||
        (m.teamB?.name || "").toLowerCase().includes(q)
      );
    }
    // Search by date (match date or created date)
    if (searchDate) {
      list = list.filter(m => {
        const d = new Date(m.matchDate || m.createdAt);
        return d.toISOString().slice(0, 10) === searchDate;
      });
    }
    // Sort
    list = [...list].sort((a, b) => {
      const da = a.matchDate || a.createdAt;
      const db = b.matchDate || b.createdAt;
      return sortDir === "desc" ? db - da : da - db;
    });
    return list;
  }, [matches, isRestricted, showAll, myTeamName, searchClub, searchDate, sortDir]);

  const createMatch = async () => {
    try {
      const m = await apiFetch("/api/matches", { method: "POST" });
      onGoMatch(m.id, "SETUP");
    } catch (ex) { setErr(ex.message); }
  };

  const playAgain = async (e, m) => {
    e.stopPropagation();
    try {
      const newMatch = await apiFetch("/api/matches", { method: "POST" });
      await apiFetch("/api/matches/" + newMatch.id + "/meta", {
        method: "PUT",
        body: JSON.stringify({
          teamAName: m.teamA?.name || m.teamAName || "",
          teamBName: m.teamB?.name || m.teamBName || "",
          overs: m.overs || 12,
        }),
      });
      // Copy all players from previous match to new match
      const prevAIds = m.teamA?.playerIds || [];
      const prevBIds = m.teamB?.playerIds || [];
      await Promise.all([
        ...prevAIds.map(pid => apiFetch("/api/matches/" + newMatch.id + "/teams/A/players", { method: "POST", body: JSON.stringify({ playerId: pid }) }).catch(() => {})),
        ...prevBIds.map(pid => apiFetch("/api/matches/" + newMatch.id + "/teams/B/players", { method: "POST", body: JSON.stringify({ playerId: pid }) }).catch(() => {})),
      ]);
      // Copy captain/vc roles
      if (m.teamA?.captainId) await apiFetch("/api/matches/" + newMatch.id + "/teams/A/players/" + m.teamA.captainId + "/role", { method: "PUT", body: JSON.stringify({ role: "CAPTAIN" }) }).catch(() => {});
      if (m.teamA?.viceCaptainId) await apiFetch("/api/matches/" + newMatch.id + "/teams/A/players/" + m.teamA.viceCaptainId + "/role", { method: "PUT", body: JSON.stringify({ role: "VICE_CAPTAIN" }) }).catch(() => {});
      if (m.teamB?.captainId) await apiFetch("/api/matches/" + newMatch.id + "/teams/B/players/" + m.teamB.captainId + "/role", { method: "PUT", body: JSON.stringify({ role: "CAPTAIN" }) }).catch(() => {});
      if (m.teamB?.viceCaptainId) await apiFetch("/api/matches/" + newMatch.id + "/teams/B/players/" + m.teamB.viceCaptainId + "/role", { method: "PUT", body: JSON.stringify({ role: "VICE_CAPTAIN" }) }).catch(() => {});
      onGoMatch(newMatch.id, "SETUP");
    } catch (ex) { setErr(ex.message); }
  };

  const openMatch = (m) => {
    if (m.status === "setup") onGoMatch(m.id, "SETUP");
    else if (m.status === "live") onGoMatch(m.id, "SCORE");
    else onGoMatch(m.id, "SUMMARY");
  };

  const deleteMatch = async (e, matchId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this match? This cannot be undone.")) return;
    try {
      await apiFetch("/api/matches/" + matchId, { method: "DELETE" });
      loadData();
    } catch (ex) { setErr(ex.message); }
  };

  const canCreate = user.role === "admin" || user.role === "captain";
  const isAdmin = user.role === "admin";

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h2 style={{ ...S.h2, margin: 0 }}>Matches</h2>
        {canCreate && (
          <button style={S.btn} onClick={createMatch}>+ New Match</button>
        )}
      </div>
      {isRestricted && myTeamName && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, fontSize: 13,
          padding: "6px 12px", borderRadius: 6,
          backgroundColor: showAll ? "#e8f5e9" : "#fff3e0",
          border: "1px solid " + (showAll ? "#a5d6a7" : "#ffcc80") }}>
          <span style={{ color: showAll ? "#388e3c" : "#e65100", fontWeight: 600, flex: 1 }}>
            {showAll ? "Showing all matches" : `Filtered to: ${myTeamName} only`}
          </span>
          <button style={{ ...S.btnSm, fontSize: 12 }} onClick={() => setShowAll(v => !v)}>
            {showAll ? "My Team Only" : "Show All"}
          </button>
        </div>
      )}
      {/* Search & Sort bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, alignItems: "center", flexWrap: "nowrap", overflowX: "auto" }}>
        <input
          style={{ ...S.input, flex: "2 1 0", minWidth: 0, width: "auto", marginBottom: 0, fontSize: 12, padding: "7px 8px" }}
          placeholder="🔍 Club..." value={searchClub}
          onChange={e => setSearchClub(e.target.value)} />
        <input
          style={{ ...S.input, flex: "1 1 0", minWidth: 110, width: "auto", marginBottom: 0, fontSize: 12, padding: "7px 6px", WebkitAppearance: "auto", appearance: "auto" }}
          type="date" value={searchDate}
          onChange={e => setSearchDate(e.target.value)} />
        {(searchClub || searchDate) && (
          <button style={{ ...S.btnSm, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => { setSearchClub(""); setSearchDate(""); }}>✕</button>
        )}
        <button
          style={{ ...S.btnSm, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}
          onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}
        >{sortDir === "desc" ? "↓ New" : "↑ Old"}</button>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {/* Filter status bar */}
      {!loading && matches.length > 0 && (
        <div style={{ fontSize: 12, color: visibleMatches.length < matches.length ? "#e65100" : "#888",
          marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
          Showing {visibleMatches.length} of {matches.length} matches
          {visibleMatches.length < matches.length && (
            <button style={{ fontSize: 11, padding: "2px 8px", borderRadius: 10, border: "1px solid #e65100",
              background: "none", color: "#e65100", cursor: "pointer" }}
              onClick={() => { setSearchClub(""); setSearchDate(""); setShowAll(true); }}>
              Clear filters / Show all
            </button>
          )}
        </div>
      )}
      {loading ? (
        <p style={{ color: "#888" }}>Loading...</p>
      ) : visibleMatches.length === 0 ? (
        <div style={S.card}>
          <p style={{ color: "#888", textAlign: "center", margin: 0 }}>{matches.length === 0 ? (canCreate ? "No matches yet. Create one!" : "No matches yet.") : "No matches found. Try clearing filters."}</p>
        </div>
      ) : (
        visibleMatches.map((m) => (
          <div
            key={m.id}
            style={{ ...S.card, cursor: "pointer", transition: "box-shadow 0.2s", borderLeft: m.status === "live" ? "4px solid " + T.primary : "none" }}
            onClick={() => openMatch(m)}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.15)"}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)"}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                  {m.teamA?.name || "Team A"} vs {m.teamB?.name || "Team B"}
                </div>
                <div style={{ color: "#666", fontSize: 13 }}>
                  {m.overs} overs &nbsp;•&nbsp; {new Date(m.matchDate || m.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                </div>
                {m.innings && m.innings.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 13, color: "#444" }}>
                    {m.innings.map((inn, i) => {
                      const st = calcInning(inn.balls || [], m.penaltyExtras || false, m.wideRuns || 1);
                      const teamName = inn.batting === "A" ? m.teamA?.name : m.teamB?.name;
                      return (
                        <span key={i} style={{ marginRight: 16 }}>
                          {teamName}: <strong>{st.total}/{st.wickets}</strong> ({st.overs})
                        </span>
                      );
                    })}
                  </div>
                )}
                {m.status === "live" && (
                  <div style={{ marginTop: 8, fontSize: 12, color: T.primary, fontWeight: 600 }}>
                    🔴 LIVE — tap to open scoreboard
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <span style={S.badge(m.status)}>{m.status?.toUpperCase()}</span>
                {canCreate && m.status === "completed" && (
                  <button
                    style={{ ...S.btnSm, fontSize: 11, padding: "4px 8px", backgroundColor: "#e8f5e9", color: T.primary, border: "1px solid " + T.primary }}
                    onClick={(e) => playAgain(e, m)}
                    title="New match with same teams"
                  >🔄 Play Again</button>
                )}
                {isAdmin && (
                  <button
                    style={{ ...S.btnDanger, padding: "4px 10px", fontSize: 12 }}
                    onClick={(e) => deleteMatch(e, m.id)}
                    title="Delete match"
                  >Delete</button>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── SetupView ────────────────────────────────────────────────────────────────

function SetupView({ user, matchId, onStarted, onBack, challengeTeams }) {
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [teamAName, setTeamAName] = useState("");
  const [teamBName, setTeamBName] = useState("");
  const [overs, setOvers] = useState(match?.overs || 12);
  const [ballsPerOver, setBallsPerOver] = useState(match?.ballsPerOver || 6);
  const [wideRuns, setWideRuns] = useState(match?.wideRuns || 1);
  const [maxOversPerBowler, setMaxOversPerBowler] = useState(match?.maxOversPerBowler || 3);
  const [playersPerTeam, setPlayersPerTeam] = useState(match?.playersPerTeam || 0);
  const [penaltyExtras, setPenaltyExtras] = useState(match?.penaltyExtras || false);
  const [matchDate, setMatchDate] = useState(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return now.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
  });
  const [addingTeam, setAddingTeam] = useState(null);
  const [searchA, setSearchA] = useState("");
  const [searchB, setSearchB] = useState("");
  const [settingRole, setSettingRole] = useState({});
  const [tossModal, setTossModal] = useState(false);
  const [tossWonBy, setTossWonBy] = useState("A");
  const [tossChoice, setTossChoice] = useState("BAT");
  const [tossMode, setTossMode] = useState("manual"); // "manual" | "online"
  const [tossFlipping, setTossFlipping] = useState(false);
  const [tossFlipResult, setTossFlipResult] = useState(null); // { side: "H"|"T" }
  const [coinDeg, setCoinDeg] = useState(0);
  const [teams, setTeams] = useState([]);
  const [teamASource, setTeamASource] = useState("");
  const [teamBSource, setTeamBSource] = useState("");
  const [teamInputMode, setTeamInputMode] = useState("manual"); // "manual" | "existing"
  const [playerPickMode, setPlayerPickMode] = useState("runtime"); // "runtime" | "before" | "auction"
  const [selectedAuctionId, setSelectedAuctionId] = useState("");
  const [auctionApplying, setAuctionApplying] = useState(false);
  const [savedAuctions, setSavedAuctions] = useState(() => getAuctions());
  const [showPlayersA, setShowPlayersA] = useState(true);
  const [showPlayersB, setShowPlayersB] = useState(true);
  const [showRolesA, setShowRolesA] = useState(false);
  const [showRolesB, setShowRolesB] = useState(false);
  const autoLoadedRef = useRef(false);

  const canEdit = user.role === "admin" || user.role === "captain";
  const isOnline = useOnlineStatus();

  const applyMatchState = (m) => {
    setMatch(m);
    setTeamAName(m.teamA?.name || "");
    setTeamBName(m.teamB?.name || "");
    setOvers(m.overs || 12);
    setBallsPerOver(m.ballsPerOver || 6);
    setWideRuns(m.wideRuns || 1);
    setMaxOversPerBowler(m.maxOversPerBowler || 3);
    setPlayersPerTeam(m.playersPerTeam || 0);
    setPenaltyExtras(m.penaltyExtras || false);
    if (m.playerPickMode) setPlayerPickMode(m.playerPickMode);
    if (m.matchDate) {
      const d = new Date(m.matchDate);
      d.setSeconds(0, 0);
      setMatchDate(d.toISOString().slice(0, 16));
    }
  };

  const loadMatch = useCallback(async () => {
    try {
      const m = await apiFetch("/api/matches/" + matchId);
      applyMatchState(m);
      cacheData("match_" + matchId, m).catch(() => {});
    } catch (ex) {
      if (!navigator.onLine || ex.message === "offline") {
        const cached = await getCached("match_" + matchId).catch(() => null);
        if (cached?.data) { applyMatchState(cached.data); return; }
      }
      setErr(ex.message);
    }
  }, [matchId]); // eslint-disable-line

  // Silent poll — only refreshes match data, never resets form fields the user may be editing
  const pollMatch = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const m = await apiFetch("/api/matches/" + matchId);
      setMatch(m);
      cacheData("match_" + matchId, m).catch(() => {});
    } catch (_) {}
  }, [matchId]);

  useEffect(() => {
    loadMatch();
    // Players
    apiFetch("/api/players")
      .then(p => { setPlayers(p); cacheData("players", p).catch(() => {}); })
      .catch(async () => {
        const c = await getCached("players").catch(() => null);
        if (c?.data) setPlayers(c.data);
      });
    // Teams
    apiFetch("/api/teams")
      .then(t => { setTeams(t); cacheData("teams", t).catch(() => {}); })
      .catch(async () => {
        const c = await getCached("teams").catch(() => null);
        if (c?.data) setTeams(c.data);
      });
    const interval = setInterval(pollMatch, 5000);
    return () => clearInterval(interval);
  }, [loadMatch, pollMatch]);

  // Auto-load teams: challenge flow, captain new-match flow, or tournament match IDs
  useEffect(() => {
    if (!match?.id || !teams.length || autoLoadedRef.current) return;
    autoLoadedRef.current = true;

    // Challenge flow: captain's team → A (locked), opponent → B
    if (challengeTeams?.myTeamId) {
      setTeamASource(challengeTeams.myTeamId);
      if (challengeTeams.theirTeamId) setTeamBSource(challengeTeams.theirTeamId);
      (async () => {
        await loadTeamIntoMatch(challengeTeams.myTeamId, "A");
        if (challengeTeams.theirTeamId) await loadTeamIntoMatch(challengeTeams.theirTeamId, "B");
      })();
      return;
    }

    // Captain new-match flow (no challenge): auto-lock captain's own team as A
    if (user?.role === "captain" && !match.teamAId) {
      const captainTeam = teams.find(t => {
        if (user.playerId && t.captainId === user.playerId) return true;
        if (t.captainId && t.players) {
          const cap = t.players.find(p => p.id === t.captainId);
          if (cap && cap.name?.toLowerCase() === user?.name?.toLowerCase()) return true;
        }
        return false;
      });
      if (captainTeam) {
        setTeamASource(captainTeam.id);
        loadTeamIntoMatch(captainTeam.id, "A");
        return;
      }
    }

    // Tournament match flow: pre-assigned team IDs
    if (match.teamAId) setTeamASource(match.teamAId);
    if (match.teamBId) setTeamBSource(match.teamBId);
    const aIds = match.teamA?.playerIds || [];
    const bIds = match.teamB?.playerIds || [];
    if ((match.teamAId || match.teamBId) && aIds.length === 0 && bIds.length === 0) {
      (async () => {
        if (match.teamAId) await loadTeamIntoMatch(match.teamAId, "A");
        if (match.teamBId) await loadTeamIntoMatch(match.teamBId, "B");
      })();
    }
  }, [match?.id, teams.length]); // eslint-disable-line

  const saveMeta = async () => {
    try {
      await apiFetch("/api/matches/" + matchId + "/meta", {
        method: "PUT",
        body: JSON.stringify({ teamAName, teamBName, overs: parseInt(overs), ballsPerOver, wideRuns, maxOversPerBowler, playersPerTeam, playerPickMode, penaltyExtras, matchDate: matchDate ? new Date(matchDate).getTime() : null }),
      });
      setMsg("Saved!");
      loadMatch();
      setTimeout(() => setMsg(""), 2000);
    } catch (ex) { setErr(ex.message); }
  };

  const addPlayer = async (team, playerId) => {
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/" + team + "/players", {
        method: "POST",
        body: JSON.stringify({ playerId }),
      });
      pollMatch(); // pollMatch keeps team name fields intact
    } catch (ex) { setErr(ex.message); }
  };

  const removePlayer = async (team, playerId) => {
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/" + team + "/players/" + playerId, { method: "DELETE" });
      pollMatch(); // pollMatch keeps team name fields intact
    } catch (ex) { setErr(ex.message); }
  };

  const setPlayerRole = async (team, playerId, role) => {
    try {
      const m = await apiFetch("/api/matches/" + matchId + "/teams/" + team + "/players/" + playerId + "/role", {
        method: "PUT",
        body: JSON.stringify({ role }),
      });
      setMatch(m);
    } catch (ex) { setErr(ex.message); }
  };

  const startMatch = async () => {
    if (!isOnline) {
      // Queue the start op and apply locally so ScoreView loads from cache
      try {
        await queueOp(matchId, 'START_MATCH', { tossWonBy, tossChoice });
        const battingFirst = (tossWonBy === "A" && tossChoice === "BAT") || (tossWonBy === "B" && tossChoice === "BOWL") ? "A" : "B";
        const localMatch = {
          ...match,
          status: "live",
          tossWonBy,
          tossChoice,
          currentInning: 0,
          innings: [{ id: "offline_inn_0", batting: battingFirst, balls: [] }],
        };
        await cacheData("match_" + matchId, localMatch);
        setTossModal(false);
        onStarted(matchId);
      } catch (ex) { setErr("Offline start failed: " + ex.message); }
      return;
    }
    try {
      await apiFetch("/api/matches/" + matchId + "/start", {
        method: "POST",
        body: JSON.stringify({ tossWonBy, tossChoice }),
      });
      setTossModal(false);
      onStarted(matchId);
    } catch (ex) { setErr(ex.message); }
  };

  const copyLink = (type) => {
    const url = window.location.origin + (type === "join" ? "/#join/" + matchId : "/#live/" + matchId);
    copyToClipboard(url).then(() => { setMsg("Link copied!"); setTimeout(() => setMsg(""), 2000); });
  };

  const loadTeamIntoMatch = async (teamId, side) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    setErr("");
    try {
      // Remove existing players from this side first
      const currentIds = side === "A" ? (match?.teamA?.playerIds || []) : (match?.teamB?.playerIds || []);
      for (const pid of currentIds) {
        try { await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players/" + pid, { method: "DELETE" }); } catch (_) {}
      }
      // Set team name
      const namePatch = side === "A" ? { teamAName: team.name } : { teamBName: team.name };
      await apiFetch("/api/matches/" + matchId + "/meta", { method: "PUT", body: JSON.stringify({ ...namePatch, overs: parseInt(overs) }) });
      if (side === "A") setTeamAName(team.name); else setTeamBName(team.name);
      // Add each player
      for (const p of (team.players || [])) {
        try {
          await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players", {
            method: "POST", body: JSON.stringify({ playerId: p.id }),
          });
        } catch (_) {} // skip if already in team or in other team
      }
      // Pre-populate C/VC/WK from team's saved roles
      if (team.captainId) {
        try { await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players/" + team.captainId + "/role", { method: "PUT", body: JSON.stringify({ role: "CAPTAIN" }) }); } catch (_) {}
      }
      if (team.viceCaptainId) {
        try { await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players/" + team.viceCaptainId + "/role", { method: "PUT", body: JSON.stringify({ role: "VICE_CAPTAIN" }) }); } catch (_) {}
      }
      if (team.wicketKeeperId) {
        try { await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players/" + team.wicketKeeperId + "/role", { method: "PUT", body: JSON.stringify({ role: "WICKET_KEEPER" }) }); } catch (_) {}
      }
      await loadMatch();
      setMsg("Team \"" + team.name + "\" loaded into Team " + side + "!");
      setTimeout(() => setMsg(""), 3000);
    } catch (ex) { setErr(ex.message); }
  };

  if (!match) return <div style={S.page}><p style={{ color: "#888" }}>Loading...</p></div>;

  const teamAIds = match.teamA?.playerIds || [];
  const teamBIds = match.teamB?.playerIds || [];

  const filterPlayers = (team, search) => {
    const current = team === "A" ? teamAIds : teamBIds;
    const other = team === "A" ? teamBIds : teamAIds;
    const source = team === "A" ? teamASource : teamBSource;
    const sourceTeam = source ? teams.find(t => t.id === source) : null;
    return players
      .filter((p) => {
        if (current.includes(p.id) || other.includes(p.id)) return false;
        if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
        if (sourceTeam) return sourceTeam.players.some(sp => sp.id === p.id);
        return true;
      })
      .slice(0, 10);
  };

  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p; });

  return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <button style={S.btnSecondary} onClick={onBack}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0 }}>Match Setup</h2>
        <span style={{ fontFamily: "monospace", color: "#888", fontSize: 13 }}>#{matchId}</span>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      {canEdit && (
        <div style={S.card}>
          <h3 style={{ ...S.h3, marginBottom: 10 }}>Match Settings</h3>

          {/* Team input mode toggle */}
          {teams.length > 0 && !challengeTeams?.myTeamId && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[["manual", "✏️ Internal"], ["existing", "📋 Existing Teams"]].map(([m, lbl]) => (
                <button key={m} type="button" onClick={() => setTeamInputMode(m)}
                  style={{ flex: 1, padding: "5px 0", borderRadius: 5, border: "2px solid",
                    borderColor: teamInputMode === m ? T.primary : "#ddd",
                    backgroundColor: teamInputMode === m ? T.primary : "white",
                    color: teamInputMode === m ? "white" : "#555",
                    fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                  {lbl}
                </button>
              ))}
            </div>
          )}

          {/* Team A, Team B, Overs — compact 3-col */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 64px", gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ ...S.label, fontSize: 11 }}>Team A</label>
              {teamInputMode === "existing" && teams.length > 0 ? (
                (() => {
                  const lockA = !!(challengeTeams?.myTeamId || (user?.role === "captain" && teamASource));
                  return lockA ? (
                    <div style={{ ...S.input, backgroundColor: "#f5f5f5", color: "#555", cursor: "not-allowed", fontSize: 13, padding: "7px 10px" }}>
                      {teams.find(t => t.id === teamASource)?.name || "Your Team"} 🔒
                    </div>
                  ) : (
                    <select style={{ ...S.select, fontSize: 13 }} value={teamASource} onChange={(e) => {
                      setTeamASource(e.target.value);
                      if (e.target.value) loadTeamIntoMatch(e.target.value, "A");
                    }}>
                      <option value="">-- Select --</option>
                      {teams.filter(t => t.id !== teamBSource).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  );
                })()
              ) : (
                (() => {
                  const lockA = !!(challengeTeams?.myTeamId || (user?.role === "captain" && teamASource));
                  return <input style={{ ...S.input, fontSize: 13, ...(lockA ? { backgroundColor: "#f5f5f5", color: "#555", cursor: "not-allowed" } : {}) }}
                    value={teamAName} onChange={(e) => setTeamAName(e.target.value)} placeholder="Team Alpha" readOnly={lockA} />;
                })()
              )}
            </div>
            <div>
              <label style={{ ...S.label, fontSize: 11 }}>Team B</label>
              {teamInputMode === "existing" && teams.length > 0 ? (
                <select style={{ ...S.select, fontSize: 13 }} value={teamBSource} onChange={(e) => {
                  setTeamBSource(e.target.value);
                  if (e.target.value) loadTeamIntoMatch(e.target.value, "B");
                }}>
                  <option value="">-- Select --</option>
                  {teams.filter(t => t.id !== teamASource).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              ) : (
                <input style={{ ...S.input, fontSize: 13 }} value={teamBName} onChange={(e) => setTeamBName(e.target.value)} placeholder="Team Beta" />
              )}
            </div>
            <div>
              <label style={{ ...S.label, fontSize: 11 }}>Overs</label>
              <input style={{ ...S.input, fontSize: 13, padding: "7px 8px" }} type="number" min={1} max={50} value={overs} onChange={(e) => setOvers(e.target.value)} />
            </div>
          </div>

          {/* Date + Player pick mode on one row */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10, alignItems: "flex-end", flexWrap: "nowrap" }}>
            <div style={{ flex: "0 0 auto" }}>
              <label style={{ ...S.label, fontSize: 10 }}>Date &amp; Time</label>
              <input style={{ ...S.input, fontSize: 11, WebkitAppearance: "auto", appearance: "auto", marginBottom: 0, padding: "6px 4px", maxWidth: 124, minWidth: 0 }}
                type="datetime-local" value={matchDate} onChange={e => setMatchDate(e.target.value)} />
            </div>
            {teamInputMode === "manual" && (
              <div style={{ flex: 1 }}>
                <label style={{ ...S.label, fontSize: 10 }}>Players</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {[["runtime", "Runtime"], ["before", "Pick Now"], ["auction", "Auction"]].map(([pm, lbl]) => (
                    <button key={pm} type="button"
                      onClick={() => { setPlayerPickMode(pm); if (pm === "auction") setSavedAuctions(getAuctions()); }}
                      style={{ flex: 1, padding: "5px 0", borderRadius: 5, border: "2px solid",
                        borderColor: playerPickMode === pm ? T.primary : "#ddd",
                        backgroundColor: playerPickMode === pm ? T.primary : "white",
                        color: playerPickMode === pm ? "white" : "#555",
                        fontWeight: 600, fontSize: 10, cursor: "pointer" }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btn} onClick={saveMeta}>Save</button>
          </div>

          {/* Auction mode — select saved auction and apply to match */}
          {playerPickMode === "auction" && teamInputMode === "manual" && (
            <div style={{ marginTop: 14, padding: "12px 14px", backgroundColor: "#f0faf4", borderRadius: 8, border: "1px solid #c8e6c9" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.primary, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Select Auction</div>
              {savedAuctions.filter(a => a.status === "completed").length === 0 ? (
                <div style={{ fontSize: 13, color: "#888" }}>No completed auctions yet. Go to the Auction menu to create one.</div>
              ) : (
                <>
                  <select style={{ ...S.select, marginBottom: 10 }} value={selectedAuctionId} onChange={e => setSelectedAuctionId(e.target.value)}>
                    <option value="">-- Select Auction --</option>
                    {savedAuctions.filter(a => a.status === "completed").map(a => (
                      <option key={a.id} value={a.id}>{a.name} ({a.teamAName} vs {a.teamBName})</option>
                    ))}
                  </select>
                  {selectedAuctionId && (() => {
                    const sel = savedAuctions.find(a => a.id === selectedAuctionId);
                    if (!sel) return null;
                    return (
                      <div style={{ marginBottom: 10, fontSize: 12, color: "#555" }}>
                        <strong>{sel.teamAName}:</strong> {sel.picks.filter(p => p.team === "A").map(p => p.name).join(", ") || "—"}<br />
                        <strong>{sel.teamBName}:</strong> {sel.picks.filter(p => p.team === "B").map(p => p.name).join(", ") || "—"}
                      </div>
                    );
                  })()}
                  <button style={{ ...S.btn, width: "100%" }} disabled={!selectedAuctionId || auctionApplying}
                    onClick={async () => {
                      const sel = savedAuctions.find(a => a.id === selectedAuctionId);
                      if (!sel || !match?.id) return;
                      setAuctionApplying(true);
                      setErr("");
                      try {
                        // Set team names from auction
                        await apiFetch("/api/matches/" + matchId + "/meta", {
                          method: "PUT",
                          body: JSON.stringify({ teamAName: sel.teamAName, teamBName: sel.teamBName, overs: parseInt(overs), playerPickMode: "auction" }),
                        });
                        setTeamAName(sel.teamAName); setTeamBName(sel.teamBName);
                        // Remove existing players from both sides
                        const curA = match?.teamA?.playerIds || [], curB = match?.teamB?.playerIds || [];
                        for (const pid of curA) { try { await apiFetch("/api/matches/" + matchId + "/teams/A/players/" + pid, { method: "DELETE" }); } catch (_) {} }
                        for (const pid of curB) { try { await apiFetch("/api/matches/" + matchId + "/teams/B/players/" + pid, { method: "DELETE" }); } catch (_) {} }
                        // Add auction picks to match teams
                        for (const p of sel.picks.filter(x => x.team === "A")) {
                          try { await apiFetch("/api/matches/" + matchId + "/teams/A/players", { method: "POST", body: JSON.stringify({ playerId: p.id }) }); } catch (_) {}
                        }
                        for (const p of sel.picks.filter(x => x.team === "B")) {
                          try { await apiFetch("/api/matches/" + matchId + "/teams/B/players", { method: "POST", body: JSON.stringify({ playerId: p.id }) }); } catch (_) {}
                        }
                        // Store auction link in localStorage for scoring view
                        localStorage.setItem("cd_auction_match_" + matchId, selectedAuctionId);
                        await loadMatch();
                        setMsg("Auction teams applied! " + sel.teamAName + " vs " + sel.teamBName);
                        setTimeout(() => setMsg(""), 3000);
                      } catch (ex) { setErr(ex.message); }
                      setAuctionApplying(false);
                    }}>
                    {auctionApplying ? "Applying…" : "Apply Auction Teams to Match"}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Advanced Settings */}
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: "pointer", fontWeight: 600, color: T.primary, fontSize: 13, userSelect: "none" }}>Advanced Settings</summary>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <div>
                <label style={S.label}>Balls per Over</label>
                <input style={S.input} type="number" min={1} max={10} value={ballsPerOver} onChange={e => setBallsPerOver(Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Wide / No Ball = Runs</label>
                <input style={S.input} type="number" min={1} max={5} value={wideRuns} onChange={e => setWideRuns(Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Max Overs/Bowler <span style={{ fontSize: 11, color: "#888" }}>(0=no limit)</span></label>
                <input style={S.input} type="number" min={0} max={50} value={maxOversPerBowler} onChange={e => setMaxOversPerBowler(Number(e.target.value))} />
              </div>
              <div>
                <label style={S.label}>Players per Team <span style={{ fontSize: 11, color: "#888" }}>(0=unlimited)</span></label>
                <input style={S.input} type="number" min={0} max={30} value={playersPerTeam} onChange={e => setPlayersPerTeam(Number(e.target.value))} />
                <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                  {playersPerTeam > 0 ? `Innings ends at ${playersPerTeam - 1} wickets` : "Players added freely, no wicket limit"}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, padding: "10px 12px", backgroundColor: penaltyExtras ? "#fff8e1" : "#f9f9f9", borderRadius: 8, border: "1px solid " + (penaltyExtras ? "#f9a825" : "#e0e0e0") }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <div style={{ position: "relative", width: 40, height: 22, flexShrink: 0 }}>
                  <input type="checkbox" checked={penaltyExtras} onChange={e => setPenaltyExtras(e.target.checked)}
                    style={{ opacity: 0, width: 0, height: 0, position: "absolute" }} />
                  <div style={{ position: "absolute", inset: 0, borderRadius: 22, backgroundColor: penaltyExtras ? "#f9a825" : "#ccc", transition: "background 0.2s" }} />
                  <div style={{ position: "absolute", top: 3, left: penaltyExtras ? 21 : 3, width: 16, height: 16, borderRadius: "50%", backgroundColor: "white", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Penalty Extras</div>
                  <div style={{ fontSize: 11, color: "#888" }}>Wides &amp; no-balls count as 3 runs + valid ball (no free deliveries)</div>
                </div>
              </label>
            </div>
          </details>
        </div>
      )}

      {((teamInputMode === "existing" && (teamASource || teamBSource)) ||
        (teamInputMode === "manual" && playerPickMode === "before" && (teamAName.trim() || teamBName.trim()))) && (
      <div className="resp-grid-2" style={{ marginBottom: 16 }}>
        {["A", "B"].map((team) => {
          // In existing mode, only render a card if that team's dropdown was selected
          if (teamInputMode === "existing") {
            if (team === "A" && !teamASource) return null;
            if (team === "B" && !teamBSource) return null;
          }
          const teamName = team === "A"
            ? (teamAName || match.teamA?.name || "Team A")
            : (teamBName || match.teamB?.name || "Team B");
          const ids = team === "A" ? teamAIds : teamBIds;
          const search = team === "A" ? searchA : searchB;
          const setSearch = team === "A" ? setSearchA : setSearchB;

          const teamInfo = team === "A" ? match.teamA : match.teamB;
          const captainId = teamInfo?.captainId;
          const vcId = teamInfo?.viceCaptainId;
          const wkId = teamInfo?.wicketKeeperId;
          const otherIds = team === "A" ? teamBIds : teamAIds;

          const roleBadge = (pid) => (<>
            {pid === captainId && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: T.primary, color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>C</span>}
            {pid === vcId && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: "#2980b9", color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>VC</span>}
          </>);

          const showPlayers = team === "A" ? showPlayersA : showPlayersB;
          const setShowPlayers = team === "A" ? setShowPlayersA : setShowPlayersB;
          const showRoles = team === "A" ? showRolesA : showRolesB;
          const setShowRoles = team === "A" ? setShowRolesA : setShowRolesB;

          return (
            <div key={team} style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ ...S.h3, margin: 0 }}>{teamName} <span style={{ color: "#888", fontSize: 12 }}>({ids.length})</span></h3>
                {ids.length > 0 && (
                  <button type="button" onClick={() => setShowPlayers(v => !v)}
                    style={{ fontSize: 11, color: T.primary, background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
                    {showPlayers ? "▲ Hide" : "▼ Show"}
                  </button>
                )}
              </div>

              {/* Player list with role badges */}
              {showPlayers && ids.map((pid) => {
                const p = playerMap[pid];
                return (
                  <div key={pid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ fontWeight: 500 }}>
                      {p ? p.name : pid}
                      {roleBadge(pid)}
                    </span>
                    {canEdit && (
                      <button style={S.btnDanger} onClick={() => removePlayer(team, pid)}>✕</button>
                    )}
                  </div>
                );
              })}

              {/* Role assignment (Captain / VC only) */}
              {showPlayers && canEdit && ids.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" onClick={() => setShowRoles(v => !v)}
                    style={{ fontSize: 11, color: "#888", background: "none", border: "1px solid #ddd", borderRadius: 5, cursor: "pointer", padding: "2px 8px", marginBottom: showRoles ? 8 : 0 }}>
                    {showRoles ? "▲ Hide Roles" : "▼ Assign Roles"}
                  </button>
                  {showRoles && (
                    <div style={{ padding: "10px 12px", backgroundColor: "#f9f9f9", borderRadius: 6 }}>
                      {[
                        { label: "Captain (C)", role: "CAPTAIN", current: captainId },
                        { label: "Vice-Captain (VC)", role: "VICE_CAPTAIN", current: vcId },
                      ].map(({ label, role, current }) => (
                        <div key={role} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <label style={{ ...S.label, margin: 0, minWidth: 110, fontSize: 11 }}>{label}</label>
                          <select
                            key={`${team}_${role}_${current || "none"}`}
                            defaultValue={current || ""}
                            style={{ ...S.select, flex: 1, fontSize: 12, padding: "4px 8px" }}
                            onChange={(e) => {
                              if (e.target.value) {
                                setPlayerRole(team, e.target.value, role);
                              } else if (current) {
                                setPlayerRole(team, current, "");
                              }
                            }}
                          >
                            <option value="">-- None --</option>
                            {ids.map((pid) => (
                              <option key={pid} value={pid}>{playerMap[pid]?.name || pid}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Add players */}
              {showPlayers && canEdit && (
                <div style={{ marginTop: 12 }}>
                  {playerPickMode === "before" ? (
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #eee", borderRadius: 6, padding: "2px 0" }}>
                      {[...players]
                        .filter(p => !otherIds.includes(p.id))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(p => (
                          <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", fontSize: 13, cursor: "pointer",
                            backgroundColor: ids.includes(p.id) ? "#f0faf4" : "transparent" }}>
                            <input type="checkbox" checked={ids.includes(p.id)}
                              onChange={e => e.target.checked ? addPlayer(team, p.id) : removePlayer(team, p.id)} />
                            {p.name}
                          </label>
                        ))}
                      {players.filter(p => !otherIds.includes(p.id)).length === 0 && (
                        <div style={{ padding: "8px 10px", fontSize: 12, color: "#aaa" }}>No players available</div>
                      )}
                    </div>
                  ) : (
                    <>
                      <input
                        style={{ ...S.input, marginBottom: 8 }}
                        placeholder="Search players to add..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div style={{ maxHeight: 200, overflowY: "auto" }}>
                        {filterPlayers(team, search).map((p) => (
                          <div
                            key={p.id}
                            style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", borderRadius: 4, cursor: "pointer", backgroundColor: "#f9f9f9", marginBottom: 4 }}
                            onClick={() => addPlayer(team, p.id)}
                          >
                            <span>{p.name}</span>
                            <button style={S.btnSm} onClick={(e) => { e.stopPropagation(); addPlayer(team, p.id); }}>+ Add</button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      <div style={{ ...S.card, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button style={S.btnOutline} onClick={() => copyLink("join")}>📋 Copy Join Link</button>
        <button style={S.btnOutline} onClick={() => copyLink("live")}>📡 Copy Live Link</button>
        {canEdit && (
          <button style={S.btn} onClick={async () => {
            setErr("");
            if (teamInputMode === "manual") {
              if (!teamAName.trim() || !teamBName.trim()) {
                setErr("Please enter both team names.");
                return;
              }
              if (isOnline) {
                try {
                  await apiFetch("/api/matches/" + matchId + "/meta", {
                    method: "PUT",
                    body: JSON.stringify({ teamAName, teamBName, overs: parseInt(overs), ballsPerOver, wideRuns, maxOversPerBowler, playersPerTeam, playerPickMode, penaltyExtras, matchDate: matchDate ? new Date(matchDate).getTime() : null }),
                  });
                } catch (ex) { setErr("Failed to save team names: " + ex.message); return; }
              }
            }
            if (teamInputMode === "existing") {
              if (teamAIds.length < 6 || teamBIds.length < 6) {
                setErr("Each team needs at least 6 players to start.");
                return;
              }
            }
            setTossModal(true);
          }}>🏏 Toss &amp; Start</button>
        )}
      </div>

      {/* Toss Modal */}
      {tossModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>🪙 Toss</h3>
            {/* Mode selector */}
            <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
              {[["online", "🎲 Flip Online"], ["manual", "✍️ Already Done"]].map(([m, label]) => (
                <button key={m} onClick={() => { setTossMode(m); setTossFlipResult(null); }}
                  style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "2px solid",
                    borderColor: tossMode === m ? T.primary : "#ddd",
                    backgroundColor: tossMode === m ? T.primary : "white",
                    color: tossMode === m ? "white" : "#333", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>

            {tossMode === "online" ? (
              <div style={{ textAlign: "center" }}>
                {/* 3D Coin */}
                <div style={{ width: 110, height: 110, margin: "0 auto 12px", perspective: "400px" }}>
                  <div style={{
                    width: "100%", height: "100%", position: "relative",
                    transformStyle: "preserve-3d",
                    transition: tossFlipping ? "transform 1.2s cubic-bezier(0.4,0,0.2,1)" : "none",
                    transform: `rotateY(${coinDeg}deg)`,
                  }}>
                    {/* Heads face */}
                    <div style={{
                      position: "absolute", width: "100%", height: "100%", borderRadius: "50%",
                      backgroundColor: "#f5c842", border: "5px solid #c9960a",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      backfaceVisibility: "hidden", boxShadow: "inset 0 0 10px rgba(0,0,0,0.15)",
                    }}>
                      <div style={{ fontSize: 34, fontWeight: 900, color: "#7a5000", lineHeight: 1 }}>H</div>
                      <div style={{ fontSize: 9, color: "#7a5000", fontWeight: 700, letterSpacing: 2, marginTop: 2 }}>HEADS</div>
                    </div>
                    {/* Tails face */}
                    <div style={{
                      position: "absolute", width: "100%", height: "100%", borderRadius: "50%",
                      backgroundColor: "#b8d4f0", border: "5px solid #4a80b0",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      backfaceVisibility: "hidden", transform: "rotateY(180deg)",
                      boxShadow: "inset 0 0 10px rgba(0,0,0,0.15)",
                    }}>
                      <div style={{ fontSize: 34, fontWeight: 900, color: "#1a3a5a", lineHeight: 1 }}>T</div>
                      <div style={{ fontSize: 9, color: "#1a3a5a", fontWeight: 700, letterSpacing: 2, marginTop: 2 }}>TAILS</div>
                    </div>
                  </div>
                </div>
                {tossFlipResult && (
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: tossFlipResult.side === "H" ? "#b8860b" : "#2060a0" }}>
                    {tossFlipResult.side === "H" ? "🟡 Heads!" : "🔵 Tails!"}
                  </div>
                )}
                <button
                  style={{ ...S.btn, marginBottom: 14, opacity: tossFlipping ? 0.6 : 1 }}
                  disabled={tossFlipping}
                  onClick={() => {
                    const side = Math.random() < 0.5 ? "H" : "T";
                    const base = Math.ceil(coinDeg / 360) * 360 + 720;
                    const newDeg = base + (side === "T" ? 180 : 0);
                    setCoinDeg(newDeg);
                    setTossFlipping(true);
                    setTimeout(() => {
                      setTossFlipResult({ side });
                      setTossFlipping(false);
                    }, 1250);
                  }}
                >
                  {tossFlipping ? "Flipping..." : tossFlipResult ? "🔄 Flip Again" : "🪙 Flip Coin"}
                </button>
                {tossFlipResult && (
                  <>
                    <div style={{ marginBottom: 12, textAlign: "left" }}>
                      <label style={S.label}>Who won the toss?</label>
                      <select style={S.select} value={tossWonBy} onChange={e => setTossWonBy(e.target.value)}>
                        <option value="A">{match.teamA?.name || "Team A"}</option>
                        <option value="B">{match.teamB?.name || "Team B"}</option>
                      </select>
                    </div>
                    <div style={{ marginBottom: 8, textAlign: "left" }}>
                      <label style={S.label}>They chose to</label>
                      <div style={{ display: "flex", gap: 10 }}>
                        {["BAT", "BOWL"].map(choice => (
                          <button key={choice}
                            style={{ flex: 1, padding: "10px 0", border: "2px solid",
                              borderColor: tossChoice === choice ? T.primary : "#ddd",
                              backgroundColor: tossChoice === choice ? T.primary : "white",
                              color: tossChoice === choice ? "white" : "#333",
                              borderRadius: 6, fontWeight: 700, fontSize: 14, cursor: "pointer" }}
                            onClick={() => setTossChoice(choice)}>
                            {choice === "BAT" ? "🏏 Bat" : "⚾ Bowl"}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Record who won the toss and what they decided.</p>
                <div style={{ marginBottom: 16 }}>
                  <label style={S.label}>Toss won by</label>
                  <select style={S.select} value={tossWonBy} onChange={e => setTossWonBy(e.target.value)}>
                    <option value="A">{match.teamA?.name || "Team A"}</option>
                    <option value="B">{match.teamB?.name || "Team B"}</option>
                  </select>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={S.label}>They chose to</label>
                  <div style={{ display: "flex", gap: 10 }}>
                    {["BAT", "BOWL"].map(choice => (
                      <button key={choice}
                        style={{ flex: 1, padding: "12px 0", border: "2px solid",
                          borderColor: tossChoice === choice ? T.primary : "#ddd",
                          backgroundColor: tossChoice === choice ? T.primary : "white",
                          color: tossChoice === choice ? "white" : "#333",
                          borderRadius: 6, fontWeight: 700, fontSize: 15, cursor: "pointer" }}
                        onClick={() => setTossChoice(choice)}>
                        {choice === "BAT" ? "🏏 Bat" : "⚾ Bowl"}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div style={{ padding: "10px 14px", backgroundColor: "#f0faf4", borderRadius: 6, marginBottom: 16, fontSize: 13, color: T.primary, fontWeight: 600 }}>
              {tossWonBy === "A" ? (match.teamA?.name || "Team A") : (match.teamB?.name || "Team B")} wins toss →{" "}
              {tossChoice === "BAT"
                ? `${tossWonBy === "A" ? (match.teamA?.name || "Team A") : (match.teamB?.name || "Team B")} bats first`
                : `${tossWonBy === "A" ? (match.teamB?.name || "Team B") : (match.teamA?.name || "Team A")} bats first`}
            </div>
            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} onClick={startMatch}>▶ Start Match</button>
              <button style={S.btnSecondary} onClick={() => { setTossModal(false); setErr(""); setTossFlipResult(null); setTossMode("manual"); setCoinDeg(0); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── JoinView ─────────────────────────────────────────────────────────────────

function JoinView({ user, matchId, onBack }) {
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadMatch = useCallback(async () => {
    try { setMatch(await apiFetch("/api/matches/" + matchId + "/live")); } catch (ex) { setErr(ex.message); }
  }, [matchId]);

  useEffect(() => {
    loadMatch();
    apiFetch("/api/players").then(setPlayers).catch(() => {});
  }, [loadMatch]);

  const canEdit = user && (user.role === "admin" || user.role === "captain");

  const addPlayer = async (playerId) => {
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/B/players", {
        method: "POST",
        body: JSON.stringify({ playerId }),
      });
      loadMatch();
    } catch (ex) { setErr(ex.message); }
  };

  const removePlayer = async (playerId) => {
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/B/players/" + playerId, { method: "DELETE" });
      loadMatch();
    } catch (ex) { setErr(ex.message); }
  };

  if (!match) return <div style={S.page}><p style={{ color: "#888" }}>{err || "Loading..."}</p></div>;

  const teamBIds = match.teamB?.playerIds || [];
  const teamAIds = match.teamA?.playerIds || [];
  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p; });

  const filteredPlayers = players.filter((p) =>
    !teamBIds.includes(p.id) && (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  ).slice(0, 10);

  return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        {onBack && <button style={S.btnSecondary} onClick={onBack}>← Back</button>}
        <h2 style={{ ...S.h2, margin: 0 }}>Join Match: {match.teamA?.name || "Team A"} vs {match.teamB?.name || "Team B"}</h2>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={S.card}>
          <h3 style={S.h3}>{match.teamA?.name || "Team A"} (set)</h3>
          {teamAIds.map((pid) => (
            <div key={pid} style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
              {playerMap[pid]?.name || pid}
            </div>
          ))}
        </div>
        <div style={S.card}>
          <h3 style={S.h3}>{match.teamB?.name || "Team B"} ({teamBIds.length} players)</h3>
          {teamBIds.map((pid) => (
            <div key={pid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
              <span>{playerMap[pid]?.name || pid}</span>
              {canEdit && <button style={S.btnDanger} onClick={() => removePlayer(pid)}>✕</button>}
            </div>
          ))}
          {canEdit && (
            <div style={{ marginTop: 12 }}>
              <input
                style={{ ...S.input, marginBottom: 8 }}
                placeholder="Search players..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {filteredPlayers.map((p) => (
                <div
                  key={p.id}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", borderRadius: 4, backgroundColor: "#f9f9f9", marginBottom: 4 }}
                >
                  <span>{p.name}</span>
                  <button style={S.btnSm} onClick={() => addPlayer(p.id)}>+ Add</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── RuntimePlayerSearch ──────────────────────────────────────────────────────
// Search existing players by name, or create a new one, then add to match team.
// onSelect(playerId, playerName) is called once the player is ready.

function RuntimePlayerSearch({ players, matchId, side, excluded, onSelect, label, placeholder }) {
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = search.trim().length > 0
    ? players.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) &&
        !(excluded || []).includes(p.id)
      ).slice(0, 8)
    : [];
  const exactMatch = players.find(p => p.name.toLowerCase() === search.trim().toLowerCase());

  const addToTeam = async (playerId) => {
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players", {
        method: "POST",
        body: JSON.stringify({ playerId }),
      });
    } catch (_) {} // already in team is fine
  };

  const selectPlayer = async (player) => {
    setBusy(true);
    await addToTeam(player.id);
    onSelect(player.id, player.name);
    setSearch("");
    setBusy(false);
  };

  const createAndSelect = async () => {
    if (!search.trim() || busy) return;
    setBusy(true);
    try {
      const newPlayer = await apiFetch("/api/players", {
        method: "POST",
        body: JSON.stringify({ name: search.trim() }),
      });
      await addToTeam(newPlayer.id);
      onSelect(newPlayer.id, newPlayer.name);
      setSearch("");
    } catch (ex) { console.error("RuntimePlayerSearch:", ex.message); }
    setBusy(false);
  };

  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <div style={{ position: "relative" }}>
        <input
          style={{ ...S.input, backgroundColor: busy ? "#f5f5f5" : "white" }}
          placeholder={placeholder || "Search or type new player name..."}
          value={search}
          disabled={busy}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !exactMatch && search.trim()) createAndSelect(); }}
        />
        {search.trim().length > 0 && (
          <div style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
            backgroundColor: "white", border: "1px solid #ddd", borderRadius: 6,
            maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}>
            {filtered.map(p => (
              <div key={p.id} onClick={() => selectPlayer(p)}
                style={{ padding: "9px 12px", cursor: "pointer", fontSize: 14,
                  borderBottom: "1px solid #f0f0f0",
                  backgroundColor: "white" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0faf4"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "white"}>
                {p.name}
              </div>
            ))}
            {!exactMatch && (
              <div onClick={createAndSelect}
                style={{ padding: "9px 12px", cursor: busy ? "not-allowed" : "pointer",
                  fontSize: 14, color: T.primary, fontWeight: 600,
                  borderTop: filtered.length > 0 ? "1px solid #eee" : "none" }}>
                {busy ? "Adding…" : `+ Add "${search.trim()}" as new player`}
              </div>
            )}
            {exactMatch && filtered.length === 0 && (
              <div onClick={() => selectPlayer(exactMatch)}
                style={{ padding: "9px 12px", cursor: "pointer", fontSize: 14 }}>
                {exactMatch.name}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── SearchablePlayerSelect ───────────────────────────────────────────────────
// Searchable dropdown for a pre-known squad list. Shows all squad options when
// the field is empty; filters as you type. Also searches all backend players
// and offers "+ Add new player" if the name doesn't exist.
function SearchablePlayerSelect({ options, players, matchId, side, excluded, label, onSelect }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const excl = new Set(excluded || []);
  const filteredOptions = search.trim()
    ? (players || []).filter(p => p.name.toLowerCase().includes(search.toLowerCase()) && !excl.has(p.id)).slice(0, 8)
    : (options || []).filter(o => !excl.has(o.id));
  const exactMatch = (players || []).find(p => p.name.toLowerCase() === search.trim().toLowerCase() && !excl.has(p.id));

  const handleSelect = async (player) => {
    setBusy(true);
    try {
      await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players", {
        method: "POST", body: JSON.stringify({ playerId: player.id }),
      });
    } catch (_) {}
    onSelect(player.id);
    setSearch(""); setOpen(false); setBusy(false);
  };

  const handleCreateAndSelect = async () => {
    if (!search.trim() || busy) return;
    setBusy(true);
    try {
      const np = await apiFetch("/api/players", { method: "POST", body: JSON.stringify({ name: search.trim() }) });
      await apiFetch("/api/matches/" + matchId + "/teams/" + side + "/players", { method: "POST", body: JSON.stringify({ playerId: np.id }) });
      onSelect(np.id);
      setSearch(""); setOpen(false);
    } catch (_) {}
    setBusy(false);
  };

  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <div style={{ position: "relative" }}>
        <input
          style={{ ...S.input, backgroundColor: busy ? "#f5f5f5" : "white" }}
          placeholder="Type to search or tap to pick…"
          value={search} disabled={busy}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              if (exactMatch) handleSelect(exactMatch);
              else if (search.trim()) handleCreateAndSelect();
            }
          }}
        />
        {open && (filteredOptions.length > 0 || (!exactMatch && search.trim())) && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, backgroundColor: "white", border: "1px solid #ddd", borderRadius: 6, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
            {filteredOptions.map(p => (
              <div key={p.id} onClick={() => handleSelect(p)}
                style={{ padding: "9px 12px", cursor: "pointer", fontSize: 14, borderBottom: "1px solid #f0f0f0", backgroundColor: "white" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0faf4"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "white"}>
                {p.name}
              </div>
            ))}
            {!exactMatch && search.trim() && (
              <div onClick={handleCreateAndSelect}
                style={{ padding: "9px 12px", cursor: busy ? "not-allowed" : "pointer", fontSize: 14, color: T.primary, fontWeight: 600, borderTop: filteredOptions.length > 0 ? "1px solid #eee" : "none" }}>
                {busy ? "Adding…" : `+ Add "${search.trim()}" as new player`}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ScoreView ────────────────────────────────────────────────────────────────

function ScoreView({ user, matchId, onCompleted, onBack }) {
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [striker, setStriker] = useState("");
  const [nonStriker, setNonStriker] = useState("");
  const [bowler, setBowler] = useState("");
  // phase: SELECT_OPENERS | SCORING | WICKET | NEW_BATTER
  const [phase, setPhase] = useState("SELECT_OPENERS");
  const [wicketType, setWicketType] = useState("Bowled");
  const [wicketBatter, setWicketBatter] = useState("");
  const [pendingBall, setPendingBall] = useState(null);
  const [newBatter, setNewBatter] = useState("");
  const [newBowler, setNewBowler] = useState("");
  const [newBowlerPending, setNewBowlerPending] = useState(false);
  const [dismissedBatterId, setDismissedBatterId] = useState("");
  const RETIRED_KEY = "cd_retired_" + matchId;
  const [retiredBatters, setRetiredBatters] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("cd_retired_" + matchId) || "[]")); }
    catch { return new Set(); }
  });
  const [retireModal, setRetireModal] = useState(false);
  const [retiringBatter, setRetiringBatter] = useState("");
  const [scorecardOpen, setScorecardOpen] = useState(false);
  const [scorecardInning, setScorecardInning] = useState(0);
  const [extraType, setExtraType] = useState("Normal"); // "Normal"|"Wide"|"NoBall"|"Bye"|"LegBye"
  const [changeBowlerOpen, setChangeBowlerOpen] = useState(false);
  const [changeBowlerPick, setChangeBowlerPick] = useState("");
  const [err, setErr] = useState("");

  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [showQueueDetails, setShowQueueDetails] = useState(false);
  const [queueOps, setQueueOps] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const OFFLINE_KEY = "cd_offline_mode_" + matchId;
  const [manualOffline, setManualOffline] = useState(
    () => localStorage.getItem(OFFLINE_KEY) === "1"
  );
  // Effective offline: either network down OR user toggled offline mode
  const isOfflineMode = manualOffline || !isOnline;
  const toggleOfflineMode = () => {
    if (!isOnline) return; // can't toggle when truly offline
    setManualOffline(prev => {
      const next = !prev;
      localStorage.setItem(OFFLINE_KEY, next ? "1" : "0");
      return next;
    });
  };

  const canScore = user.role === "admin" || user.role === "captain";
  const stateRestored = useRef(false);

  const CREASE_KEY = "cd_crease_" + matchId;
  const saveCrease = useCallback((str, nStr, bwl) => {
    if (str && nStr && bwl) {
      try { localStorage.setItem(CREASE_KEY, JSON.stringify({ s: str, n: nStr, b: bwl })); } catch (_) {}
    }
  }, [CREASE_KEY]);
  const loadCrease = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(CREASE_KEY) || "{}"); } catch { return {}; }
  }, [CREASE_KEY]);

  const loadMatch = useCallback(async () => {
    try {
      const m = await apiFetch("/api/matches/" + matchId);
      setMatch(m);
      cacheData("match_" + matchId, m).catch(() => {});
      if (m.status === "completed") onCompleted(matchId);
    } catch (ex) {
      if (!navigator.onLine || ex.message === "offline") {
        const cached = await getCached("match_" + matchId).catch(() => null);
        if (cached?.data) { setMatch(cached.data); return; }
      }
      setErr(ex.message);
    }
  }, [matchId, onCompleted]);

  const reloadPlayers = useCallback(() => {
    apiFetch("/api/players")
      .then(p => { setPlayers(p); cacheData("players", p).catch(() => {}); })
      .catch(async () => {
        const c = await getCached("players").catch(() => null);
        if (c?.data) setPlayers(c.data);
      });
  }, []);

  useEffect(() => {
    loadMatch();
    reloadPlayers();
    countPendingOps(matchId).then(setPendingCount).catch(() => {});
    // Request persistent storage so browser never auto-evicts offline queue
    if (navigator.storage?.persist) navigator.storage.persist().catch(() => {});
  }, [loadMatch, reloadPlayers, matchId]);

  // Restore striker/nonStriker/bowler/phase from saved ball data (runs once after match loads)
  useEffect(() => {
    if (stateRestored.current || !match || match.status !== "live") return;
    stateRestored.current = true;

    const cidx = match.currentInning || 0;
    const cinn = match.innings?.[cidx];
    const bs = cinn?.balls || [];

    if (bs.length === 0) {
      // Pre-fill from localStorage so runtime users don't have to re-select
      const saved = loadCrease();
      if (saved.s) setStriker(saved.s);
      if (saved.n) setNonStriker(saved.n);
      if (saved.b) setBowler(saved.b);
      setPhase("SELECT_OPENERS");
      return;
    }

    const dismissedSet = new Set(bs.filter(b => b.wicket?.batter).map(b => b.wicket.batter));
    // Also exclude any batters who retired this session (not recorded as a ball)
    const storedRetired = (() => { try { return new Set(JSON.parse(localStorage.getItem("cd_retired_" + matchId) || "[]")); } catch { return new Set(); } })();
    const allOutIds = new Set([...dismissedSet, ...storedRetired]);
    const appearedSet = new Set(bs.map(b => b.batter).filter(Boolean));
    const active = [...appearedSet].filter(id => !allOutIds.has(id));

    const lb = bs[bs.length - 1];
    const bpo = match?.ballsPerOver || 6;
    const legalCnt = bs.filter(b => !b.wide && !b.noBall).length;
    const eoo = !lb.wide && !lb.noBall && legalCnt > 0 && legalCnt % bpo === 0;
    const runsOdd = !lb.wide && !lb.noBall && (lb.runs % 2 === 1);
    const doSwap = runsOdd !== eoo;
    const wasWicket = !!lb.wicket?.batter;

    let curStriker = lb.batter;
    let curNonStriker = active.find(id => id !== curStriker) || "";
    if (doSwap) { [curStriker, curNonStriker] = [curNonStriker, curStriker]; }

    // Fill gaps using localStorage-persisted crease state
    const saved = loadCrease();
    const savedValid = (id) => id && !allOutIds.has(id);
    if (!curNonStriker && savedValid(saved.n) && saved.n !== curStriker) curNonStriker = saved.n;
    if (!curStriker && savedValid(saved.s) && saved.s !== curNonStriker) curStriker = saved.s;
    const restoredBowler = lb.bowler || (savedValid(saved.b) ? saved.b : "");

    setBowler(restoredBowler);

    if (wasWicket) {
      const outId = lb.wicket.batter;
      if (curStriker === outId) curStriker = "";
      else if (curNonStriker === outId) curNonStriker = "";
      setStriker(curStriker);
      setNonStriker(curNonStriker);
      setNewBowlerPending(eoo);
      setPhase("NEW_BATTER");
    } else if (eoo) {
      setStriker(curStriker);
      setNonStriker(curNonStriker);
      setNewBowler("");
      setPhase("END_OF_OVER");
    } else if (curStriker && curNonStriker) {
      // Full state restored — go straight to scoring
      setStriker(curStriker);
      setNonStriker(curNonStriker);
      setPhase("SCORING");
    } else {
      // Partial state — pre-fill what we know and ask user to confirm
      setStriker(curStriker || curNonStriker || (savedValid(saved.s) ? saved.s : ""));
      setNonStriker(curNonStriker !== (curStriker || curNonStriker) ? curNonStriker : (savedValid(saved.n) ? saved.n : ""));
      setPhase("SELECT_OPENERS");
    }
  }, [match, loadCrease]);

  // Persist crease state to localStorage whenever all 3 are known
  useEffect(() => {
    if (striker && nonStriker && bowler) {
      saveCrease(striker, nonStriker, bowler);
    }
  }, [striker, nonStriker, bowler, saveCrease]);

  // When innings changes, default scorecard tab to current innings
  useEffect(() => {
    setScorecardInning(match?.currentInning || 0);
  }, [match?.currentInning]);

  // ── Sync queued offline balls to server ──────────────────────────────────────
  // Must be defined BEFORE the early return so hook call order is always the same
  const syncPending = useCallback(async () => {
    if (!isOnline) { setErr("Still offline — cannot sync"); return; }
    setSyncing(true);
    setSyncError("");
    setErr("");
    try {
      const ops = await getPendingOps(matchId);
      let synced = 0;
      for (const op of ops) {
        if (op.type === 'START_MATCH') {
          await apiFetch("/api/matches/" + matchId + "/start", {
            method: "POST",
            body: JSON.stringify(op.payload),
          });
        } else if (op.type === 'BALL') {
          await apiFetch("/api/matches/" + matchId + "/balls", {
            method: "POST",
            body: JSON.stringify(op.payload),
          });
        } else if (op.type === 'UNDO') {
          await apiFetch("/api/matches/" + matchId + "/balls/last", { method: "DELETE" });
        } else if (op.type === 'END_INNING') {
          await apiFetch("/api/matches/" + matchId + "/end-inning", { method: "POST" });
        } else if (op.type === 'MOTM') {
          await apiFetch("/api/matches/" + matchId + "/motm", { method: "PUT", body: JSON.stringify(op.payload) });
        }
        await removeOp(op.id);
        synced++;
        setPendingCount(ops.length - synced);
      }
      const m = await apiFetch("/api/matches/" + matchId);
      setMatch(m);
      cacheData("match_" + matchId, m).catch(() => {});
      setPendingCount(0);
      if (m.status === "completed") { onCompleted(matchId); return; }
      if (m.currentInning !== (match?.currentInning || 0)) {
        setStriker(""); setNonStriker(""); setBowler("");
        setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
        setPhase("SELECT_OPENERS");
      }
    } catch (ex) {
      // Update remaining count so user sees exactly how many are left
      const remaining = await countPendingOps(matchId).catch(() => pendingCount);
      setPendingCount(remaining);
      setSyncError(ex.message || "Unknown error");
    } finally {
      setSyncing(false);
    }
  }, [isOnline, matchId, match, onCompleted]); // eslint-disable-line

  const discardAndReload = async () => {
    if (!window.confirm(
      `Discard all ${pendingCount} unsynced change(s) and reload from server?\n\nThis cannot be undone — any offline balls/actions not yet on the server will be permanently lost.`
    )) return;
    setSyncError("");
    setSyncing(true);
    try {
      const ops = await getPendingOps(matchId);
      for (const op of ops) await removeOp(op.id);
      setPendingCount(0);
      const m = await apiFetch("/api/matches/" + matchId);
      setMatch(m);
      cacheData("match_" + matchId, m).catch(() => {});
      if (m.status === "completed") { onCompleted(matchId); return; }
      // Reset crease state to match server reality
      setStriker(""); setNonStriker(""); setBowler("");
      setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
      setPhase(m.status === "live" ? "SELECT_OPENERS" : "SELECT_OPENERS");
    } catch (ex) {
      setSyncError("Reload failed: " + ex.message);
    } finally {
      setSyncing(false);
    }
  };

  // Export pending queue as human-readable + JSON via mobile share sheet
  const exportOfflineBackup = async () => {
    const ops = await getPendingOps(matchId).catch(() => []);
    if (!ops.length) { setErr("Queue is empty — nothing to export"); return; }
    const lines = ops.map((op, i) => {
      if (op.type === 'BALL') {
        const p = op.payload;
        const kind = p.wide ? 'Wide' : p.noBall ? 'NoBall' : 'Ball';
        const runs = `${p.runs || 0}r${p.extras ? `+${p.extras}ex` : ''}`;
        const wicket = p.wicketType ? ` OUT(${p.wicketType})` : '';
        return `#${i + 1} ${kind} ${runs}${wicket}`;
      }
      if (op.type === 'UNDO') return `#${i + 1} UNDO`;
      if (op.type === 'START_MATCH') return `#${i + 1} START — toss:${op.payload?.tossWonBy} ${op.payload?.tossChoice}`;
      if (op.type === 'END_INNING') return `#${i + 1} END INNINGS`;
      if (op.type === 'MOTM') return `#${i + 1} MOTM`;
      return `#${i + 1} ${op.type}`;
    });
    const text = [
      `Cricket Dada — Offline Backup`,
      `Match: ${matchId}`,
      `Saved: ${new Date().toLocaleString()}`,
      `Total ops: ${ops.length}`,
      ``,
      `--- Ball Log ---`,
      ...lines,
      ``,
      `--- Restore JSON (keep safe, paste back to recover) ---`,
      JSON.stringify({ matchId, exportedAt: Date.now(), ops }),
    ].join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Cricket Dada Backup', text });
      } else {
        await copyToClipboard(text);
        setErr("Backup copied — paste into WhatsApp/Notes to save");
      }
    } catch (_) {}
  };

  // Import ops from a previously exported JSON backup
  const importFromBackup = async () => {
    let parsed;
    try {
      const json = importText.includes('"ops"') ? importText.slice(importText.indexOf('{"matchId"')) : importText;
      parsed = JSON.parse(json);
    } catch (_) { setErr("Invalid backup — paste the full JSON line starting with {\"matchId\""); return; }
    if (!parsed?.ops?.length) { setErr("No ops found in backup"); return; }
    if (parsed.matchId !== matchId) {
      if (!window.confirm(`Backup is for match ${parsed.matchId}, but current match is ${matchId}. Import anyway?`)) return;
    }
    for (const op of parsed.ops) {
      await queueOp(matchId, op.type, op.payload).catch(() => {});
    }
    const count = await countPendingOps(matchId);
    setPendingCount(count);
    setShowImport(false);
    setImportText("");
    setSyncError("");
    setErr(`Restored ${parsed.ops.length} ops — tap Sync Now to sync`);
  };

  // Load queue details for the viewer
  const loadQueueDetails = async () => {
    const ops = await getPendingOps(matchId).catch(() => []);
    setQueueOps(ops);
    setShowQueueDetails(true);
  };

  // Auto-sync as soon as connection is restored; clear stale error on disconnect
  useEffect(() => {
    if (isOnline) {
      // Always re-read DB count — don't trust stale closure value
      countPendingOps(matchId).then(count => {
        if (count > 0) {
          setPendingCount(count);
          syncPending();
        }
      }).catch(() => {});
    } else {
      setSyncError("");
    }
  }, [isOnline]); // eslint-disable-line

  // Re-check queue count whenever tab regains focus (catches navigating-away-and-back)
  useEffect(() => {
    const onFocus = () => {
      countPendingOps(matchId).then(count => {
        setPendingCount(count);
      }).catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [matchId]);

  if (!match) return <div style={S.page}><p style={{ color: "#888" }}>{err || "Loading..."}</p></div>;

  const currInnIdx = match.currentInning || 0;
  const currInn = match.innings?.[currInnIdx];
  const balls = currInn?.balls || [];
  const penaltyExtrasEnabled = match.penaltyExtras || false;
  const stats = calcInning(balls, penaltyExtrasEnabled);

  // Compute runs scored in the most recently completed over (for END_OF_OVER display)
  const lastOverRuns = (() => {
    if (!balls.length) return null;
    const wr = match?.wideRuns || 1;
    const overBalls = [];
    let count = 0;
    for (let i = balls.length - 1; i >= 0; i--) {
      const b = balls[i];
      overBalls.unshift(b);
      if (penaltyExtrasEnabled || (!b.wide && !b.noBall)) count++;
      if (count === (match?.ballsPerOver || 6)) break;
    }
    if (count < (match?.ballsPerOver || 6)) return null;
    return overBalls.reduce((s, b) => {
      if (penaltyExtrasEnabled && (b.wide || b.noBall)) {
        return s + (b.wide ? 3 + Math.max(0, (b.extras || 0) - wr) : 3 + (b.runs || 0));
      }
      return s + (b.runs || 0) + (b.extras || 0);
    }, 0);
  })();

  const batTeam = currInn?.batting || "A";
  const bowlTeam = batTeam === "A" ? "B" : "A";
  const batIds = batTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
  const bowlIds = bowlTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
  const batTeamName = batTeam === "A" ? match.teamA?.name : match.teamB?.name;
  const bowlTeamName = bowlTeam === "A" ? match.teamA?.name : match.teamB?.name;
  // Derive from the persisted match setting: "runtime" = search/add on the fly, "before" = dropdowns from pre-selected squad, "auction" = auction squad + runtime add
  const isRuntime = (match.playerPickMode || "runtime") !== "before" && match.playerPickMode !== "auction";
  const isAuction = match.playerPickMode === "auction";
  // Load auction data for upcoming batters display
  const auctionForMatch = (() => {
    if (!isAuction) return null;
    try { const aid = localStorage.getItem("cd_auction_match_" + matchId); return aid ? getAuctions().find(a => a.id === aid) || null : null; } catch { return null; }
  })();

  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p.name; });

  // Build roleMap for batting team (C / VC / WK badges)
  const batTeamInfo = batTeam === "A" ? match.teamA : match.teamB;
  // roleMap: playerId → array of role labels (WK can also be C/VC)
  const roleMap = {};
  const addRoleLabel = (id, label) => { if (id) { if (!roleMap[id]) roleMap[id] = []; roleMap[id].push(label); } };
  addRoleLabel(batTeamInfo?.captainId, "C");
  addRoleLabel(batTeamInfo?.viceCaptainId, "VC");
  addRoleLabel(batTeamInfo?.wicketKeeperId, "WK");

  const maxBalls = (match.overs || 20) * 6;
  const inn1Stats = match.innings?.[0] ? calcInning(match.innings[0].balls || [], penaltyExtrasEnabled) : null;
  const target = currInnIdx === 1 && inn1Stats ? inn1Stats.total + 1 : null;
  const dismissedIds = new Set([
    ...balls.filter(b => b.wicket).map(b => b.wicket?.batter).filter(Boolean),
    ...retiredBatters,
  ]);
  const availableNextBatters = batIds.filter(id => !dismissedIds.has(id) && id !== striker && id !== nonStriker);
  // Can still add a batter if playersPerTeam is 0 (unlimited) or wickets haven't hit the limit
  const playersPerTeamSetting = match.playersPerTeam || 0;
  const canAddMoreBatters = playersPerTeamSetting === 0 || dismissedIds.size < playersPerTeamSetting - 1;

  // ── helpers ──────────────────────────────────────────────────────────────────

  const ballBtnStyle = (color) => ({
    width: 52, height: 52, minWidth: 48, borderRadius: "50%", border: "none",
    backgroundColor: color, color: "white", fontSize: 16, fontWeight: 700,
    cursor: "pointer", touchAction: "manipulation",
  });

  const getBallChip = (b) => {
    if (b.wicket) return { text: "W", bg: "#c0392b" };
    if (b.wide) return { text: "Wd", bg: "#e67e22" };
    if (b.noBall) return { text: "Nb", bg: "#d35400" };
    if (b.runs === 4) return { text: "4", bg: "#2980b9" };
    if (b.runs === 6) return { text: "6", bg: "#8e44ad" };
    return { text: b.runs === 0 ? "·" : String(b.runs), bg: "#555" };
  };

  const fmtOvers = (legalBalls) => `${Math.floor(legalBalls / 6)}.${legalBalls % 6}`;
  const runRate = stats.legalBalls > 0 ? (stats.total / (stats.legalBalls / 6)).toFixed(2) : "0.00";
  const rrr = target && (maxBalls - stats.legalBalls) > 0
    ? ((target - stats.total) / ((maxBalls - stats.legalBalls) / 6)).toFixed(2)
    : null;

  // Fall of wickets
  const fow = [];
  let runningTotal = 0;
  let legalSoFar = 0;
  for (const b of balls) {
    runningTotal += (b.runs || 0) + (b.extras || 0);
    if (!b.wide && !b.noBall) legalSoFar++;
    if (b.wicket) fow.push({ score: runningTotal, overs: fmtOvers(legalSoFar), batter: b.wicket.batter });
  }

  // ── ball posting ─────────────────────────────────────────────────────────────

  const postBall = async (ballData) => {
    const curStriker = striker;
    const curNonStriker = nonStriker;

    // ── OFFLINE PATH: queue ball and apply optimistically ────────────────────
    if (isOfflineMode) {
      const payload = { ...ballData, batterId: curStriker, bowlerId: bowler };
      queueOp(matchId, 'BALL', payload).catch(() => {});
      setPendingCount(c => c + 1);

      // Add ball to local match state so scorecard updates immediately
      const offlineBall = {
        id: `offline_${Date.now()}`,
        batter: curStriker,
        bowler,
        runs: ballData.runs || 0,
        extras: ballData.extras || 0,
        wide: ballData.wide || false,
        noBall: ballData.noBall || false,
        wicket: ballData.wicketType
          ? { type: ballData.wicketType, batter: ballData.wicketBatterId || curStriker }
          : null,
      };
      let newMatchRef = match;
      setMatch(prev => {
        if (!prev) return prev;
        const m = JSON.parse(JSON.stringify(prev));
        const idx = m.currentInning || 0;
        if (!m.innings[idx]) m.innings[idx] = { balls: [], batting: m.innings[0]?.batting === "A" ? "B" : "A" };
        m.innings[idx].balls = [...m.innings[idx].balls, offlineBall];
        newMatchRef = m;
        return m;
      });

      // Compute swap/phase from local state (same logic as online path)
      const pe = match?.penaltyExtras || false;
      const prevBalls = match.innings?.[currInnIdx]?.balls || [];
      const newBalls = [...prevBalls, offlineBall];
      const newLegal = pe ? newBalls.length : newBalls.filter(b => !b.wide && !b.noBall).length;
      const isLegal = pe || (!ballData.wide && !ballData.noBall);
      // In penalty mode, swap is based on runs ABOVE the penalty (not the 3-run penalty itself)
      const effectiveRuns = (pe && ballData.wide)
        ? Math.max(0, (ballData.extras || 0) - (match?.wideRuns || 1))
        : (ballData.runs || 0);
      const endOfOver = isLegal && newLegal > 0 && newLegal % (match?.ballsPerOver || 6) === 0;
      const runsOdd = isLegal && (effectiveRuns % 2 === 1);
      const doSwap = runsOdd !== endOfOver;

      // Local inning-end detection
      const st = calcInning(newBalls, pe);
      const batTeam = match.innings?.[currInnIdx]?.batting === "A" ? match.teamA : match.teamB;
      const teamSize = batTeam?.playerIds?.length || 11;
      const maxLegal = (match.overs || 20) * (match.ballsPerOver || 6);
      const inn1Total = currInnIdx === 1 ? calcInning(match.innings?.[0]?.balls || [], pe).total : null;
      const innEnded = st.wickets >= teamSize - 1 || st.legalBalls >= maxLegal
        || (inn1Total !== null && st.total > inn1Total);

      if (ballData.wicketType) {
        if (doSwap) { setStriker(curNonStriker); setNonStriker(curStriker); }
        // Track who was dismissed so confirmNewBatter puts incoming in the right position
        // Uses post-swap state: after swap, striker/nonStriker reflect where each batter ended up
        setDismissedBatterId(ballData.wicketBatterId || curStriker);
        setNewBowlerPending(endOfOver);
        setPhase("NEW_BATTER");
      } else {
        if (doSwap) { setStriker(curNonStriker); setNonStriker(curStriker); }
        if (innEnded) {
          if (currInnIdx === 0) {
            const nextBatting = match.innings?.[0]?.batting === "A" ? "B" : "A";
            setStriker(""); setNonStriker(""); setBowler("");
            setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
            setPhase("SELECT_OPENERS");
            setMatch(prev => {
              if (!prev) return prev;
              const m = JSON.parse(JSON.stringify(prev));
              m.currentInning = 1;
              if (!m.innings[1]) m.innings.push({ id: "offline_inn_1", batting: nextBatting, balls: [] });
              cacheData("match_" + matchId, m).catch(() => {});
              return m;
            });
          } else {
            // Innings 2 ended — match complete
            setMatch(prev => {
              if (!prev) return prev;
              const m = { ...prev, status: "completed" };
              cacheData("match_" + matchId, m).catch(() => {});
              return m;
            });
            setErr("Match complete — sync to save result");
          }
        } else if (endOfOver) {
          setNewBowler("");
          setPhase("END_OF_OVER");
        }
      }
      setErr("");
      return;
    }

    // ── ONLINE PATH ──────────────────────────────────────────────────────────
    try {
      const m = await apiFetch("/api/matches/" + matchId + "/balls", {
        method: "POST",
        body: JSON.stringify({ ...ballData, batterId: curStriker, bowlerId: bowler }),
      });
      setErr("");

      // Innings changed? Reset for new innings
      if (m.currentInning !== currInnIdx && m.status !== "completed") {
        setStriker(""); setNonStriker(""); setBowler("");
        setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
        setPhase("SELECT_OPENERS");
        setMatch(m);
        return;
      }
      if (m.status === "completed") { setMatch(m); onCompleted(matchId); return; }

      // Swap logic: (runsOdd XOR endOfOver)
      const pe = m?.penaltyExtras || false;
      const newBalls = m.innings?.[m.currentInning]?.balls || [];
      const newLegal = pe ? newBalls.length : newBalls.filter(b => !b.wide && !b.noBall).length;
      const isLegal = pe || (!ballData.wide && !ballData.noBall);
      // In penalty mode, swap is based on runs ABOVE the penalty (not the 3-run penalty itself)
      const effectiveRuns = (pe && ballData.wide)
        ? Math.max(0, (ballData.extras || 0) - (m?.wideRuns || 1))
        : (ballData.runs || 0);
      const endOfOver = isLegal && newLegal > 0 && newLegal % (m?.ballsPerOver || 6) === 0;
      const runsOdd = isLegal && (effectiveRuns % 2 === 1);
      const doSwap = runsOdd !== endOfOver;

      if (ballData.wicketType) {
        // Apply end-of-over swap first (before wicket logic), then handle dismissal
        if (doSwap) { setStriker(curNonStriker); setNonStriker(curStriker); }
        setMatch(m);
        // Track dismissed batter so confirmNewBatter places incoming correctly
        setDismissedBatterId(ballData.wicketBatterId || curStriker);
        if (endOfOver) {
          // Last ball of over was a wicket: need new batter first, then new bowler
          setNewBowlerPending(true);
        } else {
          setNewBowlerPending(false);
        }
        setPhase("NEW_BATTER");
      } else {
        if (doSwap) { setStriker(curNonStriker); setNonStriker(curStriker); }
        setMatch(m);
        if (endOfOver) {
          // Check if this was actually the last over (innings should be done)
          const currInnBalls = m.innings?.[currInnIdx]?.balls || [];
          const currInnLegal = currInnBalls.filter(b => !b.wide && !b.noBall).length;
          if (currInnLegal >= (m.overs || 20) * 6) {
            // All overs exhausted but backend didn't transition — reload to sync
            await loadMatch();
            setStriker(""); setNonStriker(""); setBowler("");
            setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
            setPhase("SELECT_OPENERS");
          } else {
            setNewBowler("");
            setPhase("END_OF_OVER");
          }
        }
      }
    } catch (ex) { setErr(ex.message); }
  };

  const recordBall = (runs) => {
    if (!striker || !nonStriker || !bowler) { setErr("Select striker, non-striker and bowler first"); return; }
    const penalty = match?.wideRuns || 1;
    let ballData;
    if (extraType === "Wide") {
      ballData = { runs: 0, extras: penalty + runs, wide: true, noBall: false };
    } else if (extraType === "NoBall") {
      ballData = { runs, extras: penalty, wide: false, noBall: true };
    } else if (extraType === "Bye" || extraType === "LegBye") {
      ballData = { runs: 0, extras: runs, wide: false, noBall: false };
    } else {
      ballData = { runs, extras: 0, wide: false, noBall: false };
    }
    setExtraType("Normal");
    postBall(ballData);
  };

  const openWicket = () => {
    if (!striker || !nonStriker || !bowler) { setErr("Select striker, non-striker and bowler first"); return; }
    setPendingBall({ runs: 0, extras: extraType === "NoBall" ? 1 : 0, wide: false, noBall: extraType === "NoBall" });
    setWicketBatter(striker);
    setWicketType("Bowled");
    setExtraType("Normal");
    setPhase("WICKET");
  };

  const openRetireModal = () => {
    if (!striker || !nonStriker) { setErr("Both batters must be at the crease to retire one"); return; }
    setRetiringBatter(striker);
    setRetireModal(true);
    setErr("");
  };

  const confirmRetire = () => {
    if (!retiringBatter) return;
    // Track the retired batter so they can't bat again and are excluded from available list
    const updated = new Set([...retiredBatters, retiringBatter]);
    setRetiredBatters(updated);
    try { localStorage.setItem(RETIRED_KEY, JSON.stringify([...updated])); } catch (_) {}
    // Tell confirmNewBatter which end the new batter should go to
    setDismissedBatterId(retiringBatter);
    setRetireModal(false);
    setPhase("NEW_BATTER");
    // Note: NO postBall call — retirement does not consume a delivery
  };

  const confirmWicket = () => {
    postBall({ runs: pendingBall.runs, extras: pendingBall.extras || 0, wide: pendingBall.wide || false, noBall: pendingBall.noBall || false, wicketType, wicketBatterId: wicketBatter });
    setPendingBall(null);
  };

  const confirmNewBatter = () => {
    if (!newBatter) { setErr("Select the incoming batsman"); return; }
    // Place incoming batter in the position of the dismissed batter (striker or non-striker)
    if (striker === dismissedBatterId) {
      setStriker(newBatter);
    } else {
      setNonStriker(newBatter);
    }
    setDismissedBatterId("");
    setNewBatter("");
    setErr("");
    if (newBowlerPending) {
      setNewBowlerPending(false);
      setNewBowler("");
      setPhase("END_OF_OVER");
    } else {
      setPhase("SCORING");
    }
  };

  const confirmEndOfOver = () => {
    if (!newBowler) { setErr("Select the bowler for the next over"); return; }
    if (newBowler === bowler) { setErr("Same bowler cannot bowl consecutive overs — pick a different bowler"); return; }
    setBowler(newBowler);
    setNewBowler("");
    setErr("");
    setPhase("SCORING");
  };

  const undoBall = async () => {
    if (isOfflineMode) {
      // Try to pop the last queued offline ball first
      const popped = await popLastOp(matchId).catch(() => null);
      if (popped && popped.type === 'BALL') {
        setPendingCount(c => Math.max(0, c - 1));
      } else {
        // All balls already synced — queue an UNDO to replay on server
        if (popped) await queueOp(matchId, 'UNDO', {}).catch(() => {}); // re-queue if it was something else
        else await queueOp(matchId, 'UNDO', {}).catch(() => {});
        setPendingCount(c => c + 1);
      }
      // Remove last ball from local match state
      setMatch(prev => {
        if (!prev) return prev;
        const m = JSON.parse(JSON.stringify(prev));
        const idx = m.currentInning || 0;
        if (m.innings[idx]?.balls?.length > 0) {
          m.innings[idx].balls = m.innings[idx].balls.slice(0, -1);
        }
        return m;
      });
      return;
    }
    try {
      const m = await apiFetch("/api/matches/" + matchId + "/balls/last", { method: "DELETE" });
      setMatch(m);
    } catch (ex) { setErr(ex.message); }
  };

  const endInning = async () => {
    if (!window.confirm("End current innings now? This cannot be undone.")) return;
    if (isOfflineMode) {
      await queueOp(matchId, 'END_INNING', {}).catch(() => {});
      setPendingCount(c => c + 1);
      const currIdx = match.currentInning || 0;
      if (currIdx === 0) {
        // Ending innings 1 → start innings 2
        const nextBatting = (match.innings?.[0]?.batting === "A") ? "B" : "A";
        setMatch(prev => {
          if (!prev) return prev;
          const m = JSON.parse(JSON.stringify(prev));
          m.currentInning = 1;
          if (!m.innings[1]) m.innings.push({ id: "offline_inn_1", batting: nextBatting, balls: [] });
          cacheData("match_" + matchId, m).catch(() => {});
          return m;
        });
        setStriker(""); setNonStriker(""); setBowler("");
        setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
        setPhase("SELECT_OPENERS");
      } else {
        // Ending innings 2 → match complete
        setMatch(prev => {
          if (!prev) return prev;
          const m = { ...prev, status: "completed" };
          cacheData("match_" + matchId, m).catch(() => {});
          return m;
        });
        setErr("Match complete — sync to save result");
      }
      return;
    }
    try {
      const m = await apiFetch("/api/matches/" + matchId + "/end-inning", { method: "POST" });
      setStriker(""); setNonStriker(""); setBowler("");
      setRetiredBatters(new Set()); try { localStorage.removeItem(RETIRED_KEY); } catch (_) {}
      setPhase("SELECT_OPENERS");
      setMatch(m);
      if (m.status === "completed") onCompleted(matchId);
    } catch (ex) { setErr(ex.message); }
  };

  // Current bowler stats
  const cbStats = bowler ? stats.bowlers[bowler] : null;

  // Recent balls — split by overs for display
  const recentBalls = balls.slice(-12);

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <div style={S.page}>
      {/* Offline mode toggle bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: isOfflineMode ? (manualOffline ? "#fff8e1" : "#fdecea") : "#f0faf4", border: `1px solid ${isOfflineMode ? (manualOffline ? "#f9a825" : "#e57373") : "#a5d6a7"}`, borderRadius: 8, padding: "8px 14px", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Toggle switch */}
          <div
            onClick={toggleOfflineMode}
            title={!isOnline ? "No network — offline mode forced" : manualOffline ? "Click to go online" : "Click to score offline"}
            style={{ width: 46, height: 26, borderRadius: 13, background: isOfflineMode ? (manualOffline ? "#f9a825" : "#e53935") : "#43a047", cursor: isOnline ? "pointer" : "not-allowed", position: "relative", flexShrink: 0, transition: "background 0.2s" }}
          >
            <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: isOfflineMode ? 23 : 3, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: isOfflineMode ? (manualOffline ? "#e65100" : "#b71c1c") : "#2e7d32" }}>
              {!isOnline ? "No Connection — Offline Mode" : manualOffline ? "Offline Mode (manual)" : "Online — Live Scoring"}
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>
              {!isOnline ? "Will auto-sync when reconnected" : manualOffline ? "Balls queued locally — toggle to go online & sync" : "Each ball posted to server instantly"}
            </div>
          </div>
        </div>
        {/* Sync controls */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {syncError && (
            <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 6, padding: "8px 12px", maxWidth: 300 }}>
              <div style={{ fontSize: 12, color: "#b71c1c", fontWeight: 700, marginBottom: 2 }}>
                Sync failed — {pendingCount} op{pendingCount !== 1 ? "s" : ""} pending
              </div>
              <div style={{ fontSize: 11, color: "#c62828", marginBottom: 6, wordBreak: "break-word" }}>{syncError}</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={syncPending} disabled={syncing || !isOnline}
                  style={{ fontSize: 12, padding: "4px 10px", borderRadius: 4, border: "none", background: "#e53935", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                  {syncing ? "Retrying…" : "Retry"}
                </button>
                {isOnline && (
                  <button onClick={discardAndReload} disabled={syncing}
                    style={{ fontSize: 12, padding: "4px 10px", borderRadius: 4, border: "1px solid #e57373", background: "#fff", color: "#c62828", cursor: "pointer" }}>
                    Discard & Reload
                  </button>
                )}
              </div>
            </div>
          )}
          {!syncError && pendingCount > 0 && isOnline && (
            <button onClick={syncPending} disabled={syncing}
              style={{ background: "#e67e22", color: "#fff", border: "none", borderRadius: 4, padding: "5px 14px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
              {syncing ? "Syncing…" : `Sync Now (${pendingCount})`}
            </button>
          )}
          {/* Always-visible offline tools row */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button onClick={loadQueueDetails}
              style={{ fontSize: 11, padding: "3px 9px", borderRadius: 4, border: "1px solid #bbb", background: "#f5f5f5", color: "#555", cursor: "pointer" }}>
              Queue ({pendingCount})
            </button>
            <button onClick={exportOfflineBackup}
              style={{ fontSize: 11, padding: "3px 9px", borderRadius: 4, border: "1px solid #1565c0", background: "#e3f2fd", color: "#1565c0", cursor: "pointer", fontWeight: 600 }}>
              Export
            </button>
            <button onClick={() => setShowImport(true)}
              style={{ fontSize: 11, padding: "3px 9px", borderRadius: 4, border: "1px solid #7b1fa2", background: "#f3e5f5", color: "#7b1fa2", cursor: "pointer", fontWeight: 600 }}>
              Restore
            </button>
          </div>
        </div>
      </div>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <button style={S.btnSecondary} onClick={onBack}>← Back</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ ...S.h2, margin: 0, fontSize: 18 }}>{match.teamA?.name} vs {match.teamB?.name}</h2>
          <div style={{ fontSize: 12, color: "#888" }}>
            {match.tossWonBy === "A" ? match.teamA?.name : match.teamB?.name} won toss &amp; chose to {match.tossChoice?.toLowerCase()}
          </div>
        </div>
        <button
          style={{ ...S.btnSecondary, fontSize: 13 }}
          onClick={() => {
            const url = window.location.origin + window.location.pathname + "#live/" + matchId;
            copyToClipboard(url).then(() => setErr("Live link copied to clipboard!")).catch(() => setErr("Share URL: " + url));
          }}
        >
          Share Live
        </button>
      </div>
      {err && <div style={{ ...S.error, marginBottom: 10, display: "flex", justifyContent: "space-between" }}>{err}<span style={{ cursor: "pointer" }} onClick={() => setErr("")}>✕</span></div>}

      {/* ── SELECT OPENERS PHASE ── */}
      {phase === "SELECT_OPENERS" && canScore && (
        <div style={S.card}>
          <h3 style={{ ...S.h3, marginBottom: 4 }}>
            {currInnIdx === 0 ? "🏏 Opening Batsmen" : "🏏 Innings 2 — Select Batsmen"}
          </h3>
          {currInnIdx === 1 && target && (
            <div style={{ backgroundColor: "#c0392b", borderRadius: 6, padding: "10px 14px", marginBottom: 12, color: "white", textAlign: "center" }}>
              <span style={{ fontSize: 13, opacity: 0.9 }}>{batTeamName} need </span>
              <span style={{ fontSize: 20, fontWeight: 800 }}>{target}</span>
              <span style={{ fontSize: 13, opacity: 0.9 }}> to win &nbsp;·&nbsp; {maxBalls} balls</span>
            </div>
          )}
          <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
            <strong>{batTeamName}</strong> bats. <strong>{bowlTeamName}</strong> bowls.
          </p>
          {/* Row 1: Striker + Non-Striker — always 2 columns on all screen sizes */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
            <div>
              {striker
                ? <div style={{ ...S.input, backgroundColor: "#f0faf4", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>★ <strong>{playerMap[striker] || striker}</strong></span>
                    <button onClick={() => setStriker("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                  </div>
                : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={batTeam}
                      excluded={[nonStriker].filter(Boolean)}
                      label={<>★ Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                      placeholder="Striker..."
                      onSelect={(id) => { setStriker(id); reloadPlayers(); }}
                    />
                  : <SearchablePlayerSelect
                    options={batIds.filter(id => id !== nonStriker).map(id => ({ id, name: playerMap[id] || id }))}
                    players={players} matchId={matchId} side={batTeam}
                    excluded={[nonStriker].filter(Boolean)}
                    label={<>★ Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                    onSelect={(id) => { setStriker(id); reloadPlayers(); }}
                  />
              }
            </div>
            <div>
              {nonStriker
                ? <div style={{ ...S.input, backgroundColor: "#f0faf4", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>† <strong>{playerMap[nonStriker] || nonStriker}</strong></span>
                    <button onClick={() => setNonStriker("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                  </div>
                : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={batTeam}
                      excluded={[striker].filter(Boolean)}
                      label={<>† Non-Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                      placeholder="Non-Striker..."
                      onSelect={(id) => { setNonStriker(id); reloadPlayers(); }}
                    />
                  : <SearchablePlayerSelect
                    options={batIds.filter(id => id !== striker).map(id => ({ id, name: playerMap[id] || id }))}
                    players={players} matchId={matchId} side={batTeam}
                    excluded={[striker].filter(Boolean)}
                    label={<>† Non-Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                    onSelect={(id) => { setNonStriker(id); reloadPlayers(); }}
                  />
              }
            </div>
          </div>

          {/* Row 2: Bowler + Start Scoring */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 4 }}>
            <div style={{ flex: 1 }}>
              {bowler
                ? <div style={{ ...S.input, backgroundColor: "#fff0f0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>⚾ <strong>{playerMap[bowler] || bowler}</strong> <span style={{ fontSize: 10, color: "#c0392b" }}>({bowlTeamName})</span></span>
                    <button onClick={() => setBowler("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                  </div>
                : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={bowlTeam}
                      excluded={[]}
                      label={<>⚾ Bowler <span style={{ color: "#c0392b", fontSize: 11 }}>({bowlTeamName})</span></>}
                      placeholder="Bowler..."
                      onSelect={(id) => { setBowler(id); reloadPlayers(); }}
                    />
                  : <SearchablePlayerSelect
                    options={bowlIds.map(id => ({ id, name: playerMap[id] || id }))}
                    players={players} matchId={matchId} side={bowlTeam}
                    excluded={[]}
                    label={<>⚾ Bowler <span style={{ color: "#c0392b", fontSize: 11 }}>({bowlTeamName})</span></>}
                    onSelect={(id) => { setBowler(id); reloadPlayers(); }}
                  />
              }
            </div>
            <button style={{ ...S.btn, whiteSpace: "nowrap", alignSelf: "flex-end", marginBottom: 1 }} onClick={() => {
              if (!striker || !nonStriker || !bowler) { setErr("Please select striker, non-striker and bowler"); return; }
              if (striker === nonStriker) { setErr("Striker and non-striker must be different players"); return; }
              setErr(""); setPhase("SCORING");
            }}>▶ Start Scoring</button>
          </div>
        </div>
      )}

      {/* ── TARGET BANNER (2nd innings, before first ball) ── */}
      {target && balls.length === 0 && (
        <div style={{ backgroundColor: "#c0392b", borderRadius: 8, padding: "14px 16px", marginBottom: 14, color: "white", textAlign: "center" }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{batTeamName} needs</div>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>Target: {target}</div>
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
            Need {target} to win &nbsp;·&nbsp; {maxBalls} balls available
          </div>
        </div>
      )}

      {/* ── LIVE SCOREBOARD (always visible once match is live) ── */}
      {balls.length > 0 && (
          <div style={{ backgroundColor: T.primary, borderRadius: 8, padding: "18px 16px", marginBottom: 14, color: "white" }}>
            {/* Score header */}
            <div style={{ textAlign: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 2 }}>{batTeamName} batting — Inning {currInnIdx + 1}</div>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 10 }}>
                <div style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>
                  {stats.total}<span style={{ fontSize: 22, opacity: 0.8 }}>/{stats.wickets}</span>
                </div>
                <div style={{ fontSize: 13, opacity: 0.85 }}>
                  {stats.overs}/{match.overs} ov &nbsp;·&nbsp; RR {runRate}
                </div>
              </div>
              {target && (
                <div style={{ marginTop: 6, padding: "4px 12px", backgroundColor: "rgba(0,0,0,0.25)", borderRadius: 20, display: "inline-block", fontSize: 12 }}>
                  Target {target} &nbsp;|&nbsp; Need {Math.max(0, target - stats.total)} off {maxBalls - stats.legalBalls} balls &nbsp;|&nbsp; RRR {rrr || "—"}
                </div>
              )}
              {penaltyExtrasEnabled && (
                <div style={{ marginTop: 6, padding: "3px 10px", backgroundColor: "#f9a825", borderRadius: 20, display: "inline-block", fontSize: 11, color: "#333", fontWeight: 700 }}>
                  ⚡ Penalty Extras: ON
                </div>
              )}
            </div>

            {/* Batsmen at crease */}
            <div style={{ backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Batting</div>
              {[{ id: striker, label: "★" }, { id: nonStriker, label: "" }].filter(x => x.id).map(({ id, label }) => {
                const s = stats.batters[id] || { runs: 0, balls: 0, fours: 0, sixes: 0 };
                const sr = s.balls > 0 ? (s.runs / s.balls * 100).toFixed(1) : "—";
                return (
                  <div key={id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 14 }}>
                    <span style={{ fontWeight: 700 }}>
                      {playerMap[id] || id}
                      {roleMap[id]?.map(r => <span key={r} style={{ fontSize: 10, fontWeight: 700, backgroundColor: "rgba(255,255,255,0.25)", padding: "1px 5px", borderRadius: 8, marginLeft: 4 }}>{r}</span>)}
                      {" "}{label}
                    </span>
                    <span style={{ opacity: 0.9 }}>
                      <strong>{s.runs}</strong><span style={{ opacity: 0.7 }}>({s.balls})</span>
                      &nbsp;&nbsp;4s:{s.fours} &nbsp;6s:{s.sixes} &nbsp;SR:{sr}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Upcoming batters (auction or "before" mode with pre-selected squad) */}
            {availableNextBatters.length > 0 && (isAuction || match.playerPickMode === "before") && (
              <div style={{ backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "8px 12px", marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Next to Bat</div>
                <div style={{ fontSize: 13, opacity: 0.9 }}>
                  {availableNextBatters.map(id => playerMap[id] || id).join(", ")}
                </div>
              </div>
            )}

            {/* Current bowler */}
            {bowler && (
              <div style={{ backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Bowling</div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                  <span style={{ fontWeight: 700 }}>{playerMap[bowler] || bowler}</span>
                  {cbStats
                    ? <span style={{ opacity: 0.9 }}>{fmtOvers(cbStats.balls)} ov &nbsp; {cbStats.runs} R &nbsp; {cbStats.wickets} W &nbsp; Econ {cbStats.balls > 0 ? (cbStats.runs / (cbStats.balls / 6)).toFixed(2) : "—"}</span>
                    : <span style={{ opacity: 0.6 }}>0.0 ov</span>}
                </div>
              </div>
            )}

            {/* Recent balls */}
            {recentBalls.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, opacity: 0.6, marginRight: 4, textTransform: "uppercase" }}>Recent</span>
                {recentBalls.map((b, i) => {
                  const chip = getBallChip(b);
                  return (
                    <span key={i} style={{
                      backgroundColor: chip.bg, color: "white", borderRadius: 4,
                      padding: "2px 7px", fontSize: 12, fontWeight: 700, minWidth: 22, textAlign: "center",
                    }}>{chip.text}</span>
                  );
                })}
              </div>
            )}
          </div>
      )}

      {/* ── SCORING CONTROLS (only in SCORING phase) ── */}
      {phase === "SCORING" && canScore && (
        <div style={S.card}>
          {/* Striker / Non-Striker / Bowler — show confirmations if already set, else search or dropdown based on mode */}
          {balls.length === 0 && (
            <div className="resp-grid-2" style={{ marginBottom: 14 }}>
              <div>
                {striker
                  ? <div style={{ ...S.input, backgroundColor: "#f0faf4", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>★ <strong>{playerMap[striker] || striker}</strong></span>
                      <button onClick={() => setStriker("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                    </div>
                  : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={batTeam}
                      excluded={[nonStriker].filter(Boolean)}
                      label={<>★ Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                      placeholder="Striker..."
                      onSelect={(id) => { setStriker(id); reloadPlayers(); }}
                    />
                  : <><label style={{ ...S.label, fontSize: 11 }}>★ Striker <span style={{ color: T.primary, fontWeight: 400 }}>({batTeamName})</span></label>
                    <select style={{ ...S.select, fontSize: 13 }} value={striker} onChange={e => setStriker(e.target.value)}>
                      <option value="">-- Select --</option>
                      {batIds.filter(id => !dismissedIds.has(id) && id !== nonStriker).map(id => <option key={id} value={id}>{playerMap[id] || id}</option>)}
                    </select></>
                }
              </div>
              <div>
                {nonStriker
                  ? <div style={{ ...S.input, backgroundColor: "#f0faf4", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>† <strong>{playerMap[nonStriker] || nonStriker}</strong></span>
                      <button onClick={() => setNonStriker("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                    </div>
                  : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={batTeam}
                      excluded={[striker].filter(Boolean)}
                      label={<>† Non-Striker <span style={{ color: T.primary, fontSize: 11 }}>({batTeamName})</span></>}
                      placeholder="Non-Striker..."
                      onSelect={(id) => { setNonStriker(id); reloadPlayers(); }}
                    />
                  : <><label style={{ ...S.label, fontSize: 11 }}>† Non-Striker <span style={{ color: T.primary, fontWeight: 400 }}>({batTeamName})</span></label>
                    <select style={{ ...S.select, fontSize: 13 }} value={nonStriker} onChange={e => setNonStriker(e.target.value)}>
                      <option value="">-- Select --</option>
                      {batIds.filter(id => !dismissedIds.has(id) && id !== striker).map(id => <option key={id} value={id}>{playerMap[id] || id}</option>)}
                    </select></>
                }
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                {bowler
                  ? <div style={{ ...S.input, backgroundColor: "#fff0f0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>⚾ <strong>{playerMap[bowler] || bowler}</strong> <span style={{ fontSize: 10, color: "#c0392b" }}>({bowlTeamName})</span></span>
                      <button onClick={() => setBowler("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                    </div>
                  : isRuntime
                  ? <RuntimePlayerSearch
                      players={players} matchId={matchId} side={bowlTeam}
                      excluded={[]}
                      label={<>⚾ Bowler <span style={{ color: "#c0392b", fontSize: 11 }}>({bowlTeamName})</span></>}
                      placeholder="Bowler..."
                      onSelect={(id) => { setBowler(id); reloadPlayers(); }}
                    />
                  : <><label style={{ ...S.label, fontSize: 11 }}>⚾ Bowler <span style={{ color: "#c0392b", fontWeight: 400 }}>({bowlTeamName})</span></label>
                    <select style={{ ...S.select, fontSize: 13 }} value={bowler} onChange={e => setBowler(e.target.value)}>
                      <option value="">-- Select --</option>
                      {bowlIds.map(id => <option key={id} value={id}>{playerMap[id] || id}</option>)}
                    </select></>
                }
              </div>
            </div>
          )}

          {/* Extra type selector */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Delivery Type</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { key: "Normal", label: "Normal", color: "#27ae60" },
                { key: "Wide", label: "Wide", color: "#e67e22" },
                { key: "NoBall", label: "No Ball", color: "#d35400" },
                { key: "Bye", label: "Bye", color: "#7f8c8d" },
                { key: "LegBye", label: "Leg Bye", color: "#7f8c8d" },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => setExtraType(key)}
                  style={{
                    padding: "6px 14px", borderRadius: 20, border: "2px solid",
                    borderColor: extraType === key ? color : "#ddd",
                    backgroundColor: extraType === key ? color : "white",
                    color: extraType === key ? "white" : "#555",
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >{label}</button>
              ))}
            </div>
          </div>

          {/* Run buttons */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Runs {extraType === "Wide" ? `(+${match?.wideRuns||1} penalty added)` : extraType === "NoBall" ? `(batter runs, +${match?.wideRuns||1} penalty added)` : extraType !== "Normal" ? "(extras)" : ""}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              {[0, 1, 2, 3, 4, 6].map(r => {
                const btnLabel = extraType === "Wide"   ? (r === 0 ? "Wd"  : `Wd+${r}`)
                               : extraType === "NoBall" ? (r === 0 ? "Nb"  : `Nb+${r}`)
                               : extraType === "Bye"    ? (r === 0 ? "B"   : `B+${r}`)
                               : extraType === "LegBye" ? (r === 0 ? "LB"  : `LB+${r}`)
                               : r;
                return (
                  <button key={r} style={ballBtnStyle(r === 4 ? "#2980b9" : r === 6 ? "#8e44ad" : extraType === "Normal" ? "#27ae60" : "#f39c12")} onClick={() => recordBall(r)}>{btnLabel}</button>
                );
              })}
            </div>
          </div>

          {/* Action buttons row */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8, justifyContent: "center" }}>
            <button style={ballBtnStyle("#c0392b")} onClick={openWicket} title="Wicket">W</button>
            <button
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #ddd", backgroundColor: "#f8f8f8", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
              onClick={() => { const t = striker; setStriker(nonStriker); setNonStriker(t); }}
              title="Swap striker and non-striker"
            >⇄ Swap</button>
            <button
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e67e22", backgroundColor: "#fff3e0", color: "#e67e22", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
              onClick={openRetireModal}
              title="Retire a batsman"
            >Retire Bat</button>
            <button
              style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #7f8c8d", backgroundColor: "#f5f5f5", color: "#555", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
              onClick={() => { setChangeBowlerPick(""); setChangeBowlerOpen(true); }}
              title="Change bowler mid-over"
            >↻ Bowler</button>
            <button style={{ ...S.btnSecondary, fontSize: 13, padding: "8px 14px" }} onClick={undoBall}>↩ Undo</button>
            <button style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8, border: "1px solid #c0392b", backgroundColor: "#fff0f0", color: "#c0392b", cursor: "pointer", fontWeight: 600 }} onClick={endInning} title="End current innings immediately">⏹ End Innings</button>
          </div>
        </div>
      )}

      {/* ── CHANGE BOWLER MID-OVER MODAL ── */}
      {changeBowlerOpen && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>Change Bowler</h3>
            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>Search and select the new bowler for the current over.</p>
            <div style={{ marginBottom: 20 }}>
              {changeBowlerPick
                ? <div style={{ ...S.input, backgroundColor: "#fff0f0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span><strong>{playerMap[changeBowlerPick] || changeBowlerPick}</strong></span>
                    <button onClick={() => setChangeBowlerPick("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                  </div>
                : <RuntimePlayerSearch
                    players={players} matchId={matchId} side={bowlTeam}
                    excluded={[]}
                    placeholder="Search or add bowler..."
                    onSelect={(id) => { setChangeBowlerPick(id); reloadPlayers(); }}
                  />}
            </div>
            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} onClick={() => {
                if (!changeBowlerPick) { setErr("Select a bowler"); return; }
                setBowler(changeBowlerPick);
                setChangeBowlerOpen(false);
                setChangeBowlerPick("");
                setErr("");
              }}>Confirm</button>
              <button style={S.btnSecondary} onClick={() => { setChangeBowlerOpen(false); setErr(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FULL SCORECARD (always visible once match started) ── */}
      <div style={S.card}>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}
          onClick={() => setScorecardOpen(o => !o)}
        >
          <h3 style={{ ...S.h3, margin: 0 }}>Full Scorecard</h3>
          <span style={{ fontSize: 18 }}>{scorecardOpen ? "▲" : "▼"}</span>
        </div>
        {scorecardOpen && (
          <div style={{ marginTop: 16 }}>
            {/* Innings tabs — show when both innings have data */}
            {match.innings?.length > 1 && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                {match.innings.map((inn, idx) => {
                  const innStats = calcInning(inn.balls || [], penaltyExtrasEnabled, match?.wideRuns || 1);
                  const teamName = inn.batting === "A" ? match.teamA?.name : match.teamB?.name;
                  return (
                    <button
                      key={idx}
                      onClick={() => setScorecardInning(idx)}
                      style={{
                        padding: "6px 14px", borderRadius: 6, border: "2px solid",
                        borderColor: scorecardInning === idx ? T.primary : "#ccc",
                        backgroundColor: scorecardInning === idx ? T.primary : "white",
                        color: scorecardInning === idx ? "white" : "#333",
                        fontWeight: 700, cursor: "pointer", fontSize: 13,
                      }}
                    >
                      {idx + 1}{idx === 0 ? "st" : "nd"} Inn — {teamName}: {innStats.total}/{innStats.wickets}
                    </button>
                  );
                })}
              </div>
            )}
            {(() => {
              const inn = match.innings?.[scorecardInning < (match.innings?.length || 0) ? scorecardInning : currInnIdx];
              if (!inn) return null;
              const innBatTeam = inn.batting || "A";
              const innBowlTeam = innBatTeam === "A" ? "B" : "A";
              const innBatIds = innBatTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
              const innBowlIds = innBowlTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
              const innBatTeamInfo = innBatTeam === "A" ? match.teamA : match.teamB;
              const innRoleMap = {};
              const addInnRole = (id, label) => { if (id) { if (!innRoleMap[id]) innRoleMap[id] = []; innRoleMap[id].push(label); } };
              addInnRole(innBatTeamInfo?.captainId, "C");
              addInnRole(innBatTeamInfo?.viceCaptainId, "VC");
              addInnRole(innBatTeamInfo?.wicketKeeperId, "WK");
              const innFow = [];
              let innRunning = 0, innLegal = 0;
              for (const b of (inn.balls || [])) {
                innRunning += (b.runs || 0) + (b.extras || 0);
                if (!b.wide && !b.noBall) innLegal++;
                if (b.wicket) innFow.push({ score: innRunning, overs: `${Math.floor(innLegal/6)}.${innLegal%6}`, batter: b.wicket.batter });
              }
              // Only pass striker/nonStriker for the active innings
              const isCurrentInn = scorecardInning === currInnIdx || (match.innings?.length === 1);
              return (
                <ScorecardTable
                  balls={inn.balls || []} playerIds={innBatIds} allPlayerIds={innBowlIds}
                  playerMap={playerMap}
                  striker={isCurrentInn ? striker : ""}
                  nonStriker={isCurrentInn ? nonStriker : ""}
                  fow={innFow} roleMap={innRoleMap}
                  penaltyExtras={penaltyExtrasEnabled} wideRuns={match?.wideRuns || 1}
                />
              );
            })()}
          </div>
        )}
      </div>

      {/* ── WICKET MODAL ── */}
      {phase === "WICKET" && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>{wicketType === "Retired" ? "🏃 Retire Batsman" : "🔴 Wicket!"}</h3>
            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
              {wicketType === "Retired" ? "Select which batsman is retiring. The new batsman will come in at that end." : "Fill in the dismissal details."}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>{wicketType === "Retired" ? "Retiring Batter" : "Dismissed Batter"}</label>
              <select style={S.select} value={wicketBatter} onChange={e => setWicketBatter(e.target.value)}>
                {[striker, nonStriker].filter(Boolean).map(id => (
                  <option key={id} value={id}>{playerMap[id] || id}</option>
                ))}
              </select>
            </div>
            {wicketType !== "Retired" && (
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Dismissal Type</label>
              <select style={S.select} value={wicketType} onChange={e => setWicketType(e.target.value)}>
                {["Bowled", "Caught", "LBW", "Run out", "Stumped", "Hit wicket", "Obstructing the field"].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            )}
            {wicketType !== "Retired" && (
              <div style={{ marginBottom: 16 }}>
                <label style={S.label}>Runs scored this ball</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[0, 1, 2, 3, 4, 6].map(r => (
                    <button
                      key={r}
                      onClick={() => setPendingBall(pb => ({ ...pb, runs: r }))}
                      style={{
                        width: 40, height: 40, borderRadius: "50%", border: "2px solid",
                        borderColor: (pendingBall?.runs ?? 0) === r ? T.primary : "#ddd",
                        backgroundColor: (pendingBall?.runs ?? 0) === r ? T.primary : "white",
                        color: (pendingBall?.runs ?? 0) === r ? "white" : "#333",
                        fontWeight: 700, cursor: "pointer", fontSize: 14,
                      }}
                    >{r}</button>
                  ))}
                </div>
              </div>
            )}
            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} onClick={confirmWicket}>{wicketType === "Retired" ? "Confirm Retire" : "Confirm Wicket"}</button>
              <button style={S.btnSecondary} onClick={() => { setPhase("SCORING"); setErr(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── QUEUE DETAILS MODAL ── */}
      {showQueueDetails && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxHeight: "80vh", overflowY: "auto" }}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>Offline Queue — {queueOps.length} ops</h3>
            <p style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
              These balls are stored on your device and not yet on the server.
            </p>
            <div style={{ fontFamily: "monospace", fontSize: 12, background: "#f5f5f5", borderRadius: 6, padding: 10, marginBottom: 14, maxHeight: "50vh", overflowY: "auto" }}>
              {queueOps.length === 0
                ? <div style={{ color: "#888" }}>Queue is empty</div>
                : queueOps.map((op, i) => {
                    let label = op.type;
                    if (op.type === 'BALL') {
                      const p = op.payload;
                      const kind = p.wide ? 'Wd' : p.noBall ? 'Nb' : '•';
                      const runs = p.runs || 0;
                      const ex = p.extras ? `+${p.extras}ex` : '';
                      const wkt = p.wicketType ? ` [W]` : '';
                      label = `${kind} ${runs}${ex}${wkt}`;
                    } else if (op.type === 'UNDO') label = '↩ UNDO';
                    else if (op.type === 'START_MATCH') label = `▶ START (${op.payload?.tossWonBy} ${op.payload?.tossChoice})`;
                    else if (op.type === 'END_INNING') label = '⏹ END INNINGS';
                    else if (op.type === 'MOTM') label = '🏆 MOTM';
                    return (
                      <div key={op.id} style={{ padding: "2px 0", borderBottom: "1px solid #eee", color: op.type === 'BALL' && op.payload?.wicketType ? "#c0392b" : "#333" }}>
                        <span style={{ color: "#999", marginRight: 6 }}>#{i + 1}</span>{label}
                      </div>
                    );
                  })
              }
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={S.btn} onClick={exportOfflineBackup}>Share/Export</button>
              <button style={S.btnSecondary} onClick={() => setShowQueueDetails(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT FROM BACKUP MODAL ── */}
      {showImport && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>Restore from Backup</h3>
            <p style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
              Paste the JSON line from your exported backup (the line starting with {"{"}"matchId"...).
            </p>
            <textarea
              style={{ ...S.input, minHeight: 100, fontFamily: "monospace", fontSize: 11, resize: "vertical" }}
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder={'{"matchId":"...","ops":[...]}'}
            />
            {err && <div style={{ ...S.error, marginBottom: 8 }}>{err}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button style={S.btn} onClick={importFromBackup} disabled={!importText.trim()}>Restore</button>
              <button style={S.btnSecondary} onClick={() => { setShowImport(false); setImportText(""); setErr(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── RETIRE MODAL ── */}
      {retireModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>🏃 Retire Batsman</h3>
            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
              Select which batsman is retiring. No ball is counted. The replacement will come in at that same end.
            </p>
            <div style={{ marginBottom: 8 }}>
              <label style={S.label}>Who is retiring?</label>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                {[striker, nonStriker].filter(Boolean).map(id => (
                  <button
                    key={id}
                    onClick={() => setRetiringBatter(id)}
                    style={{
                      flex: 1, padding: "10px 8px", borderRadius: 8, border: "2px solid",
                      borderColor: retiringBatter === id ? T.primary : "#ddd",
                      background: retiringBatter === id ? T.primary : "#fff",
                      color: retiringBatter === id ? "#fff" : "#333",
                      fontWeight: 700, fontSize: 14, cursor: "pointer",
                    }}
                  >
                    {playerMap[id] || id}
                    <span style={{ display: "block", fontSize: 11, fontWeight: 400, marginTop: 2, opacity: 0.85 }}>
                      {id === striker ? "★ On strike" : "Non-striker"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 16, marginTop: 4 }}>
              {retiringBatter === striker
                ? "New batsman will come in as striker and face the next ball."
                : "Striker continues. New batsman comes in at the non-striker end."}
            </p>
            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} onClick={confirmRetire}>Confirm Retire</button>
              <button style={S.btnSecondary} onClick={() => { setRetireModal(false); setErr(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── NEW BATTER MODAL ── */}
      {phase === "NEW_BATTER" && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>
              Next Batsman {newBowlerPending && <span style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>(then pick bowler)</span>}
            </h3>
            <p style={{ color: "#666", fontSize: 13, marginBottom: 20 }}>
              {(isRuntime ? !canAddMoreBatters : availableNextBatters.length === 0)
                ? "No more batsmen — innings will end soon."
                : "Who is coming in to bat?"}
            </p>
            {isRuntime ? (
              // Runtime mode: always a search box — can find existing players or add new ones
              canAddMoreBatters ? (
                <>
                  <RuntimePlayerSearch
                    players={players} matchId={matchId} side={batTeam}
                    excluded={[striker, nonStriker].filter(Boolean)}
                    placeholder="Search or add new batsman..."
                    onSelect={(id) => {
                      reloadPlayers();
                      // Place incoming at the dismissed/retired batter's end
                      if (dismissedBatterId && striker !== dismissedBatterId) {
                        setNonStriker(id);
                      } else {
                        setStriker(id);
                      }
                      setDismissedBatterId("");
                      setNewBatter("");
                      setErr("");
                      if (newBowlerPending) { setNewBowlerPending(false); setNewBowler(""); setPhase("END_OF_OVER"); }
                      else setPhase("SCORING");
                    }}
                  />
                  {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
                </>
              ) : (
                <button style={{ ...S.btn, width: "100%" }} onClick={() => {
                  if (newBowlerPending) { setNewBowlerPending(false); setNewBowler(""); setPhase("END_OF_OVER"); }
                  else setPhase("SCORING");
                }}>Continue Scoring</button>
              )
            ) : isAuction ? (
              // Auction mode: squad dropdown + optional runtime search
              <>
                {availableNextBatters.length > 0 && (
                  <>
                    <select style={{ ...S.select, marginBottom: 12 }} value={newBatter} onChange={e => setNewBatter(e.target.value)}>
                      <option value="">-- Select from auction squad --</option>
                      {availableNextBatters.map(id => (
                        <option key={id} value={id}>{playerMap[id] || id}</option>
                      ))}
                    </select>
                    {newBatter && (
                      <button style={{ ...S.btn, width: "100%", marginBottom: 10 }} onClick={confirmNewBatter}>▶ Come In</button>
                    )}
                    <div style={{ fontSize: 12, color: "#888", textAlign: "center", marginBottom: 8 }}>— or add an unlisted player —</div>
                  </>
                )}
                <RuntimePlayerSearch
                  players={players} matchId={matchId} side={batTeam}
                  excluded={[striker, nonStriker].filter(Boolean)}
                  placeholder="Search or add player not in auction..."
                  onSelect={(id) => {
                    reloadPlayers();
                    if (dismissedBatterId && striker !== dismissedBatterId) setNonStriker(id); else setStriker(id);
                    setDismissedBatterId(""); setNewBatter(""); setErr("");
                    if (newBowlerPending) { setNewBowlerPending(false); setNewBowler(""); setPhase("END_OF_OVER"); }
                    else setPhase("SCORING");
                  }}
                />
                {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
                {availableNextBatters.length === 0 && (
                  <button style={{ ...S.btnSecondary, width: "100%", marginTop: 10 }} onClick={() => {
                    if (newBowlerPending) { setNewBowlerPending(false); setNewBowler(""); setPhase("END_OF_OVER"); }
                    else setPhase("SCORING");
                  }}>Continue Scoring</button>
                )}
              </>
            ) : (
              // Pick Now mode: dropdown from pre-selected squad
              availableNextBatters.length > 0 ? (
                <>
                  <select style={{ ...S.select, marginBottom: 20 }} value={newBatter} onChange={e => setNewBatter(e.target.value)}>
                    <option value="">-- Select new batsman --</option>
                    {availableNextBatters.map(id => (
                      <option key={id} value={id}>{playerMap[id] || id}</option>
                    ))}
                  </select>
                  {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
                  <button style={{ ...S.btn, width: "100%" }} onClick={confirmNewBatter}>▶ Come In</button>
                </>
              ) : (
                <button style={{ ...S.btn, width: "100%" }} onClick={() => {
                  if (newBowlerPending) { setNewBowlerPending(false); setNewBowler(""); setPhase("END_OF_OVER"); }
                  else setPhase("SCORING");
                }}>Continue Scoring</button>
              )
            )}
          </div>
        </div>
      )}

      {/* ── END OF OVER MODAL ── */}
      {phase === "END_OF_OVER" && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h3 style={{ ...S.h3, marginBottom: 4 }}>Over Complete!</h3>
            {lastOverRuns !== null && (
              <div style={{ backgroundColor: "#f0f4ff", borderRadius: 6, padding: "8px 14px", marginBottom: 10, textAlign: "center", fontSize: 14 }}>
                <span style={{ color: "#555" }}>Last Over: </span>
                <strong style={{ fontSize: 18, color: T.primary }}>{lastOverRuns}</strong>
                <span style={{ color: "#555" }}> runs</span>
              </div>
            )}
            <p style={{ color: "#666", fontSize: 13, marginBottom: 16 }}>
              Batters have crossed ends. Confirm the crease and pick the next bowler.
            </p>

            {/* Batters at crease with swap button */}
            <div style={{ backgroundColor: "#f0faf4", borderRadius: 6, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.primary, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Batters at Crease</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>Striker ★</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{playerMap[striker] || striker || "—"}</div>
                </div>
                <button
                  style={{ ...S.btnSecondary, padding: "8px 12px", fontSize: 18 }}
                  title="Swap striker and non-striker"
                  onClick={() => { const t = striker; setStriker(nonStriker); setNonStriker(t); }}
                >⇄</button>
                <div style={{ flex: 1, textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#666", marginBottom: 2 }}>Non-Striker</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{playerMap[nonStriker] || nonStriker || "—"}</div>
                </div>
              </div>
            </div>

            {/* New bowler selection — searchable for all modes */}
            <div style={{ marginBottom: 20 }}>
              {newBowler
                ? <div style={{ ...S.input, backgroundColor: "#fff0f0", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span><strong>Bowler:</strong> {playerMap[newBowler] || newBowler}</span>
                    <button onClick={() => setNewBowler("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 12 }}>✕</button>
                  </div>
                : <RuntimePlayerSearch
                    players={players} matchId={matchId} side={bowlTeam}
                    excluded={[bowler].filter(Boolean)}
                    label={<>Next Over's Bowler <span style={{ color: "#c0392b", fontSize: 11, fontWeight: 400 }}>({bowlTeamName})</span></>}
                    placeholder="Search or add bowler..."
                    onSelect={(id) => { setNewBowler(id); reloadPlayers(); }}
                  />}
            </div>

            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <button style={{ ...S.btn, width: "100%" }} onClick={confirmEndOfOver}>▶ Start Next Over</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LiveView ─────────────────────────────────────────────────────────────────

function LiveView({ matchId, onBack }) {
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [err, setErr] = useState("");

  const loadMatch = useCallback(async () => {
    try { setMatch(await apiFetch("/api/matches/" + matchId + "/live")); } catch (ex) { setErr(ex.message); }
  }, [matchId]);

  useEffect(() => {
    loadMatch();
    apiFetch("/api/players").then(setPlayers).catch(() => {});
    const interval = setInterval(loadMatch, 5000);
    return () => clearInterval(interval);
  }, [loadMatch]);

  if (!match) return (
    <div style={S.page}>
      <p style={{ color: "#888" }}>{err || "Loading live match..."}</p>
    </div>
  );

  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p.name; });

  const currInnIdx = match.currentInning || 0;
  const currInn = match.innings?.[currInnIdx];
  const balls = currInn?.balls || [];
  const peEnabled = match.penaltyExtras || false;
  const stats = calcInning(balls, peEnabled);
  const batTeam = currInn?.batting || "A";
  const bowlTeam = batTeam === "A" ? "B" : "A";
  const batIds = batTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
  const bowlIds = bowlTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
  const target = currInnIdx === 1 ? (calcInning(match.innings[0]?.balls || [], peEnabled).total + 1) : null;

  // First innings summary (shown during 2nd inning)
  const firstInn = currInnIdx === 1 ? match.innings?.[0] : null;
  const firstInnStats = firstInn ? calcInning(firstInn.balls || [], peEnabled, match?.wideRuns || 1) : null;
  const firstInnTeamName = firstInn ? (firstInn.batting === "A" ? match.teamA?.name : match.teamB?.name) : null;

  return (
    <div style={S.page}>
      {onBack && <button style={{ ...S.btnSecondary, marginBottom: 16 }} onClick={onBack}>← Back</button>}
      <div style={{ ...S.card, textAlign: "center" }}>
        <h2 style={{ ...S.h2, textAlign: "center" }}>{match.teamA?.name} vs {match.teamB?.name}</h2>
        <div style={{ marginBottom: 8 }}>
          <span style={S.badge("live")}>LIVE</span>
          <span style={{ marginLeft: 8, color: "#666" }}>Inning {currInnIdx + 1} — {batTeam === "A" ? match.teamA?.name : match.teamB?.name} batting</span>
        </div>
        {firstInnStats && (
          <div style={{ fontSize: 13, color: "#888", marginBottom: 4 }}>
            {firstInnTeamName}: <strong style={{ color: "#555" }}>{firstInnStats.total}/{firstInnStats.wickets}</strong> ({firstInnStats.overs} ov)
          </div>
        )}
        <div style={{ fontSize: 48, fontWeight: 700, color: T.primary, margin: "8px 0" }}>
          {stats.total}/{stats.wickets}
        </div>
        <div style={{ fontSize: 20, color: "#555" }}>{stats.overs} overs</div>
        {target && (
          <div style={{ marginTop: 8, fontSize: 16, color: "#c0392b", fontWeight: 600 }}>
            Target: {target} | Need: {Math.max(0, target - stats.total)} from {(match.overs * 6) - stats.legalBalls} balls
          </div>
        )}
        <div style={{ fontSize: 12, color: "#aaa", marginTop: 8 }}>Auto-refreshes every 5 seconds</div>
      </div>

      {/* First innings scorecard (visible during 2nd inning) */}
      {firstInn && (() => {
        const fi = firstInn;
        const fiBatTeam = fi.batting || "A";
        const fiBowlTeam = fiBatTeam === "A" ? "B" : "A";
        const fiBatIds = fiBatTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
        const fiBowlIds = fiBowlTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
        return (
          <div style={S.card}>
            <h3 style={S.h3}>Innings 1: {firstInnTeamName} — {firstInnStats.total}/{firstInnStats.wickets} ({firstInnStats.overs})</h3>
            <ScorecardTable balls={fi.balls || []} playerIds={fiBatIds} allPlayerIds={fiBowlIds} playerMap={playerMap} penaltyExtras={peEnabled} wideRuns={match?.wideRuns || 1} />
          </div>
        );
      })()}

      <div style={S.card}>
        <h3 style={S.h3}>Innings {currInnIdx + 1}: {batTeam === "A" ? match.teamA?.name : match.teamB?.name} — {stats.total}/{stats.wickets} ({stats.overs})</h3>
        <ScorecardTable balls={balls} playerIds={batIds} allPlayerIds={bowlIds} playerMap={playerMap} penaltyExtras={peEnabled} wideRuns={match?.wideRuns || 1} />
      </div>
    </div>
  );
}

// ─── SummaryView ─────────────────────────────────────────────────────────────

function SummaryView({ user, matchId, onBack }) {
  const [match, setMatch] = useState(null);
  const [players, setPlayers] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [suggestedMotm, setSuggestedMotm] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editBalls, setEditBalls] = useState({}); // { [ballId]: { runs, extras, wide, noBall, wicketType, wicketBatter, batter, bowler } }
  const [editPropagate, setEditPropagate] = useState({}); // { [ballId]: boolean } - propagate player change to subsequent balls
  const [editPlayerNames, setEditPlayerNames] = useState({}); // { [playerId]: name }
  const [editSaving, setEditSaving] = useState(false);
  const [pendingMotm, setPendingMotm] = useState(false);
  const [unlimitedMatchEdit, setUnlimitedMatchEdit] = useState(false);

  const canEdit = user && (user.role === "admin" || user.role === "captain");
  const isAdmin = user?.role === "admin";
  const isOnline = useOnlineStatus();

  const loadMatch = useCallback(async () => {
    try {
      const m = await apiFetch("/api/matches/" + matchId);
      setMatch(m);
      cacheData("match_" + matchId, m).catch(() => {});
    } catch (ex) {
      if (!navigator.onLine || ex.message === "offline") {
        const cached = await getCached("match_" + matchId).catch(() => null);
        if (cached?.data) { setMatch(cached.data); return; }
      }
      setErr(ex.message);
    }
  }, [matchId]);

  useEffect(() => {
    loadMatch();
    apiFetch("/api/players")
      .then(p => { setPlayers(p); cacheData("players", p).catch(() => {}); })
      .catch(async () => {
        const c = await getCached("players").catch(() => null);
        if (c?.data) setPlayers(c.data);
      });
    if (isAdmin) {
      apiFetch("/api/admin/settings")
        .then(s => setUnlimitedMatchEdit(s.unlimitedMatchEdit === true))
        .catch(() => {});
    }
  }, [loadMatch, isAdmin]);

  useEffect(() => {
    if (match && players.length > 0) {
      const playerMap = {};
      players.forEach((p) => { playerMap[p.id] = p.name; });
      setSuggestedMotm(suggestMOTM(match, playerMap));
    }
  }, [match, players]);

  // Auto-sync MOTM when coming back online
  useEffect(() => {
    if (isOnline && pendingMotm && match?.motm) {
      apiFetch("/api/matches/" + matchId + "/motm", { method: "PUT", body: JSON.stringify({ playerId: match.motm }) })
        .then(() => setPendingMotm(false))
        .catch(() => {});
    }
  }, [isOnline]); // eslint-disable-line

  const setMotm = async (playerId) => {
    if (!isOnline) {
      // Apply locally and mark for sync
      setMatch(prev => {
        if (!prev) return prev;
        const m = { ...prev, motm: playerId };
        cacheData("match_" + matchId, m).catch(() => {});
        return m;
      });
      setPendingMotm(true);
      setMsg("MOTM saved offline — will sync when connected");
      setTimeout(() => setMsg(""), 3000);
      return;
    }
    try {
      await apiFetch("/api/matches/" + matchId + "/motm", { method: "PUT", body: JSON.stringify({ playerId }) });
      loadMatch();
    } catch (ex) { setErr(ex.message); }
  };

  const generateSummary = async () => {
    const playerMap = {};
    players.forEach((p) => { playerMap[p.id] = p.name; });
    setGeneratingSummary(true);
    try {
      const summary = await getMatchSummary(match, playerMap);
      await apiFetch("/api/matches/" + matchId + "/summary", { method: "PUT", body: JSON.stringify({ summary }) });
      setMsg("Summary generated!");
      loadMatch();
    } catch (ex) { setErr("Failed to generate summary: " + ex.message); } finally { setGeneratingSummary(false); }
  };

  const copyShareLink = () => {
    const url = window.location.origin + "/#summary/" + matchId;
    copyToClipboard(url).then(() => { setMsg("Link copied!"); setTimeout(() => setMsg(""), 2000); });
  };

  if (!match) return <div style={S.page}><p style={{ color: "#888" }}>{err || "Loading..."}</p></div>;

  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p.name; });

  const allPlayerIds = [...(match.teamA?.playerIds || []), ...(match.teamB?.playerIds || [])];

  let resultText = "";
  if (match.innings && match.innings.length >= 2) {
    const _pe = match.penaltyExtras || false;
    const _wr = match.wideRuns || 1;
    const inn0 = calcInning(match.innings[0]?.balls || [], _pe, _wr);
    const inn1 = calcInning(match.innings[1]?.balls || [], _pe, _wr);
    const teamA = match.teamA?.name || "Team A";
    const teamB = match.teamB?.name || "Team B";
    const battingFirst = match.innings[0]?.batting === "A" ? teamA : teamB;
    const battingSecond = match.innings[0]?.batting === "A" ? teamB : teamA;
    if (inn1.total > inn0.total) {
      const wicketsLeft = (match.teamB?.playerIds?.length || 11) - inn1.wickets - 1;
      resultText = `${battingSecond} won by ${wicketsLeft} wicket${wicketsLeft !== 1 ? "s" : ""}`;
    } else if (inn0.total > inn1.total) {
      resultText = `${battingFirst} won by ${inn0.total - inn1.total} runs`;
    } else {
      resultText = "Match Tied";
    }
  }

  return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        {onBack && <button style={S.btnSecondary} onClick={onBack}>← Back</button>}
        <h2 style={{ ...S.h2, margin: 0, flex: 1 }}>Match Summary</h2>
        {isAdmin && match.status === "completed" && (unlimitedMatchEdit || (Date.now() - (match.createdAt || 0)) < 129600000) && (
          <button style={{ ...S.btnSecondary, fontSize: 13 }} onClick={() => {
            // Pre-populate edit state from current match data
            const names = {};
            players.filter(p => [...(match.teamA?.playerIds||[]), ...(match.teamB?.playerIds||[])].includes(p.id))
              .forEach(p => { names[p.id] = p.name; });
            setEditPlayerNames(names);
            setEditBalls({});
            setEditPropagate({});
            setEditOpen(true);
          }}>🔧 Edit</button>
        )}
        <button style={S.btnOutline} onClick={copyShareLink}>🔗 Share</button>
        <button style={S.btnSecondary} onClick={() => printMatchScorecard(match, players, playerMap, resultText)}>⬇ Download PDF</button>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      <div style={{ ...S.card, textAlign: "center" }}>
        <h2 style={{ ...S.h2, textAlign: "center" }}>{match.teamA?.name} vs {match.teamB?.name}</h2>
        <span style={S.badge("completed")}>COMPLETED</span>
        {resultText && <div style={{ fontSize: 18, fontWeight: 700, color: T.primary, marginTop: 12 }}>{resultText}</div>}
        {match.innings && match.innings.map((inn, i) => {
          const st = calcInning(inn.balls || [], match.penaltyExtras || false, match.wideRuns || 1);
          const teamName = inn.batting === "A" ? match.teamA?.name : match.teamB?.name;
          return (
            <div key={i} style={{ margin: "4px 0", fontSize: 15, color: "#555" }}>
              {teamName}: <strong>{st.total}/{st.wickets}</strong> in {st.overs} overs
            </div>
          );
        })}
      </div>

      <div style={S.card}>
        <h3 style={S.h3}>Man of the Match</h3>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            {match.motm ? (
              <span style={{ fontWeight: 600, fontSize: 16 }}>🏆 {playerMap[match.motm] || match.motm}</span>
            ) : suggestedMotm ? (
              <span style={{ color: "#888" }}>Suggested: {playerMap[suggestedMotm] || suggestedMotm}</span>
            ) : (
              <span style={{ color: "#888" }}>Not set</span>
            )}
          </div>
          {canEdit && (
            <select
              style={{ ...S.select, width: 200 }}
              value={match.motm || ""}
              onChange={(e) => setMotm(e.target.value)}
            >
              <option value="">-- Select MOTM --</option>
              {allPlayerIds.map((id) => (
                <option key={id} value={id}>{playerMap[id] || id}</option>
              ))}
            </select>
          )}
          {canEdit && suggestedMotm && !match.motm && (
            <button style={S.btnSm} onClick={() => setMotm(suggestedMotm)}>Use Suggestion</button>
          )}
        </div>
      </div>

      {match.innings && match.innings.map((inn, i) => {
        const batIds = inn.batting === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
        const bowlTeam = inn.batting === "A" ? "B" : "A";
        const bowlIds = bowlTeam === "A" ? match.teamA?.playerIds || [] : match.teamB?.playerIds || [];
        const pe2 = match.penaltyExtras || false;
        const wr2 = match.wideRuns || 1;
        const st = calcInning(inn.balls || [], pe2, wr2);
        const teamName = inn.batting === "A" ? match.teamA?.name : match.teamB?.name;
        return (
          <div key={i} style={S.card}>
            <h3 style={S.h3}>Innings {i + 1}: {teamName} — {st.total}/{st.wickets} ({st.overs})</h3>
            <ScorecardTable balls={inn.balls || []} playerIds={batIds} allPlayerIds={bowlIds} playerMap={playerMap} penaltyExtras={pe2} wideRuns={wr2} />
          </div>
        );
      })}

      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ ...S.h3, margin: 0 }}>AI Match Summary</h3>
          {canEdit && (
            <button style={S.btn} onClick={generateSummary} disabled={generatingSummary}>
              {generatingSummary ? "Generating..." : "Generate Summary"}
            </button>
          )}
        </div>
        {match.aiSummary ? (
          <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, color: "#333" }}>{match.aiSummary}</div>
        ) : (
          <p style={{ color: "#888" }}>No summary yet. {canEdit ? "Click 'Generate Summary' to create one." : ""}</p>
        )}
      </div>

      {/* ── ADMIN EDIT SCORECARD MODAL ── */}
      {editOpen && (
        <div style={S.modal}>
          <div style={{ ...S.modalBox, maxWidth: 620, maxHeight: "88vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ ...S.h3, margin: 0 }}>🔧 Edit Scorecard</h3>
              <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }} onClick={() => setEditOpen(false)}>✕</button>
            </div>

            {/* Player Names */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Player Names</div>
              {Object.keys(editPlayerNames).map(pid => (
                <div key={pid} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                  <input
                    style={{ ...S.input, flex: 1, fontSize: 13 }}
                    value={editPlayerNames[pid]}
                    onChange={e => setEditPlayerNames(prev => ({ ...prev, [pid]: e.target.value }))}
                  />
                </div>
              ))}
            </div>

            {/* Ball-by-ball editor per inning */}
            {match.innings?.map((inn, innIdx) => {
              const teamName = inn.batting === "A" ? match.teamA?.name : match.teamB?.name;
              const innBatIds = inn.batting === "A" ? (match.teamA?.playerIds || []) : (match.teamB?.playerIds || []);
              const innBowlIds = inn.batting === "A" ? (match.teamB?.playerIds || []) : (match.teamA?.playerIds || []);
              return (
                <div key={innIdx} style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.primary, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Innings {innIdx + 1}: {teamName}
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ ...S.table, fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={S.th}>#</th>
                          <th style={S.th}>Batter</th>
                          <th style={S.th}>Bowler</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Runs</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Extras</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Wd</th>
                          <th style={{ ...S.th, textAlign: "center" }}>NB</th>
                          <th style={{ ...S.th, textAlign: "center" }}>Wicket</th>
                          <th style={{ ...S.th, textAlign: "center" }} title="Check to propagate player change to all subsequent balls with same player">→Next</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(inn.balls || []).map((b, bi) => {
                          const key = b.id;
                          const edit = editBalls[key] || {};
                          const get = (field, def) => field in edit ? edit[field] : (b[field] ?? def);
                          const set = (field, val) => setEditBalls(prev => ({ ...prev, [key]: { ...prev[key], ...{ [field]: val } } }));
                          const changed = !!editBalls[key];
                          const propagateChecked = !!editPropagate[key];
                          const handlePlayerChange = (field, newVal) => {
                            const oldVal = get(field, b[field] || "");
                            setEditBalls(prev => {
                              const updates = { ...prev, [key]: { ...prev[key], [field]: newVal } };
                              if (editPropagate[key]) {
                                const allBalls = inn.balls || [];
                                for (let j = bi + 1; j < allBalls.length; j++) {
                                  const sb = allBalls[j];
                                  const sbKey = sb.id;
                                  const sbCurrent = prev[sbKey]?.[field] ?? sb[field];
                                  if (sbCurrent === oldVal) {
                                    updates[sbKey] = { ...(updates[sbKey] || {}), [field]: newVal };
                                  }
                                }
                              }
                              return updates;
                            });
                          };
                          const selectStyle = { fontSize: 11, borderRadius: 4, border: "1px solid #ddd", padding: "2px 2px", maxWidth: 100, width: "100%" };
                          return (
                            <tr key={bi} style={changed ? { backgroundColor: "#fffde7" } : {}}>
                              <td style={{ ...S.td, color: "#aaa", width: 28 }}>{bi + 1}</td>
                              <td style={S.td}>
                                <select style={selectStyle} value={get("batter", b.batter || "")} onChange={e => handlePlayerChange("batter", e.target.value)}>
                                  <option value="">—</option>
                                  {innBatIds.map(id => <option key={id} value={id}>{playerMap[id] || id}</option>)}
                                </select>
                              </td>
                              <td style={S.td}>
                                <select style={selectStyle} value={get("bowler", b.bowler || "")} onChange={e => handlePlayerChange("bowler", e.target.value)}>
                                  <option value="">—</option>
                                  {innBowlIds.map(id => <option key={id} value={id}>{playerMap[id] || id}</option>)}
                                </select>
                              </td>
                              <td style={S.td}>
                                <input type="number" min={0} max={9} style={{ width: 44, textAlign: "center", padding: "2px 4px", fontSize: 12, borderRadius: 4, border: "1px solid #ddd" }}
                                  value={get("runs", 0)} onChange={e => set("runs", parseInt(e.target.value) || 0)} />
                              </td>
                              <td style={S.td}>
                                <input type="number" min={0} max={9} style={{ width: 44, textAlign: "center", padding: "2px 4px", fontSize: 12, borderRadius: 4, border: "1px solid #ddd" }}
                                  value={get("extras", 0)} onChange={e => set("extras", parseInt(e.target.value) || 0)} />
                              </td>
                              <td style={{ ...S.td, textAlign: "center" }}>
                                <input type="checkbox" checked={!!get("wide", false)} onChange={e => set("wide", e.target.checked)} />
                              </td>
                              <td style={{ ...S.td, textAlign: "center" }}>
                                <input type="checkbox" checked={!!get("noBall", false)} onChange={e => set("noBall", e.target.checked)} />
                              </td>
                              <td style={S.td}>
                                <input style={{ width: 70, padding: "2px 4px", fontSize: 11, borderRadius: 4, border: "1px solid #ddd" }}
                                  placeholder="type or blank"
                                  value={get("wicketType", b.wicket?.type || "")}
                                  onChange={e => set("wicketType", e.target.value)} />
                              </td>
                              <td style={{ ...S.td, textAlign: "center" }}>
                                <input type="checkbox" checked={propagateChecked}
                                  onChange={e => setEditPropagate(prev => ({ ...prev, [key]: e.target.checked }))}
                                  title="Propagate player changes to all subsequent balls with the same player" />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} disabled={editSaving} onClick={async () => {
                setEditSaving(true);
                setErr("");
                try {
                  // Save player name changes
                  const allPlayers = [...(match.teamA?.playerIds||[]), ...(match.teamB?.playerIds||[])];
                  for (const pid of allPlayers) {
                    const newName = editPlayerNames[pid];
                    const orig = players.find(p => p.id === pid)?.name;
                    if (newName && newName !== orig) {
                      await apiFetch("/api/players/" + pid, { method: "PUT", body: JSON.stringify({ name: newName }) });
                    }
                  }
                  // Save ball changes
                  for (const [ballId, changes] of Object.entries(editBalls)) {
                    await apiFetch("/api/matches/" + matchId + "/balls/" + ballId, { method: "PUT", body: JSON.stringify(changes) });
                  }
                  await loadMatch();
                  apiFetch("/api/players").then(setPlayers).catch(() => {});
                  setEditOpen(false);
                  setEditBalls({});
                  setEditPropagate({});
                } catch (ex) { setErr(ex.message); }
                setEditSaving(false);
              }}>{editSaving ? "Saving..." : "✓ Save Changes"}</button>
              <button style={S.btnSecondary} onClick={() => { setEditOpen(false); setEditBalls({}); setEditPropagate({}); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ProfileView ──────────────────────────────────────────────────────────────

function ProfileView({ user, onUserUpdate, currentView }) {
  const [career, setCareer] = useState(null);
  const [playerName, setPlayerName] = useState(user.name || "");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user.playerId) {
      apiFetch("/api/players/" + user.playerId + "/career")
        .then((d) => setCareer(d.career))
        .catch(() => {});
    }
  }, [user.playerId]);

  const registerAsPlayer = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    setLoading(true);
    try {
      await apiFetch("/api/auth/self-register-player", {
        method: "POST",
        body: JSON.stringify({ name: playerName }),
      });
      setMsg("Registered as player!");
      const me = await apiFetch("/api/auth/me");
      onUserUpdate(me);
    } catch (ex) {
      let m = ex.message;
      try { m = JSON.parse(m).error || m; } catch {}
      setErr(m);
    } finally {
      setLoading(false);
    }
  };

  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");

  const checkForUpdate = async () => {
    setCheckingUpdate(true);
    setUpdateStatus("");
    try {
      if (!('serviceWorker' in navigator)) { setUpdateStatus("Service worker not supported in this browser"); return; }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) { setUpdateStatus("No service worker registered yet"); return; }
      await reg.update();
      // If a new SW is waiting after update(), a new version is available
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        setUpdateStatus("New version found — reloading…");
        setTimeout(() => window.location.reload(), 1200);
      } else {
        setUpdateStatus("You are on the latest version");
      }
    } catch { setUpdateStatus("Could not check for update — try again when online"); }
    finally { setCheckingUpdate(false); }
  };

  const changeTheme = (name) => {
    localStorage.setItem("cd_theme", name);
    localStorage.setItem("cd_return_view", currentView || "PROFILE");
    window.location.reload();
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>My Profile</h2>
      <div style={S.card}>
        <div style={{ marginBottom: 8 }}><strong>Name:</strong> {user.name}</div>
        <div style={{ marginBottom: 8 }}><strong>Email:</strong> {user.email}</div>
        <div><strong>Role:</strong> {user.role}</div>
      </div>

      <div style={S.card}>
        <h3 style={S.h3}>App Theme</h3>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          {Object.entries(THEMES).map(([name, t]) => (
            <div key={name} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => changeTheme(name)}
                title={t.label}
                style={{
                  width: 44, height: 44, borderRadius: "50%",
                  backgroundColor: t.primary,
                  border: _themeName === name ? "3px solid #333" : "3px solid transparent",
                  cursor: "pointer",
                  boxShadow: _themeName === name ? "0 0 0 2px white, 0 0 0 4px " + t.primary : "none",
                  outline: "none",
                }}
              />
              <span style={{ fontSize: 10, color: _themeName === name ? T.primary : "#888", fontWeight: _themeName === name ? 700 : 400, textAlign: "center", maxWidth: 60 }}>
                {t.label}
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
          Current: <strong style={{ color: T.primary }}>{T.label}</strong> — Color code: <code>{T.primary}</code>
        </div>
      </div>

      <div style={S.card}>
        <h3 style={S.h3}>App Updates</h3>
        <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Tap below to check if a newer version of the app is available on the server.
        </p>
        <button onClick={checkForUpdate} disabled={checkingUpdate}
          style={{ ...S.btn, marginBottom: updateStatus ? 10 : 0 }}>
          {checkingUpdate ? "Checking…" : "Check for Update"}
        </button>
        {updateStatus && (
          <div style={{ fontSize: 13, marginTop: 8, padding: "8px 12px", borderRadius: 6,
            background: updateStatus.includes("latest") ? "#e8f5e9" : "#e3f2fd",
            color: updateStatus.includes("latest") ? "#2e7d32" : "#1565c0", fontWeight: 600 }}>
            {updateStatus}
          </div>
        )}
      </div>

      {!user.playerId ? (
        <div style={S.card}>
          <h3 style={S.h3}>Register as Player</h3>
          <p style={{ color: "#666", fontSize: 14, marginBottom: 16 }}>
            Link your account to a player profile to track your career stats.
          </p>
          <form onSubmit={registerAsPlayer}>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Player Name</label>
              <input style={S.input} value={playerName} onChange={(e) => setPlayerName(e.target.value)} required placeholder="Your cricket name" />
            </div>
            {err && <div style={S.error}>{err}</div>}
            {msg && <div style={S.success}>{msg}</div>}
            <button style={S.btn} disabled={loading}>{loading ? "Registering..." : "Register as Player"}</button>
          </form>
        </div>
      ) : (
        <div>
          <div style={S.card}>
            <h3 style={S.h3}>Player ID: <span style={{ fontFamily: "monospace", color: "#888" }}>{user.playerId}</span></h3>
          </div>
          {career ? (
            <>
              <div style={S.card}>
                <h3 style={S.h3}>Career Batting</h3>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Innings</th>
                      <th style={S.th}>Runs</th>
                      <th style={S.th}>Avg</th>
                      <th style={S.th}>SR</th>
                      <th style={S.th}>50s</th>
                      <th style={S.th}>100s</th>
                      <th style={S.th}>4s</th>
                      <th style={S.th}>6s</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}>{career.innings}</td>
                      <td style={S.td}><strong>{career.totRuns}</strong></td>
                      <td style={S.td}>{career.avg}</td>
                      <td style={S.td}>{career.sr}</td>
                      <td style={S.td}>{career.fifties}</td>
                      <td style={S.td}>{career.hundreds}</td>
                      <td style={S.td}>{career.totFours}</td>
                      <td style={S.td}>{career.totSixes}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={S.card}>
                <h3 style={S.h3}>Career Bowling</h3>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Wickets</th>
                      <th style={S.th}>Overs</th>
                      <th style={S.th}>Runs</th>
                      <th style={S.th}>Avg</th>
                      <th style={S.th}>Economy</th>
                      <th style={S.th}>Best</th>
                      <th style={S.th}>3W</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={S.td}><strong>{career.totWickets}</strong></td>
                      <td style={S.td}>{Math.floor(career.totBowlBalls / 6)}.{career.totBowlBalls % 6}</td>
                      <td style={S.td}>{career.totBowlRuns || 0}</td>
                      <td style={S.td}>{career.bowlAvg}</td>
                      <td style={S.td}>{career.econ}</td>
                      <td style={S.td}>{career.bestBowl ? `${career.bestBowl.w}/${career.bestBowl.r}` : "-"}</td>
                      <td style={S.td}>{career.threeWicketHauls ?? 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div style={S.card}><p style={{ color: "#888" }}>Loading career stats...</p></div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── PublicPointsView ─────────────────────────────────────────────────────────

function PublicPointsView({ seriesId, onLogin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [expandedTeam, setExpandedTeam] = useState(null);

  useEffect(() => {
    if (!seriesId) { setErr("No tournament ID provided."); return; }
    apiFetch("/api/series/" + seriesId)
      .then(setData)
      .catch(ex => setErr(ex.message));
  }, [seriesId]);

  const matchStatusColor = (s) => s === "completed" ? "#27ae60" : s === "live" ? "#e74c3c" : "#888";

  const renderTable = (rows, matches) => (
    <div style={{ overflowX: "auto" }}>
      <table style={{ ...S.table, width: "100%", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: 32, textAlign: "center" }}>#</th>
            <th style={S.th}>Team</th>
            <th style={{ ...S.th, textAlign: "center" }}>P</th>
            <th style={{ ...S.th, textAlign: "center" }}>W</th>
            <th style={{ ...S.th, textAlign: "center" }}>L</th>
            <th style={{ ...S.th, textAlign: "center" }}>T</th>
            <th style={{ ...S.th, textAlign: "center" }}>NR</th>
            <th style={{ ...S.th, textAlign: "center" }}>Pts</th>
            <th style={{ ...S.th, textAlign: "center" }}>NRR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const teamMatches = (matches || []).filter(m =>
              (m.teamAId === r.teamId || m.teamBId === r.teamId) && m.status === "completed");
            const expanded = expandedTeam === r.teamId;
            return (
              <Fragment key={r.teamId}>
                <tr style={{ backgroundColor: i === 0 ? T.light + "80" : i % 2 === 0 ? "#f9f9f9" : "white" }}>
                  <td style={{ ...S.td, textAlign: "center", color: "#888", fontWeight: i === 0 ? 700 : 400 }}>{r.rank}</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>
                    <span
                      style={{ cursor: teamMatches.length > 0 ? "pointer" : "default", color: teamMatches.length > 0 ? T.primary : "inherit" }}
                      onClick={() => teamMatches.length > 0 && setExpandedTeam(expanded ? null : r.teamId)}
                      title={teamMatches.length > 0 ? "Click to view matches" : ""}
                    >
                      {r.teamName}
                      {teamMatches.length > 0 && <span style={{ fontSize: 11, marginLeft: 5, color: "#aaa" }}>{expanded ? "▲" : "▼"}</span>}
                    </span>
                  </td>
                  <td style={{ ...S.td, textAlign: "center" }}>{r.played}</td>
                  <td style={{ ...S.td, textAlign: "center", color: "#27ae60", fontWeight: 700 }}>{r.won}</td>
                  <td style={{ ...S.td, textAlign: "center", color: "#e74c3c" }}>{r.lost}</td>
                  <td style={{ ...S.td, textAlign: "center", color: "#f39c12" }}>{r.tied}</td>
                  <td style={{ ...S.td, textAlign: "center", color: "#888" }}>{r.noResult}</td>
                  <td style={{ ...S.td, textAlign: "center", fontWeight: 700, color: T.primary }}>{r.points}</td>
                  <td style={{ ...S.td, textAlign: "center", fontWeight: 600, color: r.nrr >= 0 ? "#27ae60" : "#e74c3c" }}>
                    {r.nrr >= 0 ? "+" : ""}{r.nrr.toFixed(3)}
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={9} style={{ padding: "0 8px 10px 32px", backgroundColor: "#f0faf4" }}>
                      {teamMatches.map(m => {
                        const isA = m.teamAId === r.teamId;
                        return (
                          <div key={m.id} style={{ padding: "6px 0", borderBottom: "1px solid #e0f0e8", fontSize: 12 }}>
                            <span style={{ fontWeight: 600 }}>{m.teamAName} vs {m.teamBName}</span>
                            {m.teamAScore != null && (
                              <span style={{ color: "#555", marginLeft: 8 }}>
                                {m.teamAName}: {m.teamAScore}/{m.teamAWickets} ({m.teamAOvers} ov)
                                &nbsp;|&nbsp;
                                {m.teamBName}: {m.teamBScore}/{m.teamBWickets} ({m.teamBOvers} ov)
                              </span>
                            )}
                            {m.result && <span style={{ color: "#27ae60", marginLeft: 8 }}>{m.result}</span>}
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={{ ...S.container, maxWidth: 700, margin: "0 auto", padding: "24px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h2 style={{ color: T.primary, margin: 0 }}>Points Table</h2>
        <button style={S.btnSecondary} onClick={onLogin}>Login</button>
      </div>
      {err && <div style={S.error}>{err}</div>}
      {!data && !err && <p style={{ color: "#888" }}>Loading...</p>}
      {data && (
        <>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.primary }}>{data.name}</div>
            <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>{data.format} · {data.status}</div>
          </div>
          {(!data.pointsTable || data.pointsTable.length === 0) ? (
            <p style={{ color: "#888" }}>Points table not available yet. Complete some matches first.</p>
          ) : data.format === "GROUP_KNOCKOUT" ? (
            (() => {
              const groups = {};
              data.pointsTable.forEach(e => { const g = e.groupName || "General"; if (!groups[g]) groups[g] = []; groups[g].push(e); });
              return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([grp, entries]) => (
                <div key={grp} style={{ marginBottom: 24 }}>
                  <h4 style={{ color: T.primary, margin: "0 0 10px" }}>Group {grp}</h4>
                  {renderTable(entries, data.matches)}
                </div>
              ));
            })()
          ) : renderTable(data.pointsTable, data.matches)}
        </>
      )}
    </div>
  );
}

// ─── SeriesView ───────────────────────────────────────────────────────────────

function SeriesView({ user, onGoMatch }) {
  const isAdmin = user?.role === "admin";
  const [tournaments, setTournaments] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "", format: "ROUND_ROBIN", maxTeams: "", numberOfMatches: "3", defaultOvers: "20",
    description: "", startDate: ""
  });
  const [tournSort, setTournSort] = useState("desc"); // desc = newest first
  const [tournSearchDate, setTournSearchDate] = useState("");

  const flash = useCallback((m, isErr = false) => {
    if (isErr) setErr(m); else setMsg(m);
    setTimeout(() => { setErr(""); setMsg(""); }, 3000);
  }, []);

  const loadList = useCallback(async () => {
    try { setTournaments(await apiFetch("/api/series")); } catch (ex) { setErr(ex.message); }
  }, []);

  const loadAllTeams = useCallback(async () => {
    try { setAllTeams(await apiFetch("/api/teams")); } catch {}
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const d = await apiFetch("/api/series/" + id);
      setSelected(d);
    } catch (ex) { setErr(ex.message); }
  }, []);

  useEffect(() => { loadList(); loadAllTeams(); }, [loadList, loadAllTeams]);

  const visibleTournaments = useMemo(() => {
    let list = [...tournaments];
    if (tournSearchDate) {
      list = list.filter(t => {
        if (!t.startDate) return false;
        return new Date(t.startDate).toISOString().slice(0, 10) === tournSearchDate;
      });
    }
    list.sort((a, b) => {
      const da = a.startDate || a.createdAt || 0;
      const db = b.startDate || b.createdAt || 0;
      return tournSort === "desc" ? db - da : da - db;
    });
    return list;
  }, [tournaments, tournSort, tournSearchDate]);

  const createTournament = async () => {
    if (!form.name.trim()) { flash("Name required", true); return; }
    try {
      const body = {
        name: form.name.trim(),
        format: form.format,
        defaultOvers: parseInt(form.defaultOvers) || 20,
      };
      if (form.format === "BILATERAL") {
        body.numberOfMatches = parseInt(form.numberOfMatches) || 3;
      } else if (form.maxTeams) {
        body.maxTeams = parseInt(form.maxTeams);
      }
      if (form.description.trim()) body.description = form.description.trim();
      if (form.startDate) body.startDate = new Date(form.startDate).getTime();
      await apiFetch("/api/series", { method: "POST", body: JSON.stringify(body) });
      setForm({ name: "", format: "ROUND_ROBIN", maxTeams: "", numberOfMatches: "3", defaultOvers: "20", description: "", startDate: "" });
      setShowCreate(false);
      flash("Tournament created!");
      loadList();
    } catch (ex) { flash(ex.message, true); }
  };

  const updateStatus = useCallback(async (id, status) => {
    try {
      await apiFetch("/api/series/" + id + "/status", { method: "PUT", body: JSON.stringify({ status }) });
      flash("Status updated to " + status);
      loadList();
      loadDetail(id);
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadList, loadDetail]);

  const registerTeam = useCallback(async (seriesId, teamId) => {
    try {
      await apiFetch("/api/series/" + seriesId + "/teams/" + teamId, { method: "POST" });
      loadDetail(seriesId);
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadDetail]);

  const unregisterTeam = useCallback(async (seriesId, teamId) => {
    try {
      await apiFetch("/api/series/" + seriesId + "/teams/" + teamId, { method: "DELETE" });
      loadDetail(seriesId);
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadDetail]);

  const assignGroup = useCallback(async (seriesId, teamId, groupName) => {
    try {
      await apiFetch("/api/series/" + seriesId + "/teams/" + teamId + "/group",
        { method: "PUT", body: JSON.stringify({ groupName }) });
      loadDetail(seriesId);
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadDetail]);

  const generateFixtures = useCallback(async (seriesId) => {
    try {
      await apiFetch("/api/series/" + seriesId + "/generate-fixtures", { method: "POST" });
      flash("Fixtures generated!");
      loadDetail(seriesId);
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadDetail]);

  const deleteTournament = useCallback(async (seriesId) => {
    try {
      await apiFetch("/api/series/" + seriesId, { method: "DELETE" });
      setSelected(null);
      setDetailTab("overview");
      flash("Tournament deleted.");
      loadList();
    } catch (ex) { flash(ex.message, true); }
  }, [flash, loadList]);

  const FORMAT_LABELS = {
    FRIENDLY: "Friendly", BILATERAL: "Bilateral", ROUND_ROBIN: "Round Robin",
    KNOCKOUT: "Knockout", GROUP_KNOCKOUT: "Group + Knockout"
  };
  const STATUS_COLORS = {
    DRAFT: "#888", REGISTRATION: "#2980b9", IN_PROGRESS: "#27ae60", COMPLETED: "#7f8c8d"
  };
  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString() : "—";

  if (selected) {
    return (
      <TournamentDetailView
        t={selected}
        isAdmin={isAdmin}
        allTeams={allTeams}
        detailTab={detailTab}
        setDetailTab={setDetailTab}
        err={err}
        msg={msg}
        onBack={() => { setSelected(null); setDetailTab("overview"); loadList(); }}
        onUpdateStatus={updateStatus}
        onRegisterTeam={registerTeam}
        onUnregisterTeam={unregisterTeam}
        onAssignGroup={assignGroup}
        onGenerateFixtures={generateFixtures}
        onDeleteTournament={deleteTournament}
        onReload={() => loadDetail(selected.id)}
        onGoMatch={onGoMatch}
        flash={flash}
      />
    );
  }

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ ...S.h2, margin: 0 }}>Tournaments</h2>
        {isAdmin && (
          <button style={S.btn} onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? "Cancel" : "+ New Tournament"}
          </button>
        )}
      </div>

      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      {isAdmin && showCreate && (
        <div style={{ ...S.card, border: `2px solid ${T.primary}` }}>
          <h3 style={S.h3}>Create Tournament</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>Tournament Name *</label>
              <input style={S.input} placeholder="e.g. Summer Cup 2025" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label style={S.label}>Format</label>
              <select style={S.select} value={form.format} onChange={e => setForm(f => ({ ...f, format: e.target.value }))}>
                <option value="FRIENDLY">Friendly Series</option>
                <option value="BILATERAL">Bilateral Series</option>
                <option value="ROUND_ROBIN">Round Robin (League)</option>
                <option value="KNOCKOUT">Knockout / Elimination</option>
                <option value="GROUP_KNOCKOUT">Group Stage + Knockout</option>
              </select>
            </div>
            <div>
              {form.format === "BILATERAL" ? (
                <>
                  <label style={S.label}>Number of Matches</label>
                  <input style={S.input} type="number" min="1" max="10" value={form.numberOfMatches}
                    onChange={e => setForm(f => ({ ...f, numberOfMatches: e.target.value }))} />
                </>
              ) : (
                <>
                  <label style={S.label}>Max Teams</label>
                  <input style={S.input} type="number" placeholder="e.g. 8" value={form.maxTeams}
                    onChange={e => setForm(f => ({ ...f, maxTeams: e.target.value }))} />
                </>
              )}
            </div>
            <div>
              <label style={S.label}>Default Overs</label>
              <input style={S.input} type="number" placeholder="20" value={form.defaultOvers}
                onChange={e => setForm(f => ({ ...f, defaultOvers: e.target.value }))} />
            </div>
            <div>
              <label style={S.label}>Start Date</label>
              <input style={{ ...S.input, WebkitAppearance: "auto", appearance: "auto" }} type="date" value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={S.label}>Description</label>
              <textarea style={{ ...S.input, minHeight: 60, resize: "vertical" }}
                placeholder="Optional description..." value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button style={S.btn} onClick={createTournament}>Create Tournament</button>
          </div>
        </div>
      )}

      {/* Sort & Search bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, alignItems: "center", flexWrap: "nowrap" }}>
        <input
          style={{ ...S.input, flex: "1 1 0", minWidth: 110, width: "auto", marginBottom: 0, fontSize: 12, padding: "7px 6px", WebkitAppearance: "auto", appearance: "auto" }}
          type="date" value={tournSearchDate}
          onChange={e => setTournSearchDate(e.target.value)} />
        {tournSearchDate && (
          <button style={{ ...S.btnSm, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setTournSearchDate("")}>✕</button>
        )}
        <button
          style={{ ...S.btnSm, fontSize: 11, whiteSpace: "nowrap", flexShrink: 0, marginLeft: "auto" }}
          onClick={() => setTournSort(d => d === "desc" ? "asc" : "desc")}
        >{tournSort === "desc" ? "↓ New" : "↑ Old"}</button>
      </div>

      {visibleTournaments.length === 0 ? (
        <div style={S.card}><p style={{ color: "#888", textAlign: "center", margin: 0 }}>{tournaments.length === 0 ? "No tournaments yet." : "No tournaments match the filter."}</p></div>
      ) : (
        visibleTournaments.map(t => (
          <div key={t.id} style={{ ...S.card, cursor: "pointer" }} onClick={() => { setDetailTab("overview"); loadDetail(t.id); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ ...S.h3, margin: "0 0 8px" }}>{t.name}</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                    backgroundColor: T.light, color: T.primaryDark }}>
                    {FORMAT_LABELS[t.format] || t.format}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                    backgroundColor: (STATUS_COLORS[t.status] || "#888") + "22",
                    color: STATUS_COLORS[t.status] || "#888" }}>
                    {t.status}
                  </span>
                  {t.startDate && (
                    <span style={{ fontSize: 12, color: "#888" }}>Started: {fmtDate(t.startDate)}</span>
                  )}
                </div>
                {t.description && (
                  <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>{t.description}</p>
                )}
              </div>
              <div style={{ textAlign: "right", fontSize: 13, color: "#888", minWidth: 80, marginLeft: 12 }}>
                <div>{(t.teams?.length || 0)} teams</div>
                <div>{(t.matches?.length || 0)} matches</div>
                {t.defaultOvers > 0 && <div>{t.defaultOvers} overs</div>}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── TournamentDetailView ──────────────────────────────────────────────────────

function TournamentDetailView({
  t, isAdmin, allTeams, detailTab, setDetailTab,
  err, msg, onBack, onUpdateStatus, onRegisterTeam, onUnregisterTeam,
  onAssignGroup, onGenerateFixtures, onDeleteTournament, onReload, onGoMatch, flash
}) {
  const [assigningGroup, setAssigningGroup] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedPtTeam, setExpandedPtTeam] = useState(null);

  const FORMAT_LABELS = {
    FRIENDLY: "Friendly", BILATERAL: "Bilateral", ROUND_ROBIN: "Round Robin",
    KNOCKOUT: "Knockout", GROUP_KNOCKOUT: "Group + Knockout"
  };
  const STATUS_COLORS = {
    DRAFT: "#888", REGISTRATION: "#2980b9", IN_PROGRESS: "#27ae60", COMPLETED: "#7f8c8d"
  };
  const STATUS_TRANSITIONS = {
    DRAFT: [{ label: "Open Registration", next: "REGISTRATION" }],
    REGISTRATION: [{ label: "Start Tournament", next: "IN_PROGRESS" }],
    IN_PROGRESS: [{ label: "Mark Completed", next: "COMPLETED" }],
    COMPLETED: []
  };

  const isGroupKnockout = t.format === "GROUP_KNOCKOUT";
  const hasPointsTable = t.format === "ROUND_ROBIN" || t.format === "GROUP_KNOCKOUT" || t.format === "FRIENDLY" || t.format === "BILATERAL";
  const hasBracket = t.format === "KNOCKOUT" || t.format === "GROUP_KNOCKOUT";

  const tabs = ["overview", "teams", "schedule"];
  if (hasPointsTable) tabs.push("points");
  if (hasBracket) tabs.push("bracket");
  const TAB_LABELS = {
    overview: "Overview", teams: "Teams", schedule: "Schedule",
    points: "Points Table", bracket: "Bracket"
  };

  const registeredIds = new Set((t.teams || []).map(rt => rt.teamId));
  const unregisteredTeams = allTeams.filter(team => !registeredIds.has(team.id));
  const canRegister = isAdmin && (t.status === "DRAFT" || t.status === "REGISTRATION");
  const fmtDate = (ts) => ts ? new Date(ts).toLocaleDateString() : "—";
  const matchStatusColor = (s) => s === "completed" ? "#27ae60" : s === "in_progress" ? T.primary : "#aaa";

  // ── Overview tab
  const renderOverview = () => (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <TInfoBox label="Format" value={FORMAT_LABELS[t.format] || t.format} />
        <TInfoBox label="Status">
          <span style={{ color: STATUS_COLORS[t.status], fontWeight: 700 }}>{t.status}</span>
        </TInfoBox>
        <TInfoBox label="Teams" value={`${(t.teams || []).length}${t.maxTeams ? " / " + t.maxTeams : ""}`} />
        <TInfoBox label="Default Overs" value={t.defaultOvers || 20} />
        {t.startDate && <TInfoBox label="Start Date" value={fmtDate(t.startDate)} />}
        <TInfoBox label="Created" value={fmtDate(t.createdAt)} />
      </div>

      {t.description && (
        <div style={{ padding: "12px 14px", backgroundColor: T.light + "50", borderRadius: 8, marginBottom: 14, fontSize: 14, color: "#444" }}>
          {t.description}
        </div>
      )}

      {t.motsPlayerId && (
        <div style={{ padding: "12px 16px", backgroundColor: "#fef9e7", border: "1px solid #f1c40f",
          borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          <strong>Man of the Series:</strong> {t.motsPlayerId}
        </div>
      )}

      {isAdmin && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          {(STATUS_TRANSITIONS[t.status] || []).map(tr => (
            <button key={tr.next} style={S.btn} onClick={() => onUpdateStatus(t.id, tr.next)}>
              {tr.label}
            </button>
          ))}
          {(t.status === "REGISTRATION" || t.status === "IN_PROGRESS") && (
            <button style={{ ...S.btn, backgroundColor: "#8e44ad" }} onClick={() => onGenerateFixtures(t.id)}>
              Generate Fixtures
            </button>
          )}
          {(t.status === "DRAFT" || t.status === "REGISTRATION") && (
            showDeleteConfirm ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 14px",
                backgroundColor: "#fdf2f2", border: "1px solid #e74c3c", borderRadius: 6 }}>
                <span style={{ fontSize: 13, color: "#c0392b" }}>This cannot be undone. Delete tournament?</span>
                <button style={{ ...S.btnSm, backgroundColor: "#e74c3c" }}
                  onClick={() => onDeleteTournament(t.id)}>Yes, Delete</button>
                <button style={{ ...S.btnSm, backgroundColor: "#888" }}
                  onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              </div>
            ) : (
              <button style={{ ...S.btn, backgroundColor: "#e74c3c" }}
                onClick={() => setShowDeleteConfirm(true)}>
                Delete Tournament
              </button>
            )
          )}
        </div>
      )}
    </div>
  );

  // ── Teams tab
  const renderTeams = () => (
    <div>
      <h4 style={{ color: T.primary, margin: "0 0 12px" }}>
        Registered Teams ({(t.teams || []).length}{t.maxTeams ? " / " + t.maxTeams : ""})
      </h4>
      {(t.teams || []).length === 0 ? (
        <p style={{ color: "#888" }}>No teams registered yet.</p>
      ) : (
        <div style={{ marginBottom: 16 }}>
          {(t.teams || []).map(rt => (
            <div key={rt.teamId} style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: "10px 14px", backgroundColor: "#f9f9f9",
              borderRadius: 6, marginBottom: 6, border: "1px solid #eee" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 600 }}>{rt.teamName}</span>
                {rt.groupName && (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8,
                    backgroundColor: T.light, color: T.primaryDark, fontWeight: 700 }}>
                    Group {rt.groupName}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {isGroupKnockout && (
                    assigningGroup === rt.teamId ? (
                      <div style={{ display: "flex", gap: 4 }}>
                        {["A", "B", "C", "D"].map(g => (
                          <button key={g} style={{ ...S.btnSm, padding: "4px 10px", fontSize: 12 }}
                            onClick={() => { onAssignGroup(t.id, rt.teamId, g); setAssigningGroup(null); }}>
                            {g}
                          </button>
                        ))}
                        <button style={{ ...S.btnSm, backgroundColor: "#bbb", padding: "4px 8px", fontSize: 12 }}
                          onClick={() => setAssigningGroup(null)}>✕</button>
                      </div>
                    ) : (
                      <button style={{ ...S.btnSm, backgroundColor: "#8e44ad" }}
                        onClick={() => setAssigningGroup(rt.teamId)}>
                        Group
                      </button>
                    )
                  )}
                  {canRegister && (
                    <button style={{ ...S.btnSm, backgroundColor: "#e74c3c" }}
                      onClick={() => onUnregisterTeam(t.id, rt.teamId)}>
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canRegister && unregisteredTeams.length > 0 && (
        <div>
          <h4 style={{ color: "#666", margin: "0 0 10px", fontSize: 14 }}>Add Teams</h4>
          {unregisteredTeams.map(team => (
            <div key={team.id} style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", padding: "8px 14px", backgroundColor: "#f0f0f0",
              borderRadius: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 13 }}>{team.name}</span>
              <button style={S.btnSm} onClick={() => onRegisterTeam(t.id, team.id)}>+ Register</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Schedule tab
  const renderSchedule = () => {
    const matches = t.matches || [];
    if (matches.length === 0) return <p style={{ color: "#888" }}>No matches scheduled yet. Generate fixtures to get started.</p>;

    const ROUND_ORDER = ["Group A", "Group B", "Group C", "Group D", "General", "QF", "SF", "Final"];
    const groups = {};
    matches.forEach(m => {
      const key = m.round || (m.groupName ? "Group " + m.groupName : "General");
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const ai = ROUND_ORDER.indexOf(a), bi = ROUND_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1; if (bi === -1) return -1;
      return ai - bi;
    });

    return (
      <div>
        {sortedKeys.map(round => (
          <div key={round} style={{ marginBottom: 22 }}>
            <h4 style={{ color: T.primary, margin: "0 0 10px", paddingBottom: 6,
              borderBottom: `2px solid ${T.light}` }}>{round}</h4>
            {groups[round].map(m => (
              <div key={m.id} style={{ padding: "10px 14px", backgroundColor: "#f9f9f9",
                borderRadius: 8, marginBottom: 6, border: "1px solid #eee",
                borderLeft: `3px solid ${matchStatusColor(m.status)}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>
                      {m.teamAName || "TBD"} vs {m.teamBName || "TBD"}
                    </div>
                    {m.status === "completed" && m.teamAScore != null && (
                      <div style={{ fontSize: 12, color: "#555" }}>
                        {m.teamAName}: {m.teamAScore}/{m.teamAWickets} ({m.teamAOvers} ov)
                        &nbsp;|&nbsp;
                        {m.teamBName}: {m.teamBScore}/{m.teamBWickets} ({m.teamBOvers} ov)
                      </div>
                    )}
                    {m.result && <div style={{ fontSize: 12, color: "#27ae60", marginTop: 3 }}>{m.result}</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, minWidth: 90 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                      backgroundColor: matchStatusColor(m.status) + "22",
                      color: matchStatusColor(m.status) }}>
                      {m.status}
                    </span>
                    {m.matchNumber > 0 && (
                      <div style={{ fontSize: 11, color: "#bbb" }}>Match #{m.matchNumber}</div>
                    )}
                    {m.status === "setup" && onGoMatch && (m.teamAName !== "TBD" && m.teamBName !== "TBD") && (
                      <button style={{ ...S.btnSm, fontSize: 12, padding: "4px 10px" }}
                        onClick={() => onGoMatch(m.id, "SETUP")}>
                        Setup / Start
                      </button>
                    )}
                    {(m.status === "live" || m.status === "in_progress") && onGoMatch && (
                      <button style={{ ...S.btnSm, fontSize: 12, padding: "4px 10px", backgroundColor: "#27ae60" }}
                        onClick={() => onGoMatch(m.id, "SCORE")}>
                        Score Live
                      </button>
                    )}
                    {m.status === "completed" && onGoMatch && (
                      <button style={{ ...S.btnSm, fontSize: 12, padding: "4px 10px", backgroundColor: "#7f8c8d" }}
                        onClick={() => onGoMatch(m.id, "SUMMARY")}>
                        View Summary
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ── Points Table tab
  const renderPointsTableRows = (rows) => {
    const matches = t.matches || [];
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ ...S.table, width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: 32, textAlign: "center" }}>#</th>
              <th style={{ ...S.th }}>Team</th>
              <th style={{ ...S.th, textAlign: "center" }}>P</th>
              <th style={{ ...S.th, textAlign: "center" }}>W</th>
              <th style={{ ...S.th, textAlign: "center" }}>L</th>
              <th style={{ ...S.th, textAlign: "center" }}>T</th>
              <th style={{ ...S.th, textAlign: "center" }}>NR</th>
              <th style={{ ...S.th, textAlign: "center" }}>Pts</th>
              <th style={{ ...S.th, textAlign: "center" }}>NRR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const teamMatches = matches.filter(m =>
                (m.teamAId === r.teamId || m.teamBId === r.teamId) && m.status === "completed");
              const expanded = expandedPtTeam === r.teamId;
              return (
                <Fragment key={r.teamId}>
                  <tr style={{ backgroundColor: i === 0 ? T.light + "80" : i % 2 === 0 ? "#f9f9f9" : "white" }}>
                    <td style={{ ...S.td, textAlign: "center", color: "#888", fontWeight: i === 0 ? 700 : 400 }}>{r.rank}</td>
                    <td style={{ ...S.td, fontWeight: 700 }}>
                      <span
                        style={{ cursor: teamMatches.length > 0 ? "pointer" : "default", color: teamMatches.length > 0 ? T.primary : "inherit" }}
                        onClick={() => teamMatches.length > 0 && setExpandedPtTeam(expanded ? null : r.teamId)}
                        title={teamMatches.length > 0 ? "Click to show/hide matches" : ""}
                      >
                        {r.teamName}
                        {teamMatches.length > 0 && <span style={{ fontSize: 11, marginLeft: 5, color: "#aaa" }}>{expanded ? "▲" : "▼"}</span>}
                      </span>
                    </td>
                    <td style={{ ...S.td, textAlign: "center" }}>{r.played}</td>
                    <td style={{ ...S.td, textAlign: "center", color: "#27ae60", fontWeight: 700 }}>{r.won}</td>
                    <td style={{ ...S.td, textAlign: "center", color: "#e74c3c" }}>{r.lost}</td>
                    <td style={{ ...S.td, textAlign: "center", color: "#f39c12" }}>{r.tied}</td>
                    <td style={{ ...S.td, textAlign: "center", color: "#888" }}>{r.noResult}</td>
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 700, color: T.primary }}>{r.points}</td>
                    <td style={{ ...S.td, textAlign: "center", fontWeight: 600, color: r.nrr >= 0 ? "#27ae60" : "#e74c3c" }}>
                      {r.nrr >= 0 ? "+" : ""}{r.nrr.toFixed(3)}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={9} style={{ padding: "0 8px 10px 32px", backgroundColor: "#f0faf4" }}>
                        {teamMatches.map(m => (
                          <div key={m.id} style={{ padding: "6px 0", borderBottom: "1px solid #e0f0e8", fontSize: 12 }}>
                            <span style={{ fontWeight: 600 }}>{m.teamAName} vs {m.teamBName}</span>
                            {m.teamAScore != null && (
                              <span style={{ color: "#555", marginLeft: 8 }}>
                                {m.teamAName}: {m.teamAScore}/{m.teamAWickets} ({m.teamAOvers} ov)
                                &nbsp;|&nbsp;
                                {m.teamBName}: {m.teamBScore}/{m.teamBWickets} ({m.teamBOvers} ov)
                              </span>
                            )}
                            {m.result && <span style={{ color: "#27ae60", marginLeft: 8 }}>{m.result}</span>}
                            {onGoMatch && (
                              <button style={{ ...S.btnSm, fontSize: 11, padding: "2px 8px", marginLeft: 10 }}
                                onClick={() => onGoMatch(m.id, "SUMMARY")}>
                                Scorecard
                              </button>
                            )}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderPoints = () => {
    const pt = t.pointsTable || [];
    const shareUrl = window.location.origin + "/#points/" + t.id;
    const shareBtn = (
      <button style={{ ...S.btnSm, fontSize: 12, marginBottom: 14 }} onClick={() => {
        copyToClipboard(shareUrl).then(() => flash("Points table link copied!"));
      }}>
        Share Link
      </button>
    );
    if (pt.length === 0) return (
      <div>{shareBtn}<p style={{ color: "#888" }}>Points table not available yet. Complete some matches first.</p></div>
    );
    if (isGroupKnockout) {
      const groups = {};
      pt.forEach(e => { const g = e.groupName || "General"; if (!groups[g]) groups[g] = []; groups[g].push(e); });
      return (
        <div>
          {shareBtn}
          {Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([grp, entries]) => (
            <div key={grp} style={{ marginBottom: 24 }}>
              <h4 style={{ color: T.primary, margin: "0 0 10px" }}>Group {grp}</h4>
              {renderPointsTableRows(entries)}
            </div>
          ))}
        </div>
      );
    }
    return <div>{shareBtn}{renderPointsTableRows(pt)}</div>;
  };

  // ── Bracket tab
  const renderBracket = () => {
    const bracket = t.bracket || [];
    if (bracket.length === 0) return <p style={{ color: "#888" }}>Bracket not available yet. Generate fixtures first.</p>;

    const ROUND_ORDER = ["QF", "SF", "Final"];
    const rounds = {};
    bracket.forEach(b => { const r = b.round || "Round"; if (!rounds[r]) rounds[r] = []; rounds[r].push(b); });
    const sortedRounds = Object.keys(rounds).sort((a, b) => {
      const ai = ROUND_ORDER.indexOf(a), bi = ROUND_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1; if (bi === -1) return -1;
      return ai - bi;
    });

    return (
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "flex", gap: 20, minWidth: "fit-content", padding: "8px 4px", alignItems: "flex-start" }}>
          {sortedRounds.map(round => (
            <div key={round} style={{ minWidth: 190 }}>
              <h4 style={{ textAlign: "center", color: T.primary, marginBottom: 14, fontSize: 13,
                padding: "4px 0", borderBottom: `2px solid ${T.light}` }}>{round}</h4>
              {rounds[round].map((b, idx) => (
                <div key={b.matchId || idx} style={{
                  border: `2px solid ${b.completed ? T.primary : "#ddd"}`,
                  borderRadius: 8, marginBottom: 14, overflow: "hidden",
                  backgroundColor: b.completed ? T.light + "40" : "white",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                }}>
                  <div style={{
                    padding: "9px 12px", borderBottom: "1px solid #eee", fontSize: 13,
                    backgroundColor: b.completed && b.winnerId === b.teamAId ? T.primary + "25" : "transparent",
                    fontWeight: b.completed && b.winnerId === b.teamAId ? 700 : 400, color: "#333"
                  }}>
                    {b.teamAName || "TBD"}
                    {b.completed && b.winnerId === b.teamAId && <span style={{ color: T.primary }}> ✓</span>}
                  </div>
                  <div style={{
                    padding: "9px 12px", fontSize: 13,
                    backgroundColor: b.completed && b.winnerId === b.teamBId ? T.primary + "25" : "transparent",
                    fontWeight: b.completed && b.winnerId === b.teamBId ? 700 : 400, color: "#333"
                  }}>
                    {b.teamBName || "TBD"}
                    {b.completed && b.winnerId === b.teamBId && <span style={{ color: T.primary }}> ✓</span>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={S.page}>
      <button style={{ ...S.btnOutline, marginBottom: 14, padding: "6px 14px", fontSize: 13 }} onClick={onBack}>
        ← Back to Tournaments
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <div>
          <h2 style={{ ...S.h2, margin: "0 0 6px" }}>{t.name}</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
              backgroundColor: T.light, color: T.primaryDark }}>
              {FORMAT_LABELS[t.format] || t.format}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
              backgroundColor: (STATUS_COLORS[t.status] || "#888") + "22",
              color: STATUS_COLORS[t.status] || "#888" }}>
              {t.status}
            </span>
          </div>
        </div>
      </div>

      {err && <div style={S.error}>{err}</div>}
      {msg && <div style={S.success}>{msg}</div>}

      <div style={{ display: "flex", gap: 2, marginBottom: 18, borderBottom: `2px solid ${T.light}`,
        overflowX: "auto", paddingBottom: 0 }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setDetailTab(tab)} style={{
            padding: "9px 18px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
            borderRadius: "6px 6px 0 0", whiteSpace: "nowrap", transition: "all 0.15s",
            backgroundColor: detailTab === tab ? T.primary : "transparent",
            color: detailTab === tab ? "white" : T.primary,
            borderBottom: detailTab === tab ? `2px solid ${T.primary}` : "2px solid transparent",
            marginBottom: -2,
          }}>
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      <div>
        {detailTab === "overview" && renderOverview()}
        {detailTab === "teams" && renderTeams()}
        {detailTab === "schedule" && renderSchedule()}
        {detailTab === "points" && renderPoints()}
        {detailTab === "bracket" && renderBracket()}
      </div>
    </div>
  );
}

function TInfoBox({ label, value, children }) {
  return (
    <div style={{ padding: "10px 14px", backgroundColor: "#f9f9f9", borderRadius: 6, border: "1px solid #eee" }}>
      <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{children || value}</div>
    </div>
  );
}

// ─── TeamsView ────────────────────────────────────────────────────────────────

function TeamsView({ user, onChallenge, onGoMatch }) {
  const isAdmin = user?.role === "admin";
  const isCaptain = user?.role === "captain";
  // Captain can only manage (rename/add/remove players) their own team
  const canManageTeam = (team) => {
    if (isAdmin) return true;
    if (!isCaptain) return false;
    // Match by linked playerId
    if (user?.playerId && team.captainId === user.playerId) return true;
    // Fallback: match captain player name to user name (when playerId not yet linked)
    if (team.captainId && team.players) {
      const cap = team.players.find(p => p.id === team.captainId);
      if (cap && cap.name.toLowerCase() === (user?.name || "").toLowerCase()) return true;
    }
    return false;
  };
  const [teams, setTeams] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [addingToTeamId, setAddingToTeamId] = useState(null);
  const [addPlayerSearch, setAddPlayerSearch] = useState("");
  const [addPlayerRole, setAddPlayerRole] = useState("BATSMAN");
  const [addPlayerMakePrimary, setAddPlayerMakePrimary] = useState(false);
  const [movingPlayer, setMovingPlayer] = useState(null); // {playerId, fromTeamId}
  const [moveTargetTeamId, setMoveTargetTeamId] = useState("");
  const [statsPlayer, setStatsPlayer] = useState(null); // {id, name}
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [pendingRoles, setPendingRoles] = useState({});

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([apiFetch("/api/teams"), apiFetch("/api/players")]);
      setTeams(t);
      setAllPlayers(p);
    } catch (ex) { setErr(ex.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };
  const clearErr = () => setErr("");

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const newTeam = await apiFetch("/api/teams", { method: "POST", body: JSON.stringify({ name: newTeamName }) });
      if (isCaptain && user?.playerId && newTeam?.id) {
        await apiFetch("/api/teams/" + newTeam.id + "/roles", {
          method: "PUT", body: JSON.stringify({ captainId: user.playerId })
        });
      }
      setNewTeamName(""); flash("Team created"); load();
    } catch (ex) { setErr(ex.message); }
  };

  const saveTeamName = async (id) => {
    if (!editingTeamName.trim()) return;
    try {
      await apiFetch("/api/teams/" + id, { method: "PUT", body: JSON.stringify({ name: editingTeamName }) });
      setEditingTeamId(null); load();
    } catch (ex) { setErr(ex.message); }
  };

  const deleteTeam = async (id, name) => {
    if (!window.confirm(`Delete team "${name}"? Players will be unassigned.`)) return;
    try {
      await apiFetch("/api/teams/" + id, { method: "DELETE" });
      if (expandedTeamId === id) setExpandedTeamId(null);
      flash("Team deleted"); load();
    } catch (ex) { setErr(ex.message); }
  };

  const addPlayerToTeam = async (teamId, forceCreate = false) => {
    let player = allPlayers.find(p => p.name.toLowerCase() === addPlayerSearch.toLowerCase());
    if (!player) {
      if (!forceCreate) { setErr("Player not found. Use 'Create & Add' to add a new player."); return; }
      try {
        player = await apiFetch("/api/players", { method: "POST", body: JSON.stringify({ name: addPlayerSearch.trim() }) });
        await load();
      } catch (ex) { setErr(ex.message); return; }
    }
    try {
      await apiFetch("/api/teams/" + teamId + "/players", {
        method: "POST",
        body: JSON.stringify({ playerId: player.id, role: addPlayerRole, makePrimary: String(addPlayerMakePrimary) }),
      });
      setAddingToTeamId(null); setAddPlayerSearch(""); setAddPlayerRole("BATSMAN"); setAddPlayerMakePrimary(false);
      flash("Player added"); load();
    } catch (ex) { setErr(ex.message); }
  };

  const removePlayer = async (teamId, playerId) => {
    if (!window.confirm("Remove this player from the team?")) return;
    try {
      await apiFetch("/api/teams/" + teamId + "/players/" + playerId, { method: "DELETE" });
      flash("Player removed"); load();
    } catch (ex) { setErr(ex.message); }
  };

  const movePlayer = async () => {
    if (!moveTargetTeamId) { setErr("Select a target team"); return; }
    try {
      await apiFetch("/api/teams/" + movingPlayer.fromTeamId + "/players/" + movingPlayer.playerId + "/move", {
        method: "PUT",
        body: JSON.stringify({ targetTeamId: moveTargetTeamId }),
      });
      setMovingPlayer(null); setMoveTargetTeamId(""); flash("Player moved"); load();
    } catch (ex) { setErr(ex.message); }
  };

  const viewStats = (playerId, name) => setStatsPlayer({ id: playerId, name });

  const roleBadge = (role) => {
    const colors = { BATSMAN: T.primary, BOWLER: "#c0392b", ALL_ROUNDER: "#7d3c98", UNASSIGNED: "#888" };
    const icons  = { BATSMAN: "🏏", BOWLER: "⚾", ALL_ROUNDER: "🏏⚾", UNASSIGNED: "?" };
    const labels = { BATSMAN: "Bat", BOWLER: "Bowl", ALL_ROUNDER: "All-R", UNASSIGNED: "?" };
    return (
      <span style={{
        backgroundColor: colors[role] || "#888", color: "white",
        fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
        marginLeft: 6, verticalAlign: "middle", whiteSpace: "nowrap",
      }}>{icons[role] || "?"} {labels[role] || role}</span>
    );
  };

  const currentTeam = teams.find(t => t.id === addingToTeamId);
  const filteredSuggestions = addPlayerSearch.length > 0
    ? allPlayers
        .filter(p => p.name.toLowerCase().includes(addPlayerSearch.toLowerCase()))
        .filter(p => !(currentTeam?.players || []).some(cp => cp.id === p.id)) // not already in this team
        .sort((a, b) => {
          const aAssigned = (a.teams || []).length > 0;
          const bAssigned = (b.teams || []).length > 0;
          return aAssigned === bAssigned ? 0 : aAssigned ? 1 : -1; // unassigned first
        })
        .slice(0, 10)
    : [];

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Teams</h2>
      {err && <div style={{ ...S.error, cursor: "pointer" }} onClick={clearErr}>{err} ✕</div>}
      {msg && <div style={S.success}>{msg}</div>}

      {/* Move Player Modal */}
      {movingPlayer && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", borderRadius: 8, padding: 20, width: "94vw", maxWidth: 380, boxShadow: "0 4px 24px rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: "0 0 16px" }}>Move Player to Another Team</h3>
            <select style={{ ...S.select, marginBottom: 16 }} value={moveTargetTeamId} onChange={e => setMoveTargetTeamId(e.target.value)}>
              <option value="">-- Select target team --</option>
              {teams.filter(t => t.id !== movingPlayer.fromTeamId).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={S.btn} onClick={movePlayer}>Move</button>
              <button style={S.btnSecondary} onClick={() => { setMovingPlayer(null); setMoveTargetTeamId(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <PlayerStatsModal player={statsPlayer} onClose={() => setStatsPlayer(null)} onGoMatch={onGoMatch} />

      {/* Create Team — admin or captain */}
      {(isAdmin || isCaptain) && (
        <div style={{ ...S.card, display: "flex", gap: 10, alignItems: "center" }}>
          <input
            style={{ ...S.input, flex: 1, maxWidth: 300 }}
            placeholder="New team name"
            value={newTeamName}
            onChange={e => setNewTeamName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && createTeam()}
          />
          <button style={S.btn} onClick={createTeam}>Create Team</button>
        </div>
      )}

      {/* Team Cards */}
      {teams.length === 0 && <p style={{ color: "#888" }}>No teams yet. {isAdmin ? "Create one above." : ""}</p>}
      {teams.map(team => (
        <div key={team.id} style={S.card}>
          {/* Team Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {editingTeamId === team.id ? (
                <>
                  <input
                    style={{ ...S.input, width: 200 }}
                    value={editingTeamName}
                    onChange={e => setEditingTeamName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") saveTeamName(team.id); if (e.key === "Escape") setEditingTeamId(null); }}
                    autoFocus
                  />
                  <button style={S.btnSm} onClick={() => saveTeamName(team.id)}>Save</button>
                  <button style={S.btnSecondary} onClick={() => setEditingTeamId(null)}>Cancel</button>
                </>
              ) : (
                <h3 style={{ margin: 0, fontSize: 18, cursor: "pointer", color: T.primary }} onClick={() => setExpandedTeamId(expandedTeamId === team.id ? null : team.id)}>
                  {team.name}
                  <span style={{ fontSize: 13, fontWeight: 400, color: "#888", marginLeft: 8 }}>({team.players?.length || 0} players)</span>
                  <span style={{ fontSize: 12, marginLeft: 8 }}>{expandedTeamId === team.id ? "▲" : "▼"}</span>
                </h3>
              )}
            </div>
            {canManageTeam(team) && editingTeamId !== team.id && (
              <div style={{ display: "flex", gap: 4 }}>
                <button title="Rename" style={{ background: "none", border: "1px solid #ccc", borderRadius: 5, padding: "3px 6px", cursor: "pointer", color: "#555", display: "inline-flex", alignItems: "center" }}
                  onClick={() => { setEditingTeamId(team.id); setEditingTeamName(team.name); }}><IconEdit /></button>
                {isAdmin && <button title="Delete" style={{ background: "none", border: "1px solid #f99", borderRadius: 5, padding: "3px 6px", cursor: "pointer", color: "#c0392b", display: "inline-flex", alignItems: "center" }}
                  onClick={() => deleteTeam(team.id, team.name)}><IconDelete /></button>}
                {team.inviteCode && (
                  <button title="Copy Invite Link" style={{ background: "none", border: "1px solid #ccc", borderRadius: 5, padding: "3px 6px", cursor: "pointer", color: "#555", display: "inline-flex", alignItems: "center" }}
                    onClick={() => { const url = window.location.origin + "/#team-join/" + team.inviteCode; copyToClipboard(url).then(() => flash("Invite link copied!")); }}><IconShare /></button>
                )}
              </div>
            )}
            {isCaptain && !canManageTeam(team) && onChallenge && (
              <button style={{ ...S.btnSm, marginLeft: 8, backgroundColor: "#2980b9", color: "white", border: "none" }}
                onClick={() => {
                  const myTeam = teams.find(t => canManageTeam(t));
                  if (!myTeam) { flash("You need to manage a team to challenge others"); return; }
                  onChallenge(myTeam.id, team.id);
                }}>
                ⚔ Challenge
              </button>
            )}
          </div>

          {/* Expanded: Players List */}
          {expandedTeamId === team.id && (
            <div style={{ marginTop: 16 }}>
              {/* Add Player Form */}
              {canManageTeam(team) && (
                addingToTeamId === team.id ? (
                  <div style={{ marginBottom: 16, padding: 14, backgroundColor: "#f5f5f5", borderRadius: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add Player to {team.name}</div>
                    <div style={{ marginBottom: 10 }}>
                      <input
                        style={{ ...S.input, width: "100%", marginBottom: 8 }}
                        placeholder="Search all players..."
                        value={addPlayerSearch}
                        onChange={e => {
                          setAddPlayerSearch(e.target.value);
                          // Auto-check makePrimary when selecting an already-assigned player
                          const match = allPlayers.find(p => p.name.toLowerCase() === e.target.value.toLowerCase());
                          if (match && (match.teams || []).length > 0) setAddPlayerMakePrimary(true);
                          else setAddPlayerMakePrimary(false);
                        }}
                      />
                      {filteredSuggestions.length > 0 && (
                        <div style={{ border: "1px solid #ddd", borderRadius: 4, backgroundColor: "white", maxHeight: 220, overflowY: "auto" }}>
                          {filteredSuggestions.map(p => {
                            const isAssigned = (p.teams || []).length > 0;
                            return (
                              <div key={p.id}
                                style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13, borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", background: isAssigned ? "#fffde7" : "white" }}
                                onMouseDown={() => {
                                  setAddPlayerSearch(p.name);
                                  setAddPlayerMakePrimary(isAssigned); // pre-check for already-assigned
                                }}>
                                <span style={{ fontWeight: 500 }}>{p.name}</span>
                                {isAssigned ? (
                                  <span style={{ fontSize: 11, color: "#e65100", background: "#fff3e0", padding: "1px 6px", borderRadius: 8, whiteSpace: "nowrap" }}>
                                    {p.primaryTeam ? "★ " + p.primaryTeam : (p.teams || []).join(", ")}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 11, color: "#888" }}>unassigned</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {addPlayerSearch.length > 0 && filteredSuggestions.length === 0 && (
                        <div style={{ fontSize: 12, color: "#888", padding: "6px 0" }}>No players found — use "Create & Add" to create a new player</div>
                      )}
                    </div>
                    <select style={{ ...S.select, marginBottom: 10 }} value={addPlayerRole} onChange={e => setAddPlayerRole(e.target.value)}>
                      <option value="BATSMAN">Batsman</option>
                      <option value="BOWLER">Bowler</option>
                      <option value="ALL_ROUNDER">All Rounder</option>
                    </select>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={addPlayerMakePrimary} onChange={e => setAddPlayerMakePrimary(e.target.checked)} />
                      Set <strong>{team.name}</strong> as primary team for this player
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button style={S.btn} onClick={() => addPlayerToTeam(team.id, false)}>Add</button>
                      <button style={{ ...S.btn, backgroundColor: "#7d3c98" }} onClick={() => addPlayerToTeam(team.id, true)}>+ Create & Add</button>
                      <button style={S.btnSecondary} onClick={() => { setAddingToTeamId(null); setAddPlayerSearch(""); setAddPlayerMakePrimary(false); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button style={{ ...S.btnSm, marginBottom: 14 }} onClick={() => { setAddingToTeamId(team.id); setAddPlayerSearch(""); }}>+ Add Player</button>
                )
              )}

              {/* C/VC/WK Role Assignment */}
              {canManageTeam(team) && team.players?.length > 0 && (
                <div style={{ marginBottom: 14, padding: "12px 14px", backgroundColor: "#f0faf4", borderRadius: 6, border: "1px solid #d4edda" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.primary, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Team Roles (default for matches)</div>
                  {[
                    { label: "Captain (C)", key: "captainId" },
                    { label: "Vice-Captain (VC)", key: "viceCaptainId" },
                    { label: "Wicket Keeper (WK)", key: "wicketKeeperId" },
                  ].map(({ label, key }) => {
                    const current = pendingRoles[team.id] ? (pendingRoles[team.id][key] ?? "") : (team[key] || "");
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <label style={{ ...S.label, margin: 0, minWidth: 130, fontSize: 12 }}>{label}</label>
                        <select
                          value={current}
                          style={{ ...S.select, flex: 1, fontSize: 13, padding: "6px 10px" }}
                          onChange={(e) => {
                            setPendingRoles(prev => ({
                              ...prev,
                              [team.id]: {
                                captainId: prev[team.id]?.captainId ?? (team.captainId || ""),
                                viceCaptainId: prev[team.id]?.viceCaptainId ?? (team.viceCaptainId || ""),
                                wicketKeeperId: prev[team.id]?.wicketKeeperId ?? (team.wicketKeeperId || ""),
                                [key]: e.target.value,
                              },
                            }));
                          }}
                        >
                          <option value="">-- None --</option>
                          {team.players.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button style={S.btn} onClick={async () => {
                      const edits = pendingRoles[team.id] || {};
                      const payload = {
                        captainId: (edits.captainId ?? (team.captainId || "")) || null,
                        viceCaptainId: (edits.viceCaptainId ?? (team.viceCaptainId || "")) || null,
                        wicketKeeperId: (edits.wicketKeeperId ?? (team.wicketKeeperId || "")) || null,
                      };
                      try {
                        await apiFetch("/api/teams/" + team.id + "/roles", {
                          method: "PUT",
                          body: JSON.stringify(payload),
                        });
                        setPendingRoles(prev => { const n = { ...prev }; delete n[team.id]; return n; });
                        flash("Roles saved for " + team.name);
                        load();
                      } catch (ex) { setErr(ex.message); }
                    }}>Save Roles</button>
                    {pendingRoles[team.id] && (
                      <button style={S.btnSecondary} onClick={() => setPendingRoles(prev => { const n = { ...prev }; delete n[team.id]; return n; })}>Reset</button>
                    )}
                  </div>
                </div>
              )}

              {/* Players Table */}
              {canManageTeam(team) && isAdmin && team.players?.length > 0 && (
                <div style={{ marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
                  <button
                    style={{ ...S.btnSm, backgroundColor: T.primary, color: "white", border: "none", fontSize: 12 }}
                    onClick={async () => {
                      if (!window.confirm(`Set "${team.name}" as primary team for all ${team.players.length} members?`)) return;
                      try {
                        const r = await apiFetch("/api/teams/" + team.id + "/set-primary-for-all", { method: "PUT" });
                        flash(`★ Primary team set for ${r.updated} player(s)`); load();
                      } catch (ex) { setErr(ex.message); }
                    }}>
                    ★ Set as Primary for All
                  </button>
                  <span style={{ fontSize: 11, color: "#888" }}>Sets this team as the primary team for all current members</span>
                </div>
              )}
              {team.players?.length === 0 ? (
                <p style={{ color: "#aaa", fontSize: 13, margin: 0 }}>No players in this team.</p>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Player</th>
                      <th style={S.th}>Role</th>
                      {canManageTeam(team) && <th style={S.th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {team.players.map(p => {
                      const fullPlayer = allPlayers.find(ap => ap.id === p.id);
                      const isPrimary = fullPlayer?.primaryTeam === team.name;
                      return (
                        <tr key={p.id}>
                          <td style={S.td}>
                            <span
                              style={{ cursor: "pointer", color: T.primary, fontWeight: 600, textDecoration: "underline" }}
                              onClick={() => viewStats(p.id, p.name)}
                            >
                              {p.name}
                            </span>
                            {isPrimary && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: "#f9a825", color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>★ Primary</span>}
                            {p.id === team.captainId && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: T.primary, color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>C</span>}
                            {p.id === team.viceCaptainId && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: "#2980b9", color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>VC</span>}
                            {p.id === team.wicketKeeperId && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: "#7d3c98", color: "white", padding: "1px 6px", borderRadius: 8, marginLeft: 6 }}>WK</span>}
                          </td>
                          <td style={S.td}>{roleBadge(p.role)}</td>
                          {canManageTeam(team) && (
                            <td style={S.td}>
                              {isAdmin && !isPrimary && (
                                <button style={{ ...S.btnSm, marginRight: 6, fontSize: 11 }}
                                  title="Set as primary team for this player"
                                  onClick={async () => {
                                    try {
                                      await apiFetch("/api/players/" + p.id, { method: "PUT", body: JSON.stringify({ name: p.name, role: p.role || "BATSMAN", primaryTeam: team.name }) });
                                      flash("★ Primary team set for " + p.name); load();
                                    } catch (ex) { setErr(ex.message); }
                                  }}>
                                  ★
                                </button>
                              )}
                              {isAdmin && <button style={{ ...S.btnSm, marginRight: 6 }}
                                onClick={() => { setMovingPlayer({ playerId: p.id, fromTeamId: team.id }); setMoveTargetTeamId(""); }}>
                                Move
                              </button>}
                              <button style={S.btnDanger} onClick={() => removePlayer(team.id, p.id)}>Remove</button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── PlayersView ──────────────────────────────────────────────────────────────

function PlayersView({ user, onGoMatch }) {
  const isAdmin = user?.role === "admin";
  const isRestricted = user?.role === "captain" || user?.role === "player";
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [myTeam, setMyTeam] = useState(null);   // from /api/teams/mine
  const [filterTeam, setFilterTeam] = useState("");
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [search, setSearch] = useState("");
  const [statsPlayer, setStatsPlayer] = useState(null);
  const [editingPlayerId, setEditingPlayerId] = useState(null);
  const [editingPlayerName, setEditingPlayerName] = useState("");
  const [editingPlayerRole, setEditingPlayerRole] = useState("");
  const [editingPrimaryTeam, setEditingPrimaryTeam] = useState("");
  const [mergeModal, setMergeModal] = useState(false);
  const [mergeFrom, setMergeFrom] = useState("");
  const [mergeTo, setMergeTo] = useState("");
  const [mergeDelete, setMergeDelete] = useState(true);
  const [merging, setMerging] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [careers, setCareers] = useState({});
  const [sortBy, setSortBy] = useState("runs");
  const [sortDir, setSortDir] = useState("desc");
  const [statsLoading, setStatsLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const reqs = [apiFetch("/api/players"), apiFetch("/api/teams")];
      if (isRestricted) reqs.push(apiFetch("/api/teams/mine").catch(() => null));
      const [pl, tm, mine] = await Promise.all(reqs);
      const playerList = pl || [];
      setPlayers(playerList);
      setTeams(tm || []);
      if (mine) { setMyTeam(mine); setFilterTeam(mine.name); }
      // Load all career stats in one bulk call instead of N individual calls
      setStatsLoading(true);
      const careersMap = await apiFetch("/api/players/careers").catch(() => ({}));
      setCareers(careersMap);
    } catch {}
    finally { setStatsLoading(false); }
  }, [isRestricted]);

  useEffect(() => { load(); }, [load]);

  const openStats = (p) => setStatsPlayer({ id: p.id, name: p.name });

  const savePlayer = async (id) => {
    if (!editingPlayerName.trim()) return;
    try {
      await apiFetch("/api/players/" + id, { method: "PUT", body: JSON.stringify({ name: editingPlayerName, role: editingPlayerRole, primaryTeam: editingPrimaryTeam }) });
      setEditingPlayerId(null);
      load();
    } catch (ex) { setErr(ex.message); }
  };

  const deletePlayer = async (id) => {
    if (!window.confirm("Delete this player?")) return;
    try { await apiFetch("/api/players/" + id, { method: "DELETE" }); load(); }
    catch (ex) { setErr(ex.message); }
  };

  const openMergeModal = (p) => {
    setMergeFrom(p.id);
    setMergeTo("");
    setMergeDelete(true);
    setMergeResult(null);
    setErr("");
    setMergeModal(true);
  };

  const confirmMerge = async () => {
    if (!mergeFrom || !mergeTo) { setErr("Select both players"); return; }
    if (mergeFrom === mergeTo) { setErr("Cannot merge a player into themselves"); return; }
    const fromName = players.find(p => p.id === mergeFrom)?.name || mergeFrom;
    const toName = players.find(p => p.id === mergeTo)?.name || mergeTo;
    if (!window.confirm(`Merge "${fromName}" INTO "${toName}"?\n\nAll batting/bowling stats from "${fromName}" will move to "${toName}".${mergeDelete ? `\n\n"${fromName}" will be DELETED after merge.` : ""}`)) return;
    setMerging(true); setErr("");
    try {
      const result = await apiFetch(`/api/players/${mergeFrom}/merge-into/${mergeTo}`, {
        method: "POST",
        body: JSON.stringify({ deleteSource: mergeDelete }),
      });
      setMergeResult(result);
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setMerging(false); }
  };

  const roleColors = { BATSMAN: T.primary, BOWLER: "#c0392b", ALL_ROUNDER: "#7d3c98" };
  const roleIcons  = { BATSMAN: "🏏", BOWLER: "⚾", ALL_ROUNDER: "🏏⚾" };
  const roleLabels = { BATSMAN: "Bat", BOWLER: "Bowl", ALL_ROUNDER: "All-R" };

  const myTeamName = myTeam?.name || null;

  // Admin sees all players by default (filterTeam="" initially); captain/player scoped to their team
  const effectiveFilter = isRestricted && !showAllPlayers && myTeamName ? myTeamName : filterTeam;

  const filtered = players.filter(p => {
    if (effectiveFilter && !(p.teams || []).includes(effectiveFilter)) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const visible = [...filtered].sort((a, b) => {
    const ca = careers[a.id] || {}, cb = careers[b.id] || {};
    const dir = sortDir === "desc" ? -1 : 1;
    if (sortBy === "runs")    return dir * ((cb.totRuns || 0) - (ca.totRuns || 0));
    if (sortBy === "wickets") return dir * ((cb.totWickets || 0) - (ca.totWickets || 0));
    if (sortBy === "matches") return dir * ((cb.matchesPlayed || 0) - (ca.matchesPlayed || 0));
    return dir * a.name.localeCompare(b.name);
  });

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortDir("desc"); }
  };
  const sortArrow = (field) => sortBy === field ? (sortDir === "desc" ? " ▼" : " ▲") : "";

  return (
    <div style={S.container}>
      <h2 style={{ color: T.primary, marginBottom: 14 }}>Players</h2>
      {err && <div style={{ color: "red", marginBottom: 8 }}>{err}</div>}

      {/* Filter + Search row */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input style={{ ...S.input, flex: 1, minWidth: 140, maxWidth: 240 }} placeholder="Search player..." value={search} onChange={e => setSearch(e.target.value)} />
        {isRestricted && myTeamName ? (
          <button style={{ ...S.btnSm, fontSize: 12, backgroundColor: showAllPlayers ? "#4caf50" : T.primary }}
            onClick={() => setShowAllPlayers(v => !v)}>
            {showAllPlayers ? "My Team Only" : "All Players"}
          </button>
        ) : (
          <select style={{ ...S.select, flex: 1, minWidth: 130, maxWidth: 200 }} value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
            <option value="">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        )}
      </div>

      {/* Sort bar */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#888", marginRight: 2 }}>Sort:</span>
        {[["runs", "Runs"], ["wickets", "Wkts"], ["matches", "Matches"], ["name", "Name"]].map(([field, label]) => (
          <button key={field} onClick={() => toggleSort(field)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 16, border: "1px solid", cursor: "pointer", fontWeight: sortBy === field ? 700 : 400,
              borderColor: sortBy === field ? T.primary : "#ddd",
              background: sortBy === field ? T.light : "#fff",
              color: sortBy === field ? T.primary : "#555" }}>
            {label}{sortArrow(field)}
          </button>
        ))}
        <span style={{ fontSize: 11, color: "#aaa", marginLeft: "auto" }}>
          {filtered.length} player{filtered.length !== 1 ? "s" : ""}
          {statsLoading ? " · loading stats…" : ""}
        </span>
      </div>

      {/* Player cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {visible.map((p, i) => {
          const c = careers[p.id];
          const isEditing = editingPlayerId === p.id;
          return (
            <div key={p.id} style={{ background: "white", border: "1px solid #e8e8e8", borderRadius: 10, padding: "10px 14px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              {isEditing ? (
                /* ── Edit mode ── */
                <div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                    <input style={{ ...S.input, flex: 1, minWidth: 140 }} value={editingPlayerName}
                      placeholder="Name" autoFocus
                      onChange={e => setEditingPlayerName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") savePlayer(p.id); if (e.key === "Escape") setEditingPlayerId(null); }} />
                    <select style={{ ...S.select, flex: 1, minWidth: 120 }} value={editingPlayerRole} onChange={e => setEditingPlayerRole(e.target.value)}>
                      <option value="BATSMAN">Batsman</option>
                      <option value="BOWLER">Bowler</option>
                      <option value="ALL_ROUNDER">All-Rounder</option>
                    </select>
                    <select style={{ ...S.select, flex: 1, minWidth: 120 }} value={editingPrimaryTeam} onChange={e => setEditingPrimaryTeam(e.target.value)}>
                      <option value="">— No primary team —</option>
                      {(p.teams || []).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={S.btn} onClick={() => savePlayer(p.id)}>Save</button>
                    <button style={S.btnSecondary} onClick={() => setEditingPlayerId(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                /* ── View mode ── */
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {/* Rank */}
                  <span style={{ fontSize: 12, color: "#bbb", minWidth: 20, textAlign: "right" }}>{i + 1}</span>

                  {/* Name */}
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <strong style={{ cursor: "pointer", color: T.primary, textDecoration: "underline", fontSize: 15 }} onClick={() => openStats(p)}>
                      {p.name}
                    </strong>
                  </div>

                  {/* Stats chips */}
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <div style={{ textAlign: "center", minWidth: 38 }}>
                      <div style={{ fontSize: 10, color: "#aaa" }}>M</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#555" }}>{c ? (c.matchesPlayed || 0) : "—"}</div>
                    </div>
                    <div style={{ textAlign: "center", minWidth: 42 }}>
                      <div style={{ fontSize: 10, color: "#aaa" }}>Runs</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.primary }}>{c ? (c.totRuns || 0) : "—"}</div>
                    </div>
                    <div style={{ textAlign: "center", minWidth: 38 }}>
                      <div style={{ fontSize: 10, color: "#aaa" }}>Wkts</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#c0392b" }}>{c ? (c.totWickets || 0) : "—"}</div>
                    </div>
                  </div>

                  {/* Admin action buttons — all inline */}
                  {isAdmin && (
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button title="Edit" style={{ background: "none", border: "1px solid #ccc", borderRadius: 5, padding: "4px 7px", cursor: "pointer", color: "#555", display: "inline-flex", alignItems: "center" }}
                        onClick={() => { setEditingPlayerId(p.id); setEditingPlayerName(p.name); setEditingPlayerRole(p.role || "BATSMAN"); setEditingPrimaryTeam(p.primaryTeam || ""); }}>
                        <IconEdit />
                      </button>
                      <button title="Merge into another player" style={{ background: "none", border: "1px solid #7b1fa2", borderRadius: 5, padding: "4px 7px", cursor: "pointer", color: "#7b1fa2", fontSize: 13, fontWeight: 700 }}
                        onClick={() => openMergeModal(p)}>⇢</button>
                      <button title="Delete" style={{ background: "none", border: "1px solid #f99", borderRadius: 5, padding: "4px 7px", cursor: "pointer", color: "#c0392b", display: "inline-flex", alignItems: "center" }}
                        onClick={() => deletePlayer(p.id)}>
                        <IconDelete />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && (
          <div style={{ color: "#aaa", textAlign: "center", padding: 32 }}>No players found</div>
        )}
      </div>

      <PlayerStatsModal player={statsPlayer} onClose={() => setStatsPlayer(null)} onGoMatch={onGoMatch} />

      {/* ── MERGE PLAYERS MODAL ── */}
      {mergeModal && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", borderRadius: 10, padding: 24, width: "94vw", maxWidth: 460, boxShadow: "0 4px 32px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: "#7b1fa2" }}>Merge Players</h3>
              <button style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888" }} onClick={() => { setMergeModal(false); setMergeResult(null); setErr(""); }}>✕</button>
            </div>

            {mergeResult ? (
              <div>
                <div style={{ background: "#f3e5f5", border: "1px solid #ce93d8", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, color: "#6a1b9a", marginBottom: 6 }}>Merge complete</div>
                  <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>
                    <strong>{mergeResult.fromName}</strong> → <strong>{mergeResult.toName}</strong><br />
                    {mergeResult.ballsReassigned} ball record(s) moved<br />
                    {mergeResult.matchSlotsReassigned} match slot(s) reassigned<br />
                    {mergeResult.sourceDeleted ? <span style={{ color: "#c0392b" }}>Source player deleted</span> : <span style={{ color: "#888" }}>Source player kept</span>}
                  </div>
                </div>
                <button style={S.btn} onClick={() => { setMergeModal(false); setMergeResult(null); }}>Done</button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>MERGE FROM (old / duplicate player)</label>
                  <select style={{ ...S.select, width: "100%" }} value={mergeFrom} onChange={e => setMergeFrom(e.target.value)}>
                    <option value="">— Select source player —</option>
                    {players.map(p => <option key={p.id} value={p.id}>{p.name}{p.teams?.length ? ` (${p.primaryTeam || p.teams[0]})` : ""}</option>)}
                  </select>
                </div>
                <div style={{ textAlign: "center", fontSize: 20, color: "#7b1fa2", margin: "4px 0 8px" }}>⇢</div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#888", display: "block", marginBottom: 4 }}>MERGE INTO (target / keep this player)</label>
                  <select style={{ ...S.select, width: "100%" }} value={mergeTo} onChange={e => setMergeTo(e.target.value)}>
                    <option value="">— Select target player —</option>
                    {players.filter(p => p.id !== mergeFrom).map(p => <option key={p.id} value={p.id}>{p.name}{p.teams?.length ? ` (${p.primaryTeam || p.teams[0]})` : ""}</option>)}
                  </select>
                </div>
                {mergeFrom && mergeTo && (
                  <div style={{ background: "#fff3e0", border: "1px solid #ffb74d", borderRadius: 6, padding: "10px 12px", marginBottom: 14, fontSize: 13, color: "#e65100" }}>
                    All batting & bowling records from <strong>{players.find(p => p.id === mergeFrom)?.name}</strong> will be moved to <strong>{players.find(p => p.id === mergeTo)?.name}</strong>.
                  </div>
                )}
                <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, cursor: "pointer", fontSize: 14 }}>
                  <input type="checkbox" checked={mergeDelete} onChange={e => setMergeDelete(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span>Delete source player (<strong>{players.find(p => p.id === mergeFrom)?.name || "—"}</strong>) after merge</span>
                </label>
                {err && <div style={{ ...S.error, marginBottom: 10 }}>{err}</div>}
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={{ ...S.btn, background: "#7b1fa2", flex: 1 }} onClick={confirmMerge} disabled={merging || !mergeFrom || !mergeTo}>
                    {merging ? "Merging…" : "Confirm Merge"}
                  </button>
                  <button style={S.btnSecondary} onClick={() => { setMergeModal(false); setErr(""); }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PlayerStatsModal ─────────────────────────────────────────────────────────
// Shared modal used in both PlayersView and LeaderboardView.

function PlayerStatsModal({ player, onClose, onGoMatch }) {
  const [statsData, setStatsData] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [inningsLog, setInningsLog] = useState(null);
  const [tab, setTab] = useState("batting");

  useEffect(() => {
    if (!player) return;
    setStatsData(null); setTeamStats(null); setInningsLog(null);
    apiFetch("/api/players/" + player.id + "/career")
      .then(d => setStatsData(d.career || d))
      .catch(() => {});
    apiFetch("/api/teams/players/" + player.id + "/stats")
      .then(d => setTeamStats(d.statsByTeam || []))
      .catch(() => {});
    apiFetch("/api/players/" + player.id + "/innings")
      .then(d => setInningsLog(Array.isArray(d) ? d : []))
      .catch(() => setInningsLog([]));
  }, [player]);

  if (!player) return null;

  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ backgroundColor: "white", borderRadius: 10, padding: 24, width: "94vw", maxWidth: 540, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 4px 32px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, color: T.primary }}>{player.name} — Career Stats</h3>
          <button style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#888" }} onClick={onClose}>✕</button>
        </div>
        {!statsData ? <p style={{ color: "#888" }}>Loading...</p> : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 16px", marginBottom: 16, padding: "10px 12px", backgroundColor: T.light, borderRadius: 6 }}>
              <div><span style={{ color: "#888", fontSize: 12 }}>Matches</span><br /><strong style={{ fontSize: 18 }}>{statsData.matchesPlayed ?? 0}</strong></div>
              <div><span style={{ color: "#888", fontSize: 12 }}>Innings</span><br /><strong style={{ fontSize: 18 }}>{statsData.innings ?? 0}</strong></div>
              <div><span style={{ color: "#888", fontSize: 12 }}>Not Outs</span><br /><strong style={{ fontSize: 18 }}>{statsData.notOuts ?? 0}</strong></div>
            </div>
            <div style={{ display: "flex", borderBottom: "2px solid #eee", marginBottom: 16 }}>
              {["batting", "bowling", "by team", "innings"].map(t => (
                <button key={t} style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: tab === t ? T.primary : "#888", borderBottom: tab === t ? "2px solid " + T.primary : "2px solid transparent", marginBottom: -2, textTransform: "capitalize" }} onClick={() => setTab(t)}>{t}</button>
              ))}
            </div>
            {tab === "batting" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                <div><span style={{ color: "#888", fontSize: 12 }}>Runs</span><br /><strong style={{ fontSize: 22 }}>{statsData.totRuns ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Highest Score</span><br /><strong style={{ fontSize: 22 }}>{statsData.highestScore ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Average <span style={{ fontSize: 10, color: "#aaa" }}>(ICC)</span></span><br /><strong>{statsData.avg ?? "-"}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Strike Rate</span><br /><strong>{statsData.sr ?? "-"}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Balls Faced</span><br /><strong>{statsData.totBalls ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>4s</span><br /><strong>{statsData.totFours ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>6s</span><br /><strong>{statsData.totSixes ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>50s</span><br /><strong>{statsData.fifties ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>100s</span><br /><strong>{statsData.hundreds ?? 0}</strong></div>
              </div>
            )}
            {tab === "bowling" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                <div><span style={{ color: "#888", fontSize: 12 }}>Wickets</span><br /><strong style={{ fontSize: 22 }}>{statsData.totWickets ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Overs Bowled</span><br /><strong style={{ fontSize: 22 }}>{Math.floor((statsData.totBowlBalls || 0) / 6)}.{(statsData.totBowlBalls || 0) % 6}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Runs Conceded</span><br /><strong>{statsData.totBowlRuns ?? 0}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Best Figures</span><br /><strong>{statsData.bestBowl && statsData.bestBowl.w > 0 ? `${statsData.bestBowl.w}/${statsData.bestBowl.r}` : "-"}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Bowling Avg</span><br /><strong>{statsData.bowlAvg ?? "-"}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>Economy</span><br /><strong>{statsData.econ ?? "-"}</strong></div>
                <div><span style={{ color: "#888", fontSize: 12 }}>3W Hauls</span><br /><strong>{statsData.threeWicketHauls ?? 0}</strong></div>
              </div>
            )}
            {tab === "innings" && (
              inningsLog === null ? <p style={{ color: "#888" }}>Loading...</p> :
              inningsLog.length === 0 ? <p style={{ color: "#aaa", textAlign: "center", padding: 20 }}>No innings recorded</p> : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ ...S.table, width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ ...S.th, textAlign: "center" }}>#</th>
                        <th style={S.th}>Date</th>
                        <th style={S.th}>Opponent</th>
                        <th style={{ ...S.th, textAlign: "center" }}>Runs</th>
                        <th style={{ ...S.th, textAlign: "center" }}>Balls</th>
                        <th style={{ ...S.th, textAlign: "center" }}>4s</th>
                        <th style={{ ...S.th, textAlign: "center" }}>6s</th>
                        <th style={{ ...S.th, textAlign: "center" }}>SR</th>
                        <th style={S.th}>Dismissal</th>
                        {onGoMatch && <th style={{ ...S.th, textAlign: "center" }}></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {inningsLog.map((inn, i) => {
                        const d = new Date(inn.dateMs);
                        const dateStr = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
                        const isNotOut = inn.dismissal === "not out";
                        const matchView = inn.matchStatus === "live" ? "SCORE" : inn.matchStatus === "setup" ? "SETUP" : "SUMMARY";
                        return (
                          <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "white" }}>
                            <td style={{ ...S.td, textAlign: "center", color: "#aaa", fontSize: 12 }}>{i + 1}</td>
                            <td style={{ ...S.td, fontSize: 12, color: "#666" }}>{dateStr}</td>
                            <td style={S.td}>{inn.opponent || "—"}</td>
                            <td style={{ ...S.td, textAlign: "center", fontWeight: 700, color: inn.runs >= 50 ? T.primary : "inherit" }}>{inn.runs}{isNotOut ? "*" : ""}</td>
                            <td style={{ ...S.td, textAlign: "center" }}>{inn.balls}</td>
                            <td style={{ ...S.td, textAlign: "center" }}>{inn.fours}</td>
                            <td style={{ ...S.td, textAlign: "center" }}>{inn.sixes}</td>
                            <td style={{ ...S.td, textAlign: "center" }}>{inn.sr}</td>
                            <td style={{ ...S.td, fontSize: 12, color: isNotOut ? "#27ae60" : "#888", fontStyle: isNotOut ? "italic" : "normal" }}>{inn.dismissal}</td>
                            {onGoMatch && (
                              <td style={{ ...S.td, textAlign: "center" }}>
                                <button
                                  title={inn.matchStatus === "live" ? "Score match" : "View / Edit scorecard"}
                                  onClick={() => { onClose(); onGoMatch(inn.matchId, matchView); }}
                                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, color: T.primary, padding: "2px 4px" }}>
                                  {inn.matchStatus === "live" ? "🟢" : "📋"}
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
            {tab === "by team" && (
              teamStats === null ? <p style={{ color: "#888" }}>Loading...</p> :
              teamStats.length === 0 ? <p style={{ color: "#aaa", textAlign: "center", padding: 20 }}>No team data available</p> :
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {teamStats.map(ts => (
                  <div key={ts.teamName} style={{ border: "1px solid #e0e0e0", borderRadius: 8, padding: "12px 14px", background: "#fafafa" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: T.primary, marginBottom: 8 }}>{ts.teamName}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 0" }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#888" }}>Runs</div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: T.primary }}>{ts.runs ?? 0}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#888" }}>Wickets</div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: "#c0392b" }}>{ts.wickets ?? 0}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#888" }}>Innings</div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>{ts.innings ?? 0}</div>
                      </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "4px 0", marginTop: 8, paddingTop: 8, borderTop: "1px solid #eee" }}>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: "#aaa" }}>Avg</div><div style={{ fontSize: 13, fontWeight: 600 }}>{ts.avg ?? "-"}</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: "#aaa" }}>SR</div><div style={{ fontSize: 13, fontWeight: 600 }}>{ts.sr ?? "-"}</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: "#aaa" }}>Econ</div><div style={{ fontSize: 13, fontWeight: 600 }}>{ts.econ ?? "-"}</div></div>
                      <div style={{ textAlign: "center" }}><div style={{ fontSize: 10, color: "#aaa" }}>Best</div><div style={{ fontSize: 13, fontWeight: 600 }}>{ts.bestBowl && ts.bestBowl !== "0/0" ? ts.bestBowl : "-"}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── LeaderboardView ──────────────────────────────────────────────────────────

function LeaderboardView({ onGoMatch }) {
  const [teams, setTeams] = useState([]);
  const [filterTeam, setFilterTeam] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [batsmen, setBatsmen] = useState([]);
  const [bowlers, setBowlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsPlayer, setStatsPlayer] = useState(null);

  // Generate last 24 months as options
  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("default", { month: "long", year: "numeric" });
      opts.push({ val, label });
    }
    return opts;
  })();

  useEffect(() => {
    apiFetch("/api/teams").then(setTeams).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchAll = async () => {
      try {
        const careersUrl = "/api/players/careers" + (filterMonth ? "?month=" + filterMonth : "");
        const [players, careersMap] = await Promise.all([
          apiFetch("/api/players"),
          apiFetch(careersUrl).catch(() => ({}))
        ]);
        const withCareer = players
          .map(p => ({ ...p, career: careersMap[p.id] || null }))
          .filter(p => p.career);
        const filtered = filterTeam ? withCareer.filter(p => (p.teams || []).includes(filterTeam)) : withCareer;
        const topBat = [...filtered]
          .sort((a, b) => (b.career.totRuns || 0) - (a.career.totRuns || 0))
          .slice(0, 5);
        const topBowl = [...filtered]
          .sort((a, b) => (b.career.totWickets || 0) - (a.career.totWickets || 0))
          .slice(0, 5);
        setBatsmen(topBat);
        setBowlers(topBowl);
      } catch {}
      setLoading(false);
    };
    fetchAll();
  }, [filterTeam, filterMonth]);

  const medal = ["🥇", "🥈", "🥉", "4", "5"];

  const BatTable = () => (
    <div style={{ flex: 1, minWidth: 280 }}>
      <h3 style={{ color: T.primary, marginBottom: 12, fontSize: 16 }}>Top 5 Batsmen</h3>
      <table style={{ ...S.table, width: "100%" }}>
        <thead>
          <tr>
            <th style={S.th}>#</th>
            <th style={S.th}>Player</th>
            <th style={S.th}>Inn</th>
            <th style={S.th}>Runs</th>
            <th style={S.th}>Avg</th>
            <th style={S.th}>SR</th>
            <th style={S.th}>HS</th>
          </tr>
        </thead>
        <tbody>
          {batsmen.map((p, i) => (
            <tr key={p.id} style={{ backgroundColor: i === 0 ? T.light : "white" }}>
              <td style={{ ...S.td, textAlign: "center", fontSize: 16 }}>{medal[i]}</td>
              <td style={S.td}>
                <div style={{ fontWeight: 700, cursor: "pointer", color: T.primary, textDecoration: "underline" }} onClick={() => setStatsPlayer({ id: p.id, name: p.name })}>{p.name}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{p.primaryTeam ? <strong style={{ color: T.primary }}>★ {p.primaryTeam}</strong> : ((p.teams || [])[0] || "—")}</div>
              </td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.innings ?? 0}</td>
              <td style={{ ...S.td, fontWeight: 700, color: T.primary, textAlign: "center" }}>{p.career.totRuns ?? 0}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.avg ?? "-"}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.sr && p.career.sr !== "-" ? Math.round(parseFloat(p.career.sr)) : "-"}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.highestScore ?? 0}</td>
            </tr>
          ))}
          {batsmen.length === 0 && !loading && (
            <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#aaa", padding: 20 }}>No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const BowlTable = () => (
    <div style={{ flex: 1, minWidth: 280 }}>
      <h3 style={{ color: "#c0392b", marginBottom: 12, fontSize: 16 }}>Top 5 Bowlers</h3>
      <table style={{ ...S.table, width: "100%" }}>
        <thead>
          <tr>
            <th style={S.th}>#</th>
            <th style={S.th}>Player</th>
            <th style={S.th}>Ov</th>
            <th style={S.th}>Wkts</th>
            <th style={S.th}>Econ</th>
            <th style={S.th}>Avg</th>
            <th style={S.th}>Best</th>
          </tr>
        </thead>
        <tbody>
          {bowlers.map((p, i) => {
            const balls = p.career.totBowlBalls ?? 0;
            const overs = balls > 0 ? `${Math.floor(balls / 6)}.${balls % 6}` : "-";
            return (
            <tr key={p.id} style={{ backgroundColor: i === 0 ? "#fff5f5" : "white" }}>
              <td style={{ ...S.td, textAlign: "center", fontSize: 16 }}>{medal[i]}</td>
              <td style={S.td}>
                <div style={{ fontWeight: 700, cursor: "pointer", color: "#c0392b", textDecoration: "underline" }} onClick={() => setStatsPlayer({ id: p.id, name: p.name })}>{p.name}</div>
                <div style={{ fontSize: 11, color: "#888" }}>{p.primaryTeam ? <strong style={{ color: T.primary }}>★ {p.primaryTeam}</strong> : ((p.teams || [])[0] || "—")}</div>
              </td>
              <td style={{ ...S.td, textAlign: "center" }}>{overs}</td>
              <td style={{ ...S.td, fontWeight: 700, color: "#c0392b", textAlign: "center" }}>{p.career.totWickets ?? 0}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.econ ?? "-"}</td>
              <td style={{ ...S.td, textAlign: "center" }}>{p.career.bowlAvg ?? "-"}</td>
              <td style={{ ...S.td, textAlign: "center" }}>
                {p.career.bestBowl && p.career.bestBowl.w > 0 ? `${p.career.bestBowl.w}/${p.career.bestBowl.r}` : "-"}
              </td>
            </tr>
            );
          })}
          {bowlers.length === 0 && !loading && (
            <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#aaa", padding: 20 }}>No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={S.container}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <h2 style={{ color: T.primary, margin: 0 }}>Leaderboard</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <select style={{ ...S.select, width: 150 }} value={filterTeam} onChange={e => setFilterTeam(e.target.value)}>
            <option value="">All Teams</option>
            {teams.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          <select style={{ ...S.select, width: 170 }} value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
            <option value="">All Time</option>
            {monthOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
        </div>
      </div>
      {loading ? (
        <p style={{ color: "#888", textAlign: "center", padding: 40 }}>Loading stats...</p>
      ) : (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <BatTable />
          <BowlTable />
        </div>
      )}
      <PlayerStatsModal player={statsPlayer} onClose={() => setStatsPlayer(null)} onGoMatch={onGoMatch} />
    </div>
  );
}

// ─── TeamJoinView ─────────────────────────────────────────────────────────────

function TeamJoinView({ user, inviteCode, onBack }) {
  const [team, setTeam] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiFetch("/api/teams/join/" + inviteCode)
      .then(setTeam)
      .catch(() => setErr("Invalid invite link"));
  }, [inviteCode]);

  const join = async () => {
    setErr(""); setLoading(true);
    try {
      await apiFetch("/api/teams/join/" + inviteCode, { method: "POST" });
      setMsg("You've joined " + team?.name + "!");
    } catch (ex) {
      let m = ex.message; try { m = JSON.parse(m).error || m; } catch {}
      setErr(m);
    } finally { setLoading(false); }
  };

  return (
    <div style={S.page}>
      <button style={S.btnSecondary} onClick={onBack}>← Back to Teams</button>
      <div style={{ ...S.card, maxWidth: 480, margin: "24px auto" }}>
        {!team ? (
          err ? <div style={S.error}>{err}</div> : <p style={{ color: "#888" }}>Loading...</p>
        ) : msg ? (
          <div>
            <div style={S.success}>{msg}</div>
            <button style={{ ...S.btn, marginTop: 16 }} onClick={onBack}>View Teams</button>
          </div>
        ) : (
          <>
            <h2 style={{ color: T.primary, marginBottom: 4 }}>Join Team</h2>
            <h3 style={{ marginBottom: 8 }}>{team.name}</h3>
            <p style={{ color: "#666", fontSize: 14, marginBottom: 20 }}>
              {(team.players || []).length} player{(team.players || []).length !== 1 ? "s" : ""} currently in this team.
            </p>
            {err && <div style={{ ...S.error, marginBottom: 12 }}>{err}</div>}
            {user ? (
              <button style={{ ...S.btn, width: "100%" }} onClick={join} disabled={loading}>
                {loading ? "Joining..." : "Join " + team.name}
              </button>
            ) : (
              <p style={{ color: "#888", fontSize: 13 }}>Sign in to join this team.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── NavBar ───────────────────────────────────────────────────────────────────

// ─── AuctionPoolSearch ────────────────────────────────────────────────────────
// Searchable player picker for auction pool. Loads all players from the backend,
// offers "Use All Players" bulk-add, a search dropdown, and "+ Add new" if the
// typed name is not found. onSelect({id, name}) is awaited by the parent.
function AuctionPoolSearch({ alreadyInPool, onSelect, onSelectAll, hideAddAll }) {
  const [search, setSearch] = useState("");
  const [allPlayers, setAllPlayers] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    apiFetch("/api/players").then(setAllPlayers).catch(() => setAllPlayers([]));
  }, []);

  const inPool = new Set((alreadyInPool || []).map(p => p.id));
  const available = (allPlayers || []).filter(p => !inPool.has(p.id));
  const filtered = search.trim()
    ? available.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 8)
    : [];
  const exactMatch = available.find(p => p.name.toLowerCase() === search.trim().toLowerCase());

  const doSelect = async (player) => {
    setBusy(true); setErr("");
    try { await onSelect({ id: player.id, name: player.name }); }
    catch (ex) { setErr(ex.message); }
    setSearch(""); setBusy(false);
  };

  const doCreate = async () => {
    if (!search.trim() || busy) return;
    setBusy(true); setErr("");
    try {
      let player = (allPlayers || []).find(p => p.name.toLowerCase() === search.trim().toLowerCase());
      if (!player) {
        player = await apiFetch("/api/players", { method: "POST", body: JSON.stringify({ name: search.trim() }) });
        setAllPlayers(prev => [...(prev || []), player]);
      }
      await onSelect({ id: player.id, name: player.name });
    } catch (ex) { setErr(ex.message); }
    setSearch(""); setBusy(false);
  };

  const doAddAll = async () => {
    if (!available.length || busy) return;
    setBusy(true); setErr("");
    const snapshot = [...available];
    try {
      if (onSelectAll) {
        // Batch callback — parent updates state in one go (avoids stale-closure issues)
        await onSelectAll(snapshot);
      } else {
        // Sequential API calls (online auction)
        for (const p of snapshot) await onSelect({ id: p.id, name: p.name });
      }
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <div>
      {allPlayers === null && <div style={{ color: "#aaa", fontSize: 13, marginBottom: 8 }}>Loading players…</div>}
      {!hideAddAll && available.length > 0 && (
        <button onClick={doAddAll} disabled={busy}
          style={{ ...S.btnSecondary, width: "100%", marginBottom: 10, fontSize: 13 }}>
          {busy ? "Adding…" : `Use All Players (${available.length})`}
        </button>
      )}
      <div style={{ position: "relative" }}>
        <input
          style={{ ...S.input, marginBottom: 0, backgroundColor: busy ? "#f5f5f5" : "white" }}
          placeholder="Search player by name…"
          value={search} disabled={busy}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              if (exactMatch) doSelect(exactMatch);
              else if (search.trim()) doCreate();
            }
          }}
        />
        {search.trim().length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, backgroundColor: "white", border: "1px solid #ddd", borderRadius: 6, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
            {filtered.map(p => (
              <div key={p.id} onClick={() => doSelect(p)}
                style={{ padding: "9px 12px", cursor: "pointer", fontSize: 14, borderBottom: "1px solid #f0f0f0", backgroundColor: "white" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#f0faf4"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "white"}>
                {p.name}
              </div>
            ))}
            {!exactMatch && (
              <div onClick={doCreate}
                style={{ padding: "9px 12px", cursor: busy ? "not-allowed" : "pointer", fontSize: 14, color: T.primary, fontWeight: 600, borderTop: filtered.length > 0 ? "1px solid #eee" : "none" }}>
                {busy ? "Adding…" : `+ Add "${search.trim()}" as new player`}
              </div>
            )}
          </div>
        )}
      </div>
      {err && <div style={{ ...S.error, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

// ─── AuctionView ──────────────────────────────────────────────────────────────

function AuctionView({ onBack, user }) {
  const [step, setStep] = useState("LIST"); // LIST | SETUP | POOL | PICKS | ONLINE_CREATE | ONLINE_JOIN | ONLINE_ROOM
  const [auctions, setAuctions] = useState(() => getAuctions());
  const [currentAuction, setCurrentAuction] = useState(null);
  const [err, setErr] = useState("");
  const [auctionName, setAuctionName] = useState("");
  const [teamAName, setTeamAName] = useState("");
  const [teamBName, setTeamBName] = useState("");
  const [playerInput, setPlayerInput] = useState("");
  const [addingPlayer, setAddingPlayer] = useState(false);

  // Edit mode state (admin only, completed auctions)
  const [editMode, setEditMode] = useState(false);
  const [editPicks, setEditPicks] = useState([]);
  const [editAddTeam, setEditAddTeam] = useState("A");

  // Online auction state
  const [onlineRoom, setOnlineRoom] = useState(null); // full response {code, hostEmail, joinerEmail, state}
  const [joinCode, setJoinCode] = useState("");
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [onlineOnlineName, setOnlineOnlineName] = useState("");
  const [onlineTeamA, setOnlineTeamA] = useState("");
  const [onlineTeamB, setOnlineTeamB] = useState("");
  const [onlinePlayerInput, setOnlinePlayerInput] = useState("");
  const [onlineAddingPlayer, setOnlineAddingPlayer] = useState(false);

  const userEmail = user?.email || "";

  // Poll online room state every 3 seconds when in ONLINE_ROOM step
  useEffect(() => {
    if (step !== "ONLINE_ROOM" || !onlineRoom?.code) return;
    const interval = setInterval(async () => {
      try {
        const res = await apiFetch("/api/auctions/online/" + onlineRoom.code);
        setOnlineRoom(res);
        // If completed, save locally and go to list
        if (res.state?.status === "completed") {
          saveCompletedOnlineAuction(res);
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(interval);
  }, [step, onlineRoom?.code]);

  const saveCompletedOnlineAuction = (room) => {
    const s = room.state;
    const auction = {
      id: "online_" + room.code,
      name: s.name || room.code,
      teamAName: s.teamAName,
      teamBName: s.teamBName,
      playerPool: s.playerPool || [],
      picks: s.picks || [],
      status: "completed",
      createdAt: Date.now(),
      onlineCode: room.code,
    };
    saveAuctionToStorage(auction);
    setAuctions(getAuctions());
  };

  const isHost = onlineRoom && onlineRoom.hostEmail === userEmail;
  const isJoiner = onlineRoom && onlineRoom.joinerEmail === userEmail;

  const refreshAuctions = () => setAuctions(getAuctions());

  const updateAuction = (updated) => {
    saveAuctionToStorage(updated);
    setCurrentAuction(updated);
    refreshAuctions();
  };

  const createAuction = () => {
    if (!teamAName.trim() || !teamBName.trim()) { setErr("Enter both team names"); return; }
    const auction = {
      id: "auction_" + Date.now(),
      name: auctionName.trim() || ("Auction " + new Date().toLocaleDateString()),
      teamAName: teamAName.trim(),
      teamBName: teamBName.trim(),
      playerPool: [],
      picks: [],
      status: "in_progress",
      createdAt: Date.now(),
    };
    saveAuctionToStorage(auction);
    setCurrentAuction(auction);
    refreshAuctions();
    setStep("POOL");
    setErr("");
  };

  const addToLocalPool = (player) => {
    if ((currentAuction?.playerPool || []).find(p => p.id === player.id)) throw new Error("Already in pool");
    updateAuction({ ...currentAuction, playerPool: [...(currentAuction?.playerPool || []), player] });
  };

  const removeFromPool = (id) => {
    updateAuction({ ...currentAuction, playerPool: currentAuction.playerPool.filter(p => p.id !== id), picks: currentAuction.picks.filter(p => p.id !== id) });
  };

  const pickPlayer = (playerId) => {
    const player = currentAuction.playerPool.find(p => p.id === playerId);
    if (!player) return;
    const turn = currentAuction.picks.length % 2 === 0 ? "A" : "B";
    updateAuction({ ...currentAuction, picks: [...currentAuction.picks, { team: turn, id: player.id, name: player.name }] });
  };

  const undoPick = () => {
    if (!currentAuction.picks.length) return;
    updateAuction({ ...currentAuction, picks: currentAuction.picks.slice(0, -1) });
  };

  const completeAuction = () => updateAuction({ ...currentAuction, status: "completed" });

  const moveCompletedPick = (team, idx, dir) => {
    const teamPicks = (currentAuction?.picks || []).filter(p => p.team === team);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= teamPicks.length) return;
    const copy = [...teamPicks];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    const otherPicks = (currentAuction?.picks || []).filter(p => p.team !== team);
    updateAuction({ ...currentAuction, picks: [...otherPicks, ...copy] });
  };

  // Edit helpers (admin, completed auction)
  const startEdit = () => {
    setEditPicks([...(currentAuction?.picks || [])]);
    setEditAddTeam("A");
    setEditMode(true);
  };
  const cancelEdit = () => setEditMode(false);
  const saveEdit = () => {
    updateAuction({ ...currentAuction, picks: editPicks });
    setEditMode(false);
  };
  const moveEditPick = (team, idx, dir) => {
    const teamPicks = editPicks.filter(p => p.team === team);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= teamPicks.length) return;
    const copy = [...teamPicks];
    [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
    setEditPicks([...editPicks.filter(p => p.team !== team), ...copy]);
  };
  const removeEditPick = (id) => setEditPicks(editPicks.filter(p => p.id !== id));
  const addToEditPicks = (player) => {
    if (editPicks.find(p => p.id === player.id)) return;
    setEditPicks([...editPicks, { team: editAddTeam, id: player.id, name: player.name }]);
  };

  const openAuction = (auction) => {
    setCurrentAuction(auction);
    setStep(auction.playerPool.length === 0 ? "POOL" : "PICKS");
  };

  // ── ONLINE_CREATE ────────────────────────────────────────────────────────────
  if (step === "ONLINE_CREATE") return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button style={S.btnSecondary} onClick={() => { setStep("LIST"); setErr(""); }}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0 }}>Create Online Auction</h2>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>
          You'll get a 6-letter code to share with the other captain. They join from their login and you take turns picking.
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={S.label}>Auction Name <span style={{ color: "#888", fontSize: 11 }}>(optional)</span></label>
          <input style={S.input} value={onlineOnlineName} onChange={e => setOnlineOnlineName(e.target.value)} placeholder={"e.g. " + new Date().toLocaleDateString() + " Auction"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div>
            <label style={S.label}>Your Team Name (Team A)</label>
            <input style={S.input} value={onlineTeamA} onChange={e => setOnlineTeamA(e.target.value)} placeholder="Team Alpha" />
          </div>
          <div>
            <label style={S.label}>Other Team Name (Team B)</label>
            <input style={S.input} value={onlineTeamB} onChange={e => setOnlineTeamB(e.target.value)} placeholder="Team Beta" />
          </div>
        </div>
        {err && <div style={{ ...S.error, marginBottom: 10 }}>{err}</div>}
        <button style={{ ...S.btn, width: "100%" }} disabled={onlineLoading}
          onClick={async () => {
            if (!onlineTeamA.trim() || !onlineTeamB.trim()) { setErr("Enter both team names"); return; }
            setOnlineLoading(true); setErr("");
            try {
              const res = await apiFetch("/api/auctions/online", {
                method: "POST",
                body: JSON.stringify({ name: onlineOnlineName.trim() || (new Date().toLocaleDateString() + " Auction"), teamAName: onlineTeamA.trim(), teamBName: onlineTeamB.trim() }),
              });
              setOnlineRoom(res);
              setStep("ONLINE_ROOM");
            } catch (ex) { setErr(ex.message); }
            setOnlineLoading(false);
          }}>
          {onlineLoading ? "Creating…" : "Create & Get Code"}
        </button>
      </div>
    </div>
  );

  // ── ONLINE_JOIN ───────────────────────────────────────────────────────────────
  if (step === "ONLINE_JOIN") return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button style={S.btnSecondary} onClick={() => { setStep("LIST"); setErr(""); }}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0 }}>Join Online Auction</h2>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>Enter the 6-letter code shared by the other captain.</div>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Auction Code</label>
          <input style={{ ...S.input, textTransform: "uppercase", letterSpacing: 4, fontSize: 20, textAlign: "center" }}
            value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
            maxLength={6} placeholder="ABCDEF" />
        </div>
        {err && <div style={{ ...S.error, marginBottom: 10 }}>{err}</div>}
        <button style={{ ...S.btn, width: "100%" }} disabled={onlineLoading || joinCode.length < 6}
          onClick={async () => {
            setOnlineLoading(true); setErr("");
            try {
              const res = await apiFetch("/api/auctions/online/" + joinCode + "/join", { method: "POST", body: JSON.stringify({}) });
              setOnlineRoom(res);
              setStep("ONLINE_ROOM");
            } catch (ex) { setErr(ex.message); }
            setOnlineLoading(false);
          }}>
          {onlineLoading ? "Joining…" : "Join Auction"}
        </button>
      </div>
    </div>
  );

  // ── ONLINE_ROOM ───────────────────────────────────────────────────────────────
  if (step === "ONLINE_ROOM" && onlineRoom) {
    const s = onlineRoom.state || {};
    const status = s.status || "waiting_for_joiner";
    const picks = s.picks || [];
    const pool = s.playerPool || [];
    const pickedIds = new Set(picks.map(p => p.id));
    const remaining = pool.filter(p => !pickedIds.has(p.id));
    const pickCount = picks.length;
    const turnTeam = pickCount % 2 === 0 ? "A" : "B";
    const myTurn = (turnTeam === "A" && isHost) || (turnTeam === "B" && isJoiner);
    const teamAPicks = picks.filter(p => p.team === "A");
    const teamBPicks = picks.filter(p => p.team === "B");
    const turnName = turnTeam === "A" ? s.teamAName : s.teamBName;

    const copyCode = () => copyToClipboard(onlineRoom.code).catch(() => {});

    return (
      <div style={S.page}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <button style={S.btnSecondary} onClick={() => setStep("LIST")}>← Back</button>
          <h2 style={{ ...S.h2, margin: 0, flex: 1 }}>{s.name || "Online Auction"}</h2>
          <span style={{ fontSize: 11, color: "#888" }}>Auto-refreshing</span>
        </div>

        {/* Code banner */}
        <div style={{ backgroundColor: "#f0faf4", border: "1px solid #a5d6a7", borderRadius: 8, padding: "10px 16px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Auction Code — share with the other captain</div>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 6, color: T.primary }}>{onlineRoom.code}</div>
          </div>
          <button style={S.btnSm} onClick={copyCode}>Copy</button>
        </div>

        {/* Status */}
        {status === "waiting_for_joiner" && (
          <div style={{ ...S.card, textAlign: "center", color: "#e67e22", fontWeight: 600 }}>
            Waiting for Team B captain to join with code <strong>{onlineRoom.code}</strong>…
          </div>
        )}

        {/* Pool setup — host manages pool, both see it */}
        {(status === "pool_setup" || status === "waiting_for_joiner") && (
          <div style={S.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.primary, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Player Pool {isHost ? "(you manage this)" : "(host is managing)"}
            </div>
            {isHost && (
              <div style={{ marginBottom: 12 }}>
                <AuctionPoolSearch
                  alreadyInPool={pool}
                  onSelect={async (player) => {
                    const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/pool", {
                      method: "POST", body: JSON.stringify({ id: player.id, name: player.name }),
                    });
                    setOnlineRoom(res);
                  }}
                />
              </div>
            )}
            {err && <div style={{ ...S.error, marginBottom: 8 }}>{err}</div>}
            {pool.length === 0 ? <div style={{ color: "#aaa", textAlign: "center", padding: 12, fontSize: 13 }}>No players added yet</div>
              : pool.map(p => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                  <span style={{ fontSize: 13 }}>{p.name}</span>
                  {isHost && <button style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer" }}
                    onClick={async () => {
                      try { const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/pool/" + p.id, { method: "DELETE" }); setOnlineRoom(res); }
                      catch (ex) { setErr(ex.message); }
                    }}>✕</button>}
                </div>
              ))}
            {isHost && status === "pool_setup" && pool.length >= 2 && (
              <button style={{ ...S.btn, width: "100%", marginTop: 12 }}
                onClick={async () => {
                  setOnlineLoading(true); setErr("");
                  try { const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/start", { method: "POST", body: JSON.stringify({}) }); setOnlineRoom(res); }
                  catch (ex) { setErr(ex.message); }
                  setOnlineLoading(false);
                }}>{onlineLoading ? "Starting…" : "Start Auction →"}</button>
            )}
            {!isHost && status === "pool_setup" && <div style={{ color: "#888", textAlign: "center", fontSize: 13, marginTop: 8 }}>Waiting for host to start the auction…</div>}
          </div>
        )}

        {/* Picking phase */}
        {status === "in_progress" && (
          <>
            <div style={{ backgroundColor: myTurn ? (turnTeam === "A" ? T.primary : "#c0392b") : "#888", borderRadius: 8, padding: "12px 16px", marginBottom: 14, color: "white", textAlign: "center" }}>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{myTurn ? "Your Pick!" : "Waiting…"}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{turnName} is picking</div>
              <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>Pick {pickCount + 1} of {pool.length}</div>
            </div>

            {remaining.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Available ({remaining.length}) {!myTurn && <span style={{ color: "#aaa", fontWeight: 400 }}>— not your turn</span>}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {remaining.map(p => (
                    <button key={p.id}
                      disabled={!myTurn}
                      onClick={async () => {
                        setErr("");
                        try { const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/pick", { method: "POST", body: JSON.stringify({ playerId: p.id }) }); setOnlineRoom(res); }
                        catch (ex) { setErr(ex.message); }
                      }}
                      style={{ padding: "8px 16px", borderRadius: 20, border: "2px solid " + (myTurn ? (turnTeam === "A" ? T.primary : "#c0392b") : "#ddd"), backgroundColor: myTurn ? "white" : "#f5f5f5", color: myTurn ? "#333" : "#aaa", cursor: myTurn ? "pointer" : "not-allowed", fontWeight: 600, fontSize: 13 }}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {err && <div style={{ ...S.error, marginBottom: 8 }}>{err}</div>}

            {isHost && (
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {picks.length > 0 && (
                  <button style={S.btnSecondary} onClick={async () => {
                    try { const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/undo", { method: "POST", body: JSON.stringify({}) }); setOnlineRoom(res); }
                    catch (ex) { setErr(ex.message); }
                  }}>↩ Undo</button>
                )}
                <button style={{ ...S.btn, backgroundColor: "#2e7d32", flex: 1 }} onClick={async () => {
                  try {
                    const res = await apiFetch("/api/auctions/online/" + onlineRoom.code + "/complete", { method: "POST", body: JSON.stringify({}) });
                    setOnlineRoom(res);
                    saveCompletedOnlineAuction(res);
                    setStep("LIST");
                  } catch (ex) { setErr(ex.message); }
                }}>✓ {remaining.length === 0 ? "Complete Auction" : "Mark Done"}</button>
              </div>
            )}
            {!isHost && remaining.length === 0 && <div style={{ color: "#888", textAlign: "center", fontSize: 13 }}>All players picked — waiting for host to complete the auction.</div>}
          </>
        )}

        {/* Completed */}
        {status === "completed" && (
          <div style={{ ...S.card, backgroundColor: "#f0faf4", textAlign: "center", marginBottom: 14 }}>
            <div style={{ fontWeight: 700, color: T.primary, marginBottom: 6 }}>Auction Complete!</div>
            <div style={{ fontSize: 13, color: "#555" }}>Saved to your Auctions list. Select it in New Match settings to use these teams.</div>
          </div>
        )}

        {/* Teams */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[["A", teamAPicks, s.teamAName], ["B", teamBPicks, s.teamBName]].map(([side, sidePicks, name]) => (
            <div key={side} style={{ ...S.card, borderTop: "3px solid " + (side === "A" ? T.primary : "#c0392b"), padding: 12 }}>
              <div style={{ fontWeight: 700, color: side === "A" ? T.primary : "#c0392b", marginBottom: 8, fontSize: 14 }}>{name}</div>
              {sidePicks.length === 0 ? <div style={{ color: "#aaa", fontSize: 12 }}>No picks yet</div>
                : sidePicks.map((p, i) => (
                  <div key={p.id} style={{ fontSize: 13, padding: "4px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ color: "#aaa", marginRight: 6, fontSize: 11 }}>{i + 1}.</span>{p.name}
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── LIST ────────────────────────────────────────────────────────────────────
  if (step === "LIST") return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={S.h2}>Auctions</h2>
        <button style={S.btn} onClick={() => { setAuctionName(""); setTeamAName(""); setTeamBName(""); setErr(""); setStep("SETUP"); }}>+ Local</button>
      </div>
      {/* Online auction options */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <button style={{ ...S.btnOutline, padding: "10px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
          onClick={() => { setOnlineOnlineName(""); setOnlineTeamA(""); setOnlineTeamB(""); setErr(""); setStep("ONLINE_CREATE"); }}>
          <span style={{ fontSize: 18 }}>📡</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Create Online</span>
          <span style={{ fontSize: 11, color: "#888" }}>Host a live auction</span>
        </button>
        <button style={{ ...S.btnOutline, padding: "10px 0", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}
          onClick={() => { setJoinCode(""); setErr(""); setStep("ONLINE_JOIN"); }}>
          <span style={{ fontSize: 18 }}>🔗</span>
          <span style={{ fontSize: 13, fontWeight: 700 }}>Join Online</span>
          <span style={{ fontSize: 11, color: "#888" }}>Enter code to join</span>
        </button>
      </div>
      {auctions.length === 0 && (
        <div style={S.card}><p style={{ color: "#888", textAlign: "center" }}>No auctions yet. Create a local one or start an online auction above!</p></div>
      )}
      {auctions.map(a => (
        <div key={a.id} style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{a.name}</div>
              <div style={{ fontSize: 13, color: "#555" }}>{a.teamAName} vs {a.teamBName}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                {a.picks.filter(p => p.team === "A").length} + {a.picks.filter(p => p.team === "B").length} players picked &nbsp;·&nbsp;
                <span style={{ color: a.status === "completed" ? "#2e7d32" : "#e67e22", fontWeight: 600 }}>
                  {a.status === "completed" ? "✓ Complete" : "In Progress"}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button style={S.btnSm} onClick={() => openAuction(a)}>Open</button>
              <button style={{ ...S.btnSm, backgroundColor: "#fdecea", color: "#c0392b", border: "1px solid #e57373" }}
                onClick={() => { deleteAuctionFromStorage(a.id); refreshAuctions(); }}>✕</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // ── SETUP ───────────────────────────────────────────────────────────────────
  if (step === "SETUP") return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button style={S.btnSecondary} onClick={() => { setStep("LIST"); setErr(""); }}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0 }}>New Auction</h2>
      </div>
      <div style={S.card}>
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Auction Name <span style={{ color: "#888", fontSize: 11 }}>(optional)</span></label>
          <input style={S.input} value={auctionName} onChange={e => setAuctionName(e.target.value)} placeholder={"e.g. " + new Date().toLocaleDateString() + " Auction"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
          <div>
            <label style={S.label}>Team A Name</label>
            <input style={S.input} value={teamAName} onChange={e => setTeamAName(e.target.value)} placeholder="Team Alpha" />
          </div>
          <div>
            <label style={S.label}>Team B Name</label>
            <input style={S.input} value={teamBName} onChange={e => setTeamBName(e.target.value)} placeholder="Team Beta" />
          </div>
        </div>
        {err && <div style={{ ...S.error, marginBottom: 10 }}>{err}</div>}
        <button style={{ ...S.btn, width: "100%" }} onClick={createAuction}>Next: Add Players →</button>
      </div>
    </div>
  );

  // ── POOL ────────────────────────────────────────────────────────────────────
  if (step === "POOL") return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button style={S.btnSecondary} onClick={() => { setStep("LIST"); setErr(""); }}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0, flex: 1 }}>Player Pool — {currentAuction?.name}</h2>
      </div>
      <div style={S.card}>
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>Add all players who will be auctioned between <strong>{currentAuction?.teamAName}</strong> and <strong>{currentAuction?.teamBName}</strong>.</div>
        <AuctionPoolSearch
          alreadyInPool={currentAuction?.playerPool || []}
          onSelect={addToLocalPool}
          onSelectAll={(players) => {
            const inPool = new Set((currentAuction?.playerPool || []).map(p => p.id));
            const toAdd = players.filter(p => !inPool.has(p.id));
            if (toAdd.length > 0) updateAuction({ ...currentAuction, playerPool: [...(currentAuction?.playerPool || []), ...toAdd] });
          }}
        />
        {err && <div style={{ ...S.error, marginBottom: 10, marginTop: 8 }}>{err}</div>}
        {(currentAuction?.playerPool || []).length === 0 && (
          <div style={{ color: "#aaa", textAlign: "center", padding: 20, fontSize: 13 }}>No players added yet</div>
        )}
        {(currentAuction?.playerPool || []).map(p => (
          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
            <span style={{ fontSize: 14 }}>{p.name}</span>
            <button style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 16 }} onClick={() => removeFromPool(p.id)}>✕</button>
          </div>
        ))}
        {(currentAuction?.playerPool || []).length >= 2 && (
          <button style={{ ...S.btn, width: "100%", marginTop: 16 }} onClick={() => setStep("PICKS")}>Start Auction →</button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          <div style={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
          <span style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap" }}>or</span>
          <div style={{ flex: 1, height: 1, backgroundColor: "#e0e0e0" }} />
        </div>
        <button style={{ ...S.btnSecondary, width: "100%", marginTop: 12 }}
          onClick={() => {
            updateAuction({ ...currentAuction, noPool: true });
            setStep("PICKS");
          }}>
          Proceed without Pool
        </button>
        <div style={{ fontSize: 12, color: "#888", textAlign: "center", marginTop: 6 }}>
          Captains pick any player turn-by-turn using a live search
        </div>
      </div>
    </div>
  );

  // ── PICKS ───────────────────────────────────────────────────────────────────
  const pickedIds = new Set((currentAuction?.picks || []).map(p => p.id));
  const remaining = (currentAuction?.playerPool || []).filter(p => !pickedIds.has(p.id));
  const pickTurn = (currentAuction?.picks || []).length % 2 === 0 ? "A" : "B";
  const turnName = pickTurn === "A" ? currentAuction?.teamAName : currentAuction?.teamBName;
  const isDone = currentAuction?.status === "completed";
  const teamAPicks = (currentAuction?.picks || []).filter(p => p.team === "A");
  const teamBPicks = (currentAuction?.picks || []).filter(p => p.team === "B");

  // Edit mode view (admin, completed auction)
  if (isDone && editMode) {
    const editTeamAPicks = editPicks.filter(p => p.team === "A");
    const editTeamBPicks = editPicks.filter(p => p.team === "B");
    const editTeamColor = (t) => t === "A" ? T.primary : "#c0392b";
    const editTeamName = (t) => t === "A" ? currentAuction?.teamAName : currentAuction?.teamBName;
    const renderEditTeam = (team, picks) => (
      <div style={{ ...S.card, borderTop: "3px solid " + editTeamColor(team), padding: 12 }}>
        <div style={{ fontWeight: 700, color: editTeamColor(team), marginBottom: 10, fontSize: 14 }}>{editTeamName(team)}</div>
        {picks.length === 0
          ? <div style={{ color: "#aaa", fontSize: 12, marginBottom: 8 }}>No players</div>
          : picks.map((p, i) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 0", borderBottom: "1px solid #f0f0f0" }}>
              <span style={{ color: "#aaa", fontSize: 11, width: 18, flexShrink: 0 }}>{i + 1}.</span>
              <span style={{ flex: 1, fontSize: 13 }}>{p.name}</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <button disabled={i === 0} onClick={() => moveEditPick(team, i, -1)}
                  style={{ padding: "1px 6px", fontSize: 11, border: "1px solid #ddd", borderRadius: 3, background: i === 0 ? "#f5f5f5" : "white", cursor: i === 0 ? "default" : "pointer", color: i === 0 ? "#ccc" : "#333" }}>▲</button>
                <button disabled={i === picks.length - 1} onClick={() => moveEditPick(team, i, 1)}
                  style={{ padding: "1px 6px", fontSize: 11, border: "1px solid #ddd", borderRadius: 3, background: i === picks.length - 1 ? "#f5f5f5" : "white", cursor: i === picks.length - 1 ? "default" : "pointer", color: i === picks.length - 1 ? "#ccc" : "#333" }}>▼</button>
              </div>
              <button onClick={() => removeEditPick(p.id)}
                style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: "0 2px" }}>✕</button>
            </div>
          ))}
      </div>
    );
    return (
      <div style={S.page}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <button style={S.btnSecondary} onClick={cancelEdit}>✕ Cancel</button>
          <h2 style={{ ...S.h2, margin: 0, flex: 1 }}>Edit — {currentAuction?.name}</h2>
          <button style={{ ...S.btn, backgroundColor: "#2e7d32" }} onClick={saveEdit}>Save</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          {renderEditTeam("A", editTeamAPicks)}
          {renderEditTeam("B", editTeamBPicks)}
        </div>

        <div style={S.card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Add Player</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {["A", "B"].map(t => (
              <button key={t} onClick={() => setEditAddTeam(t)}
                style={{ flex: 1, padding: "8px 0", borderRadius: 6, border: "2px solid " + editTeamColor(t), backgroundColor: editAddTeam === t ? editTeamColor(t) : "white", color: editAddTeam === t ? "white" : editTeamColor(t), fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                {editTeamName(t)}
              </button>
            ))}
          </div>
          <AuctionPoolSearch
            alreadyInPool={editPicks}
            hideAddAll
            onSelect={addToEditPicks}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <button style={S.btnSecondary} onClick={() => { setStep(isDone ? "LIST" : "POOL"); setErr(""); setEditMode(false); }}>← Back</button>
        <h2 style={{ ...S.h2, margin: 0, flex: 1 }}>{currentAuction?.name}</h2>
        {isDone && <span style={{ fontSize: 12, backgroundColor: "#e8f5e9", color: "#2e7d32", padding: "4px 10px", borderRadius: 20, fontWeight: 700 }}>✓ Completed</span>}
        {isDone && user?.role === "admin" && (
          <button style={{ ...S.btnSm, backgroundColor: "#fff3e0", color: "#e65100", border: "1px solid #ffb74d" }} onClick={startEdit}>✏️ Edit</button>
        )}
      </div>

      {!isDone && (
        <div style={{ backgroundColor: pickTurn === "A" ? T.primary : "#c0392b", borderRadius: 8, padding: "12px 16px", marginBottom: 14, color: "white", textAlign: "center" }}>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Now Picking</div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>{turnName}</div>
          {!currentAuction?.noPool && (
            <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>Pick {(currentAuction?.picks || []).length + 1} of {(currentAuction?.playerPool || []).length}</div>
          )}
        </div>
      )}

      {!isDone && currentAuction?.noPool && (
        <div style={S.card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Search &amp; Pick for {turnName}
          </div>
          <AuctionPoolSearch
            alreadyInPool={currentAuction?.picks || []}
            hideAddAll
            onSelect={(player) => {
              if ((currentAuction?.picks || []).find(p => p.id === player.id)) return;
              const pick = { team: pickTurn, id: player.id, name: player.name };
              updateAuction({ ...currentAuction, picks: [...(currentAuction?.picks || []), pick] });
            }}
          />
        </div>
      )}

      {!isDone && !currentAuction?.noPool && (
        <div style={S.card}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Available Players ({remaining.length})
          </div>
          {remaining.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {remaining.map(p => (
                <button key={p.id} onClick={() => pickPlayer(p.id)}
                  style={{ padding: "8px 16px", borderRadius: 20, border: "2px solid " + (pickTurn === "A" ? T.primary : "#c0392b"), backgroundColor: "white", color: "#333", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                  {p.name}
                </button>
              ))}
            </div>
          )}
          <div style={{ borderTop: remaining.length > 0 ? "1px solid #f0f0f0" : "none", paddingTop: remaining.length > 0 ? 12 : 0 }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>Search or add player to pool</div>
            <AuctionPoolSearch
              alreadyInPool={currentAuction?.playerPool || []}
              onSelect={addToLocalPool}
              onSelectAll={(players) => {
                const inPool = new Set((currentAuction?.playerPool || []).map(p => p.id));
                const toAdd = players.filter(p => !inPool.has(p.id));
                if (toAdd.length > 0) updateAuction({ ...currentAuction, playerPool: [...(currentAuction?.playerPool || []), ...toAdd] });
              }}
            />
          </div>
        </div>
      )}

      {!isDone && (currentAuction?.picks || []).length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <button style={S.btnSecondary} onClick={undoPick}>↩ Undo Last Pick</button>
          {(remaining.length === 0 || (currentAuction?.picks || []).length > 0) && (
            <button style={{ ...S.btn, backgroundColor: "#2e7d32" }} onClick={() => { completeAuction(); setStep("LIST"); }}>
              ✓ {remaining.length === 0 ? "Complete Auction" : "Mark Done"}
            </button>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        {[["A", teamAPicks, currentAuction?.teamAName], ["B", teamBPicks, currentAuction?.teamBName]].map(([side, picks, name]) => (
          <div key={side} style={{ ...S.card, borderTop: "3px solid " + (side === "A" ? T.primary : "#c0392b"), padding: 12 }}>
            <div style={{ fontWeight: 700, color: side === "A" ? T.primary : "#c0392b", marginBottom: 8, fontSize: 14 }}>{name}</div>
            {picks.length === 0 ? (
              <div style={{ color: "#aaa", fontSize: 12 }}>No picks yet</div>
            ) : picks.map((p, i) => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 0", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ color: "#aaa", fontSize: 11, width: 18, flexShrink: 0 }}>{i + 1}.</span>
                <span style={{ flex: 1, fontSize: 13 }}>{p.name}</span>
                {isDone && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                    <button disabled={i === 0} onClick={() => moveCompletedPick(side, i, -1)}
                      style={{ padding: "1px 5px", fontSize: 10, border: "1px solid #ddd", borderRadius: 3, background: i === 0 ? "#f5f5f5" : "white", cursor: i === 0 ? "default" : "pointer", color: i === 0 ? "#ccc" : "#555", lineHeight: 1 }}>▲</button>
                    <button disabled={i === picks.length - 1} onClick={() => moveCompletedPick(side, i, 1)}
                      style={{ padding: "1px 5px", fontSize: 10, border: "1px solid #ddd", borderRadius: 3, background: i === picks.length - 1 ? "#f5f5f5" : "white", cursor: i === picks.length - 1 ? "default" : "pointer", color: i === picks.length - 1 ? "#ccc" : "#555", lineHeight: 1 }}>▼</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {isDone && (
        <div style={{ ...S.card, backgroundColor: "#f0faf4", textAlign: "center" }}>
          <div style={{ fontWeight: 700, color: T.primary, marginBottom: 6 }}>Auction Complete!</div>
          <div style={{ fontSize: 13, color: "#555" }}>Select this auction in New Match settings to use these teams.</div>
        </div>
      )}
    </div>
  );
}

function NavBar({ user, view, onNav, onLogout, installPrompt, showIosInstall, onInstall, installFlash }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [showIosTooltip, setShowIosTooltip] = useState(false);
  if (!user) return null;

  const linkBtn = (label, targetView) => (
    <button
      key={label}
      style={view === targetView ? S.navBtnActive : S.navBtn}
      onClick={() => onNav(targetView)}
    >
      {label}
    </button>
  );

  const desktopLinks = [
    linkBtn("Home", "HOME"),
    linkBtn("Leaderboard", "LEADERBOARD"),
    linkBtn("Players", "PLAYERS"),
    linkBtn("Teams", "TEAMS"),
    linkBtn("Auction", "AUCTION"),
    linkBtn("Tournament", "SERIES"),
    linkBtn("Profile", "PROFILE"),
    ...(user.role === "admin" ? [linkBtn("Admin", "ADMIN")] : []),
  ];

  // Bottom nav primary tabs
  const bottomTabs = [
    { label: "Home",        targetView: "HOME",        icon: "⌂" },
    { label: "Leaderboard", targetView: "LEADERBOARD", icon: "★" },
    { label: "Players",     targetView: "PLAYERS",     icon: "🏏" },
    { label: "Profile",     targetView: "PROFILE",     icon: "◉" },
    { label: "More",    targetView: null,       icon: "⋯" },
  ];

  // Items in the "More" drawer
  const moreDrawerItems = [
    { label: "Tournament", targetView: "SERIES" },
    { label: "Teams",      targetView: "TEAMS" },
    { label: "Auction",    targetView: "AUCTION" },
    ...(user.role === "admin" ? [{ label: "Admin", targetView: "ADMIN" }] : []),
    { label: "Logout", targetView: null, action: onLogout },
  ];

  const moreActiveViews = ["SERIES", "TEAMS", "ADMIN", "AUCTION"];
  const moreIsActive = moreOpen || moreActiveViews.includes(view);

  return (
    <>
      {/* ── Top navbar (desktop full / mobile slim brand only) ── */}
      <nav style={S.navbar}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
          <div style={{ ...S.navBrand, display: "flex", alignItems: "center", gap: 8 }}>
            <ThemedLogo size={84} variant="dark" style={{ display: "block" }} />
          </div>
          {/* Desktop links */}
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} className="nav-desktop">
            {desktopLinks}
            {installPrompt && (
              <button
                title="Add to Home Screen"
                style={{ ...S.navBtn, display: "flex", alignItems: "center", gap: 4, padding: "5px 10px" }}
                onClick={onInstall}
              >
                ⊕ Install App
              </button>
            )}
            {showIosInstall && (
              <div style={{ position: "relative" }}>
                <button
                  title="Add to Home Screen"
                  style={{ ...S.navBtn, display: "flex", alignItems: "center", gap: 4, padding: "5px 10px" }}
                  onClick={() => setShowIosTooltip(v => !v)}
                >
                  ⊕ Add to Home Screen
                </button>
                {showIosTooltip && (
                  <div style={{
                    position: "absolute", top: 38, right: 0, zIndex: 999,
                    backgroundColor: "white", color: "#333", borderRadius: 8, padding: "12px 14px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.18)", fontSize: 13, width: 220,
                    border: "1px solid #eee", lineHeight: 1.5,
                  }}>
                    Tap the <strong>Share</strong> button (□↑) in Safari, then choose <strong>"Add to Home Screen"</strong>.
                    <button onClick={() => setShowIosTooltip(false)}
                      style={{ display: "block", marginTop: 8, fontSize: 11, color: "#888", background: "none", border: "none", cursor: "pointer" }}>
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            )}
            <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>
              {user.name}{" "}
              <span style={{ backgroundColor: "rgba(255,255,255,0.2)", padding: "1px 6px", borderRadius: 10, fontSize: 10 }}>
                {user.role}
              </span>
            </span>
            <button style={S.navBtn} onClick={onLogout}>Logout</button>
          </div>
          {/* Mobile: install icon + user name */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }} className="nav-mobile-user-area">
            {installPrompt && (
              <button
                title="Add to Home Screen"
                onClick={onInstall}
                style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6,
                  color: "white", fontSize: 20, lineHeight: 1, padding: "3px 8px", cursor: "pointer" }}
              >
                ⊕
              </button>
            )}
            {showIosInstall && (
              <div style={{ position: "relative" }}>
                <button
                  title="Add to Home Screen"
                  onClick={() => setShowIosTooltip(v => !v)}
                  style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 6,
                    color: "white", fontSize: 20, lineHeight: 1, padding: "3px 8px", cursor: "pointer" }}
                >
                  ⊕
                </button>
                {showIosTooltip && (
                  <div style={{
                    position: "absolute", top: 38, right: 0, zIndex: 999,
                    backgroundColor: "white", color: "#333", borderRadius: 8, padding: "12px 14px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.18)", fontSize: 13, width: 220,
                    border: "1px solid #eee", lineHeight: 1.5,
                  }}>
                    Tap <strong>Share</strong> (□↑) → <strong>"Add to Home Screen"</strong>.
                    <button onClick={() => setShowIosTooltip(false)}
                      style={{ display: "block", marginTop: 8, fontSize: 11, color: "#888", background: "none", border: "none", cursor: "pointer" }}>
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            )}
            <span className="nav-mobile-user" style={{ color: "rgba(255,255,255,0.9)", fontSize: 13, display: "none" }}>
              {user.name}
            </span>
          </div>
        </div>
      </nav>

      {/* ── Mobile bottom tab bar ── */}
      <nav className="bottom-nav">
        {bottomTabs.map(tab => {
          const isActive = tab.targetView ? view === tab.targetView : moreIsActive;
          return (
            <button
              key={tab.label}
              className="bottom-nav-btn"
              style={{ color: isActive ? T.primary : "#888" }}
              onClick={() => {
                if (tab.targetView) { onNav(tab.targetView); setMoreOpen(false); }
                else setMoreOpen(o => !o);
              }}
            >
              <span className="bottom-nav-icon">{tab.icon}</span>
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── More drawer overlay ── */}
      {moreOpen && (
        <>
          <div className="bottom-nav-overlay" onClick={() => setMoreOpen(false)} />
          <div className="bottom-nav-drawer">
            <div style={{ width: 36, height: 4, backgroundColor: "#ddd", borderRadius: 2, margin: "0 auto 16px" }} />
            {moreDrawerItems.map(item => {
              const isActive = item.targetView && view === item.targetView;
              return (
                <button
                  key={item.label}
                  className="bottom-nav-drawer-btn"
                  style={{
                    color: isActive ? T.primary : "#1a1a1a",
                    backgroundColor: isActive ? T.primary + "18" : "transparent",
                    fontWeight: isActive ? 700 : 400,
                  }}
                  onClick={() => {
                    setMoreOpen(false);
                    if (item.action) item.action();
                    else onNav(item.targetView);
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </>
      )}
      {/* Install success toast */}
      {installFlash && (
        <div style={{
          position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)",
          backgroundColor: T.primary, color: "white",
          padding: "10px 20px", borderRadius: 24,
          fontSize: 14, fontWeight: 600,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
          zIndex: 9999, pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          {installFlash}
        </div>
      )}
    </>
  );
}

// ─── App Root ─────────────────────────────────────────────────────────────────

export default function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("LOGIN");
  const [matchId, setMatchId] = useState(null);
  const [publicSeriesId, setPublicSeriesId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [teamInviteCode, setTeamInviteCode] = useState("");
  const [challengeTeams, setChallengeTeams] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosInstall, setShowIosInstall] = useState(false);
  const [installFlash, setInstallFlash] = useState("");
  const [staleData, setStaleData] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [appUpdated, setAppUpdated] = useState(false);
  const appIsOnline = useOnlineStatus();

  // ── Listen for SW "new version activated" message ──
  useEffect(() => {
    const handler = () => setAppUpdated(true);
    window.addEventListener('app-updated', handler);
    return () => window.removeEventListener('app-updated', handler);
  }, []);

  // ── 48h stale cache check ──
  useEffect(() => {
    if (!appIsOnline) return;
    const STALE_MS = 48 * 60 * 60 * 1000;
    getCached("players").then(c => {
      if (c && Date.now() - c.ts > STALE_MS) setStaleData(true);
    }).catch(() => {});
  }, [appIsOnline]);

  const refreshAllData = async () => {
    setRefreshing(true);
    try {
      const [p, t] = await Promise.all([
        apiFetch("/api/players"),
        apiFetch("/api/teams"),
      ]);
      await Promise.all([
        cacheData("players", p),
        cacheData("teams", t),
      ]);
      setStaleData(false);
    } catch (_) {}
    setRefreshing(false);
  };

  // ── PWA: update manifest + capture install prompt ──
  useEffect(() => {
    // Point manifest to the current theme so installed icon matches theme
    const manifestLink = document.getElementById("pwa-manifest");
    const touchIcon = document.getElementById("apple-touch-icon");
    const themeMetaColor = document.getElementById("meta-theme-color");
    if (manifestLink) manifestLink.href = `/manifest-${_themeName}.json`;
    if (touchIcon) touchIcon.href = `/logo-${_themeName}-dark.png`;
    if (themeMetaColor) themeMetaColor.content = T.primary;

    // Detect already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true) {
      setIsInstalled(true);
    }

    // Capture Android/desktop install prompt
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);

    // Detect iOS (Safari doesn't fire beforeinstallprompt)
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandalone = window.navigator.standalone === true;
    if (isIos && !isInStandalone) setShowIosInstall(true);

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Handle hash routing
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#live/")) {
      setView("LIVE");
      setMatchId(hash.slice(6));
    } else if (hash.startsWith("#join/")) {
      setView("JOIN");
      setMatchId(hash.slice(6));
    } else if (hash.startsWith("#summary/")) {
      setView("SUMMARY");
      setMatchId(hash.slice(9));
    } else if (hash.startsWith("#team-join/")) {
      const code = hash.replace("#team-join/", "").trim();
      setTeamInviteCode(code);
      setView("TEAM_JOIN");
    } else if (hash.startsWith("#points/")) {
      setPublicSeriesId(hash.slice(8));
      setView("PUBLIC_POINTS");
    }
  }, []);

  // Auto-login from stored token
  useEffect(() => {
    if (_token) {
      apiFetch("/api/auth/me")
        .then((me) => {
          setUser(me);
          if (_pendingReturnView) {
            setView(_pendingReturnView);
          } else {
            const h = window.location.hash;
            const hasHashRoute = h.startsWith("#live/") || h.startsWith("#join/") ||
              h.startsWith("#summary/") || h.startsWith("#team-join/") || h.startsWith("#points/");
            if (!hasHashRoute) setView("HOME");
          }
        })
        .catch(async (ex) => {
          // If offline, restore user from auth cache instead of clearing session
          if (!navigator.onLine || ex.message === "offline") {
            try {
              const cached = await getAuth();
              if (cached?.user) {
                setUser(cached.user);
                const h = window.location.hash;
                const hasHashRoute = h.startsWith("#live/") || h.startsWith("#join/") ||
                  h.startsWith("#summary/") || h.startsWith("#team-join/") || h.startsWith("#points/");
                if (!hasHashRoute) setView("HOME");
                return;
              }
            } catch (_) {}
          }
          setToken("");
        })
        .finally(() => setAuthChecked(true));
    } else {
      setAuthChecked(true);
    }
  }, []); // eslint-disable-next-line

  const handleLogin = (me) => {
    setUser(me);
    setView("HOME");
  };

  const handleLogout = () => {
    setToken("");
    setUser(null);
    setView("LOGIN");
    setMatchId(null);
    window.location.hash = "";
    clearAuth().catch(() => {});
  };

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { setInstallPrompt(null); setIsInstalled(true); }
  };

  const handleNav = (targetView) => {
    setView(targetView);
    setMatchId(null);
    window.location.hash = "";
  };

  const handleGoMatch = (id, targetView) => {
    setMatchId(id);
    setView(targetView);
  };

  // Create a blank match then navigate to SETUP (used for challenge and captain new match)
  const handleStartNewMatch = async (myTeamId, theirTeamId) => {
    try {
      const m = await apiFetch("/api/matches", { method: "POST" });
      if (myTeamId) setChallengeTeams({ myTeamId, theirTeamId: theirTeamId || null });
      setMatchId(m.id);
      setView("SETUP");
    } catch (_) {}
  };

  if (!authChecked) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: T.light, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: T.primary, fontSize: 18 }}>Loading...</div>
      </div>
    );
  }

  // Public views (no auth needed for live/join with hash routing)
  if (view === "LIVE") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: T.light }}>
        {user && <NavBar user={user} view={view} onNav={handleNav} onLogout={handleLogout}
          installPrompt={installPrompt} showIosInstall={showIosInstall} onInstall={handleInstall} installFlash={installFlash} />}
        <LiveView matchId={matchId} onBack={user ? () => handleNav("HOME") : null} />
      </div>
    );
  }

  if (view === "PUBLIC_POINTS") {
    return <PublicPointsView seriesId={publicSeriesId} onLogin={() => setView("LOGIN")} />;
  }

  if (!user) {
    if (view === "REGISTER") return <RegisterView onGoLogin={() => setView("LOGIN")} />;
    if (view === "FORGOT_PASSWORD") return <ForgotPasswordView onBack={() => setView("LOGIN")} />;
    return <LoginView onLogin={handleLogin} onGoRegister={() => setView("REGISTER")} onForgotPassword={() => setView("FORGOT_PASSWORD")} />;
  }

  const renderView = () => {
    switch (view) {
      case "HOME":
        return <HomeView user={user} onGoMatch={handleGoMatch} />;
      case "ADMIN":
        return <AdminView />;
      case "SETUP":
        return (
          <SetupView
            user={user}
            matchId={matchId}
            challengeTeams={challengeTeams}
            onStarted={(id) => { setChallengeTeams(null); setMatchId(id); setView("SCORE"); }}
            onBack={() => { setChallengeTeams(null); handleNav("HOME"); }}
          />
        );
      case "JOIN":
        return <JoinView user={user} matchId={matchId} onBack={() => handleNav("HOME")} />;
      case "SCORE":
        return (
          <ScoreView
            user={user}
            matchId={matchId}
            onCompleted={(id) => { setMatchId(id); setView("SUMMARY"); }}
            onBack={() => handleNav("HOME")}
          />
        );
      case "SUMMARY":
        return <SummaryView user={user} matchId={matchId} onBack={() => handleNav("HOME")} />;
      case "PROFILE":
        return <ProfileView user={user} onUserUpdate={(me) => setUser(me)} currentView={view} />;
      case "TEAMS":
        return <TeamsView user={user} onChallenge={(myTeamId, theirTeamId) => handleStartNewMatch(myTeamId, theirTeamId)} onGoMatch={handleGoMatch} />;
      case "TEAM_JOIN":
        return <TeamJoinView user={user} inviteCode={teamInviteCode} onBack={() => handleNav("TEAMS")} />;
      case "SERIES":
        return <SeriesView user={user} onGoMatch={handleGoMatch} />;
      case "PLAYERS":
        return <PlayersView user={user} onGoMatch={handleGoMatch} />;
      case "LEADERBOARD":
        return <LeaderboardView onGoMatch={handleGoMatch} />;
      case "AUCTION":
        return <AuctionView user={user} onBack={() => handleNav("HOME")} />;
      default:
        return <HomeView user={user} onGoMatch={handleGoMatch} />;
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: T.light }}>
      <NavBar user={user} view={view} onNav={handleNav} onLogout={handleLogout}
        installPrompt={installPrompt} showIosInstall={showIosInstall} onInstall={handleInstall} installFlash={installFlash} />
      {appUpdated && (
        <div style={{ background: T.primary, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>New version available — tap to get latest features</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{ fontSize: 13, padding: "6px 16px", borderRadius: 6, border: "none", background: "#fff", color: T.primary, fontWeight: 700, cursor: "pointer" }}>
              Update Now
            </button>
            <button
              onClick={() => setAppUpdated(false)}
              style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.5)", background: "transparent", color: "#fff", cursor: "pointer" }}>
              Later
            </button>
          </div>
        </div>
      )}
      {staleData && appIsOnline && (
        <div style={{ background: "#fff3cd", borderBottom: "1px solid #ffc107", padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#856404" }}>App data is over 48 hours old — refresh for latest players & teams</span>
          <button
            onClick={refreshAllData}
            disabled={refreshing}
            style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, border: "1px solid #ffc107", background: "#ffc107", color: "#212529", fontWeight: 600, cursor: "pointer" }}
          >
            {refreshing ? "Refreshing..." : "Refresh Now"}
          </button>
        </div>
      )}
      {!appIsOnline && (
        <div style={{ background: "#fdecea", borderBottom: "1px solid #e57373", padding: "6px 16px", textAlign: "center", fontSize: 12, color: "#b71c1c", fontWeight: 600 }}>
          No internet connection — app running from cache
        </div>
      )}
      {renderView()}
    </div>
  );
}
